var __defProp = Object.defineProperty;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
import { DateTime, Settings, Duration as Duration$1 } from "luxon";
import Axios, { isAxiosError } from "axios";
function isNotNew(resource) {
  return !!resource.id && !!resource.meta;
}
function isPatientNote(resource) {
  return (resource == null ? void 0 : resource.resourceType) === "Note" && (resource == null ? void 0 : resource.composition) === "Individual";
}
function isGroupNote(resource) {
  return (resource == null ? void 0 : resource.resourceType) === "Note" && (resource == null ? void 0 : resource.composition) === "Group";
}
function isMultiServiceNote(resource) {
  return (resource == null ? void 0 : resource.resourceType) === "Note" && (resource == null ? void 0 : resource.composition) === "MultiService";
}
function isMultiNote(resource) {
  return isGroupNote(resource) || isMultiServiceNote(resource);
}
function isParentEncounter(resource) {
  return (resource == null ? void 0 : resource.resourceType) === "Encounter" && (resource.documentationType === "GroupNote" || resource.documentationType === "MultiServiceNote");
}
const partitionedResourceTypes = ["Allergy", "Appointment", "CarePlan", "CarePlanOutcome", "CarePlanSession", "ChatMessage", "ClinicalDocument", "ComplianceAudit", "Condition", "Consent", "Coverage", "Dialogue", "DocumentReference", "Encounter", "EpisodeOfCare", "Media", "Medication", "Note", "NoteAudit", "Notification", "ObservableRoutines", "Observation", "PacketResponse", "QuestionnaireResponse", "RelatedPerson", "Scribble", "Transcription"];
const selfPartitionedResourceTypes = ["DocumentReference", "Media", "Transcription"];
const attachedContentResourceTypes = ["Communication", "DocumentReference", "Media"];
const resourceTypes = ["KeyVault", "Allergy", "Announcement", "Appointment", "CarePlan", "CarePlanOutcome", "CarePlanSession", "CdaParsingTask", "Channel", "ChannelMembership", "ChatMessage", "ClinicalDocument", "Communication", "ComplianceAudit", "ComplianceQuestionnaire", "Condition", "Consent", "Contract", "Coverage", "Dialogue", "District", "DocumentReference", "DocumentTransmission", "Encounter", "EpisodeOfCare", "Form", "GrantSet", "Group", "GroupNoteDefinition", "HealthcareService", "Itinerary", "Listing", "Location", "Media", "Medication", "Notation", "Note", "NoteAudit", "NoteDefinition", "NoteRule", "NoteSectionForm", "NoteTemplate", "Notification", "ObservableRoutines", "Observation", "Organization", "Packet", "PacketResponse", "Patient", "PersonalPreferences", "PersonalSignature", "PracticePreferences", "Practitioner", "Program", "Questionnaire", "QuestionnaireResponse", "RelatedPerson", "ReportCriteria", "ReportDelivery", "Schedule", "Scribble", "Script", "SecurityRole", "SecurityToken", "SharedScribble", "SignatureConfiguration", "Snippet", "SnippetTemplate", "SpeechRule", "SpeechRuleTemplate", "TextExpansion", "TextExpansionTemplate", "Transcription"];
const referencePaths = {
  KeyVault: [
    "owner"
  ],
  Media: [],
  District: [],
  GrantSet: [],
  SecurityRole: [
    "derivedFrom",
    "grants"
  ],
  Person: [
    "avatar",
    "photo",
    "addresses.district",
    "profile.roles"
  ],
  Location: [
    "address.district"
  ],
  Organization: [
    "addresses.district",
    "contacts.organization",
    "contacts.addresses.district"
  ],
  Listing: [
    "author"
  ],
  Program: [
    "parent",
    "defaultLocationKind",
    "allowedLocationKinds"
  ],
  NoteSectionForm: [
    "elements.initial.valueReference",
    "elements.initial.valueSignature.signatureImage",
    "elements.initial.valueSignature.signer",
    "elements.initial.valueServiceEvents.serviceType",
    "elements.initial.valueServiceEvents.sections.form",
    "elements.answerOptions.serviceTypeFilters",
    "elements.answerOptions.programFilters",
    "elements.answerOptions.insuranceFilters",
    "elements.answerOptions.locationFilters",
    "elements.answerOptions.serviceLocationFilters",
    "elements.form",
    "elements.serviceTypeFilters",
    "elements.programFilters",
    "elements.insuranceFilters",
    "elements.locationFilters",
    "elements.serviceLocationFilters",
    "hiddenElements.initial.valueReference",
    "hiddenElements.initial.valueSignature.signatureImage",
    "hiddenElements.initial.valueSignature.signer",
    "hiddenElements.initial.valueServiceEvents.serviceType",
    "hiddenElements.initial.valueServiceEvents.sections.form"
  ],
  Snippet: [
    "author",
    "shared"
  ],
  HealthcareService: [
    "additionalSections.form",
    "additionalSections.defaultComment",
    "groupNoteSections.form",
    "groupNoteSections.defaultComment",
    "carePlanTemplateForm",
    "overlappingServiceExceptions"
  ],
  Practitioner: [
    "signature",
    "contacts.organization",
    "contacts.addresses.district",
    "employments.department",
    "exclusions.patient",
    "groupMemberships.listing",
    "insuranceIdentifiers.assigner",
    "supervisors.subject",
    "availability.location",
    "availability.service",
    "avatar",
    "photo",
    "addresses.district",
    "profile.roles"
  ],
  Patient: [
    "defaultLocation",
    "careTeam.member",
    "comments.author",
    "confidentialTeam.practitioner",
    "contacts.organization",
    "contacts.addresses.district",
    "avatar",
    "photo",
    "addresses.district",
    "profile.roles"
  ],
  Condition: [
    "patient",
    "encounter",
    "assessor",
    "notes.author"
  ],
  EpisodeOfCare: [
    "patient",
    "careTeam.member",
    "diagnoses.condition",
    "programs.program",
    "programs.assignedLocation",
    "programs.diagnoses.condition"
  ],
  Schedule: [
    "episodeOfCare",
    "location",
    "owner",
    "program",
    "serviceType",
    "attendees.subject",
    "attendees.episodeOfCare"
  ],
  Appointment: [
    "episodeOfCare",
    "location",
    "owner",
    "program",
    "schedule",
    "serviceType",
    "serviceLocation",
    "attendees.subject",
    "attendees.episodeOfCare",
    "missedVisitServiceCode"
  ],
  CarePlan: [
    "author",
    "episodeOfCare",
    "patient",
    "activity.addresses"
  ],
  Transcription: [],
  Dialogue: [
    "subject",
    "practitioner",
    "audio",
    "transcription",
    "transcript.transcription",
    "transcript.speakers.subject"
  ],
  Contract: [],
  Consent: [
    "subject",
    "contract",
    "grantors.signatureImage",
    "grantors.signer"
  ],
  Encounter: [
    "appointment",
    "episodeOfCare",
    "location.location",
    "location.kind",
    "partOf",
    "program",
    "serviceType",
    "subject",
    "diagnoses.condition",
    "carePlanActivities.carePlan",
    "carePlanActivities.activity.addresses",
    "participants.individual",
    "visitStatusOverrides.serviceType",
    "patient",
    "problems.problem",
    "dialogues",
    "consents",
    "patientSignatures.signatureImage",
    "patientSignatures.signer"
  ],
  Allergy: [
    "encounter",
    "patient",
    "recorder",
    "notes.author"
  ],
  Announcement: [],
  CarePlanSession: [
    "author"
  ],
  CarePlanOutcome: [
    "author",
    "patient",
    "partOf",
    "carePlanActivities.carePlan",
    "carePlanActivities.activity.addresses",
    "episodeOfCare"
  ],
  DocumentReference: [
    "author",
    "patient"
  ],
  CdaParsingTask: [
    "document",
    "practitioners"
  ],
  ChatMessage: [
    "author",
    "channel",
    "thread",
    "documents",
    "media"
  ],
  Channel: [
    "author",
    "threadSource.channel",
    "threadSource.message"
  ],
  ChannelMembership: [
    "channel",
    "member"
  ],
  ClinicalDocument: [
    "document",
    "encounter.location.district",
    "originalDocument",
    "subject",
    "webDocument",
    "practitioners"
  ],
  Communication: [
    "inResponseTo",
    "sender",
    "about",
    "recipients.address.district",
    "recipients.user",
    "payloads"
  ],
  NoteDefinition: [
    "auditing.services",
    "sections.form",
    "sections.defaultComment",
    "services",
    "programs",
    "locations"
  ],
  NoteTemplate: [
    "author",
    "definition",
    "sections.form",
    "services"
  ],
  SignatureConfiguration: [
    "noteDefinition",
    "appliesToServices"
  ],
  GroupNoteDefinition: [
    "cancelled",
    "cancelledTemplate",
    "noShow",
    "noShowTemplate",
    "show",
    "showTemplate",
    "defaultShowSummaryForm",
    "defaultNoShowSummaryForm",
    "defaultCancelledSummaryForm",
    "alternativeShowNoteDefinitions",
    "alternativeShowNoteTemplates"
  ],
  Note: [
    "definition",
    "encounter",
    "partOf",
    "derivedFrom",
    "subject",
    "template",
    "signatureConfiguration",
    "summaryDocument",
    "participants.individual",
    "sections.form",
    "signatures.signatureImage",
    "signatures.signer",
    "patientSignatures.signatureImage",
    "patientSignatures.signer",
    "reviewers.assignee",
    "definitionAssignments.assignee",
    "definitionAssignments.definition",
    "definitionAssignments.service"
  ],
  ComplianceQuestionnaire: [
    "services",
    "programs",
    "definitions",
    "serviceExclusions",
    "programExclusions",
    "definitionExclusions",
    "locationExclusions",
    "locations"
  ],
  ComplianceAudit: [
    "note",
    "patient",
    "serviceType",
    "location",
    "program",
    "questionnaire",
    "participants.individual"
  ],
  Coverage: [
    "beneficiary",
    "payor",
    "policyHolder",
    "subscriber"
  ],
  DocumentTransmission: [
    "subject",
    "payload.document"
  ],
  Questionnaire: [
    "elements.initial.valueReference",
    "elements.initial.valueSignature.signatureImage",
    "elements.initial.valueSignature.signer",
    "elements.initial.valueServiceEvents.serviceType",
    "elements.initial.valueServiceEvents.sections.form",
    "elements.answerOptions.serviceTypeFilters",
    "elements.answerOptions.programFilters",
    "elements.answerOptions.insuranceFilters",
    "elements.answerOptions.locationFilters",
    "elements.answerOptions.serviceLocationFilters",
    "elements.form",
    "elements.serviceTypeFilters",
    "elements.programFilters",
    "elements.insuranceFilters",
    "elements.locationFilters",
    "elements.serviceLocationFilters"
  ],
  Form: [
    "questionnaires"
  ],
  Group: [
    "location",
    "serviceType",
    "program",
    "definition",
    "members.episodeOfCare",
    "members.member",
    "alternativeDefinitions"
  ],
  Itinerary: [
    "group"
  ],
  Medication: [
    "patient",
    "administeredBy",
    "prescriber",
    "notes.author"
  ],
  Notation: [],
  NoteAudit: [
    "note",
    "patient",
    "serviceType",
    "location",
    "program",
    "participants.individual",
    "similarNotes.note",
    "similarNotes.review.auditor",
    "overlappingNotes.note",
    "overlappingNotes.review.auditor",
    "complianceAuditReviews.review.auditor",
    "complianceAuditReviews.complianceAudit"
  ],
  NoteRule: [
    "limitToServices"
  ],
  Notification: [
    "recipient",
    "entities"
  ],
  ObservableRoutines: [
    "patient"
  ],
  Observation: [
    "encounter",
    "patient",
    "notes.author",
    "performer"
  ],
  Packet: [
    "programs"
  ],
  QuestionnaireResponse: [
    "author",
    "questionnaire",
    "source"
  ],
  PacketResponse: [
    "author",
    "encounter",
    "packet",
    "patient",
    "answers",
    "documents"
  ],
  PersonalPreferences: [
    "person",
    "pins",
    "lastReadNotification"
  ],
  PersonalSignature: [
    "person",
    "signature"
  ],
  PracticePreferences: [
    "noteCreationForCancelledAppointment.definition",
    "noteCreationForCancelledAppointment.serviceType",
    "emailTestingRecipients",
    "patientSignatureServiceTypeExclusions",
    "ambientListeningConsent",
    "scribeNoteFormatConfigurations.serviceTypes",
    "defaultGroupNoteDefinition",
    "appointmentStartNoteServiceTypeExclusions"
  ],
  RelatedPerson: [
    "company",
    "patient",
    "comments.author",
    "avatar",
    "photo",
    "addresses.district",
    "profile.roles"
  ],
  ReportCriteria: [
    "owner",
    "exclusions",
    "inclusions"
  ],
  ReportDelivery: [
    "author",
    "encryptedPassword.keyVault",
    "recipients.address.district",
    "recipients.user",
    "exclusions",
    "inclusions"
  ],
  Scribble: [
    "author",
    "encounter",
    "patient",
    "media",
    "documents"
  ],
  Script: [
    "programs"
  ],
  SecurityToken: [
    "owner",
    "patientAllowances"
  ],
  SharedScribble: [
    "patient",
    "encounter",
    "media",
    "collaborators",
    "documents"
  ],
  SnippetTemplate: [],
  SpeechRuleTemplate: [],
  SpeechRule: [
    "noteDefinitions.definition",
    "services",
    "userExclusions",
    "derivedFrom"
  ],
  TextExpansion: [
    "author"
  ],
  TextExpansionTemplate: []
};
const readOnlyPaths = {
  KeyVault: [
    "display",
    "store.display",
    "owner.resource",
    "resourceType"
  ],
  Media: [
    "partition",
    "thumbnails",
    "recognizedText",
    "resourceType"
  ],
  District: [
    "display",
    "resourceType"
  ],
  GrantSet: [
    "display",
    "resourceType"
  ],
  SecurityRole: [
    "derivedFrom.resourceType",
    "derivedFrom.resource",
    "distribution",
    "hidden",
    "display",
    "grants.resourceType",
    "grants.resource",
    "resourceType"
  ],
  Person: [
    "avatar.resourceType",
    "avatar.resource",
    "photo.resourceType",
    "photo.resource",
    "display",
    "name.display",
    "profile.roles.resourceType",
    "profile.roles.resource",
    "resourceType"
  ],
  Location: [
    "display",
    "resourceType"
  ],
  Organization: [
    "contacts.name.display",
    "display",
    "resourceType"
  ],
  Listing: [
    "author.resource",
    "display",
    "resourceType"
  ],
  Program: [
    "parent.resourceType",
    "parent.resource",
    "display",
    "defaultLocationKind.resourceType",
    "defaultLocationKind.resource",
    "allowedLocationKinds.resourceType",
    "allowedLocationKinds.resource",
    "resourceType"
  ],
  NoteSectionForm: [
    "display",
    "elements.initial.valueReference.resource",
    "elements.initial.valueSignature.signatureImage.resourceType",
    "elements.initial.valueSignature.signatureImage.resource",
    "elements.initial.valueServiceEvents.serviceType.resourceType",
    "elements.initial.valueServiceEvents.serviceType.resource",
    "elements.initial.valueServiceEvents.sections.form.resourceType",
    "elements.initial.valueServiceEvents.sections.form.resource",
    "elements.answerOptions.serviceTypeFilters.resourceType",
    "elements.answerOptions.serviceTypeFilters.resource",
    "elements.answerOptions.programFilters.resourceType",
    "elements.answerOptions.programFilters.resource",
    "elements.answerOptions.insuranceFilters.resourceType",
    "elements.answerOptions.insuranceFilters.resource",
    "elements.answerOptions.locationFilters.resourceType",
    "elements.answerOptions.locationFilters.resource",
    "elements.answerOptions.serviceLocationFilters.resourceType",
    "elements.answerOptions.serviceLocationFilters.resource",
    "elements.form.resourceType",
    "elements.form.resource",
    "elements.serviceTypeFilters.resourceType",
    "elements.serviceTypeFilters.resource",
    "elements.programFilters.resourceType",
    "elements.programFilters.resource",
    "elements.insuranceFilters.resourceType",
    "elements.insuranceFilters.resource",
    "elements.locationFilters.resourceType",
    "elements.locationFilters.resource",
    "elements.serviceLocationFilters.resourceType",
    "elements.serviceLocationFilters.resource",
    "hiddenElements.initial.valueReference.resource",
    "hiddenElements.initial.valueSignature.signatureImage.resourceType",
    "hiddenElements.initial.valueSignature.signatureImage.resource",
    "hiddenElements.initial.valueServiceEvents.serviceType.resourceType",
    "hiddenElements.initial.valueServiceEvents.serviceType.resource",
    "hiddenElements.initial.valueServiceEvents.sections.form.resourceType",
    "hiddenElements.initial.valueServiceEvents.sections.form.resource",
    "usage",
    "resourceType"
  ],
  Snippet: [
    "author.resourceType",
    "author.resource",
    "display",
    "shared.resourceType",
    "shared.resource",
    "resourceType"
  ],
  HealthcareService: [
    "display",
    "additionalSections.form.resourceType",
    "additionalSections.form.resource",
    "additionalSections.defaultComment.resourceType",
    "additionalSections.defaultComment.resource",
    "groupNoteSections.form.resourceType",
    "groupNoteSections.form.resource",
    "groupNoteSections.defaultComment.resourceType",
    "groupNoteSections.defaultComment.resource",
    "carePlanTemplateForm.resourceType",
    "carePlanTemplateForm.resource",
    "overlappingServiceExceptions.resourceType",
    "overlappingServiceExceptions.resource",
    "resourceType"
  ],
  Practitioner: [
    "signature.resourceType",
    "signature.resource",
    "otherNames.display",
    "contacts.name.display",
    "employments.department.resourceType",
    "employments.department.resource",
    "exclusions.patient.resourceType",
    "exclusions.patient.resource",
    "groupMemberships.listing.resourceType",
    "groupMemberships.listing.resource",
    "insuranceIdentifiers.assigner.resourceType",
    "insuranceIdentifiers.assigner.resource",
    "supervisors.subject.resourceType",
    "supervisors.subject.resource",
    "availability.location.resourceType",
    "availability.location.resource",
    "availability.service.resourceType",
    "availability.service.resource",
    "resourceType",
    "avatar.resourceType",
    "avatar.resource",
    "photo.resourceType",
    "photo.resource",
    "display",
    "name.display",
    "profile.roles.resourceType",
    "profile.roles.resource"
  ],
  Patient: [
    "defaultLocation.resourceType",
    "defaultLocation.resource",
    "careTeam.member.resource",
    "comments.author.resourceType",
    "comments.author.resource",
    "confidentialTeam.practitioner.resourceType",
    "confidentialTeam.practitioner.resource",
    "contacts.name.display",
    "display",
    "otherNames.display",
    "resourceType",
    "avatar.resourceType",
    "avatar.resource",
    "photo.resourceType",
    "photo.resource",
    "name.display",
    "profile.roles.resourceType",
    "profile.roles.resource"
  ],
  Condition: [
    "partition",
    "display",
    "patient",
    "patient.resourceType",
    "patient.resource",
    "encounter.resourceType",
    "encounter.resource",
    "assessor.resourceType",
    "assessor.resource",
    "onsetDate",
    "resolutionDate",
    "notes.author.resourceType",
    "notes.author.resource",
    "resourceType"
  ],
  EpisodeOfCare: [
    "patient",
    "patient.resourceType",
    "patient.resource",
    "careTeam.member.resource",
    "diagnoses.condition.resourceType",
    "diagnoses.condition.resource",
    "display",
    "partition",
    "programs.program.resourceType",
    "programs.program.resource",
    "programs.assignedLocation.resourceType",
    "programs.assignedLocation.resource",
    "programs.diagnoses.condition.resourceType",
    "programs.diagnoses.condition.resource",
    "resourceType"
  ],
  Schedule: [
    "episodeOfCare.resourceType",
    "episodeOfCare.resource",
    "location",
    "location.resourceType",
    "location.resource",
    "owner.resourceType",
    "owner.resource",
    "program.resourceType",
    "program.resource",
    "serviceType",
    "serviceType.resourceType",
    "serviceType.resource",
    "attendees.subject.resource",
    "attendees.episodeOfCare.resourceType",
    "attendees.episodeOfCare.resource",
    "duration",
    "resourceType"
  ],
  Appointment: [
    "created",
    "episodeOfCare.resourceType",
    "episodeOfCare.resource",
    "location.resourceType",
    "location.resource",
    "owner.resourceType",
    "owner.resource",
    "program.resourceType",
    "program.resource",
    "schedule.resourceType",
    "schedule.resource",
    "serviceType.resourceType",
    "serviceType.resource",
    "serviceLocation.resourceType",
    "serviceLocation.resource",
    "attendees.subject.resource",
    "attendees.episodeOfCare.resourceType",
    "attendees.episodeOfCare.resource",
    "duration",
    "isGroup",
    "partition",
    "missedVisitServiceCode.resourceType",
    "missedVisitServiceCode.resource",
    "resourceType"
  ],
  CarePlan: [
    "partition",
    "author.resourceType",
    "author.resource",
    "episodeOfCare.resourceType",
    "episodeOfCare.resource",
    "patient",
    "patient.resourceType",
    "patient.resource",
    "display",
    "resourceType"
  ],
  Transcription: [
    "partition",
    "resourceType"
  ],
  Dialogue: [
    "partition",
    "subject",
    "subject.resourceType",
    "subject.resource",
    "practitioner.resourceType",
    "practitioner.resource",
    "audio.resourceType",
    "audio.resource",
    "transcription.resourceType",
    "transcription.resource",
    "transcript.transcription.resourceType",
    "transcript.transcription.resource",
    "resourceType"
  ],
  Contract: [
    "resourceType"
  ],
  Consent: [
    "partition",
    "subject.resourceType",
    "subject.resource",
    "contract.resourceType",
    "contract.resource",
    "created",
    "grantors.signatureImage.resourceType",
    "grantors.signatureImage.resource",
    "resourceType"
  ],
  Encounter: [
    "partition",
    "appointment.resourceType",
    "appointment.resource",
    "episodeOfCare.resourceType",
    "episodeOfCare.resource",
    "location.location.resourceType",
    "location.location.resource",
    "location.kind.resourceType",
    "location.kind.resource",
    "partOf.resourceType",
    "partOf.resource",
    "program.resourceType",
    "program.resource",
    "serviceType.resourceType",
    "serviceType.resource",
    "subject",
    "subject.resource",
    "diagnoses.condition.resourceType",
    "diagnoses.condition.resource",
    "carePlanActivities.carePlan.resourceType",
    "carePlanActivities.carePlan.resource",
    "participants.individual.resourceType",
    "participants.individual.resource",
    "visitStatusOverrides.serviceType.resourceType",
    "visitStatusOverrides.serviceType.resource",
    "patient",
    "patient.resource",
    "dialogues.resourceType",
    "dialogues.resource",
    "consents.resourceType",
    "consents.resource",
    "patientSignatures.signatureImage.resourceType",
    "patientSignatures.signatureImage.resource",
    "resourceType"
  ],
  Allergy: [
    "encounter.resourceType",
    "encounter.resource",
    "patient",
    "patient.resourceType",
    "patient.resource",
    "recorder.resourceType",
    "recorder.resource",
    "notes.author.resourceType",
    "notes.author.resource",
    "partition",
    "resourceType"
  ],
  Announcement: [
    "created",
    "resourceType"
  ],
  CarePlanSession: [
    "partition",
    "author.resourceType",
    "author.resource",
    "resourceType"
  ],
  CarePlanOutcome: [
    "partition",
    "author.resourceType",
    "author.resource",
    "patient",
    "patient.resourceType",
    "patient.resource",
    "partOf.resourceType",
    "partOf.resource",
    "carePlanActivities.carePlan.resourceType",
    "carePlanActivities.carePlan.resource",
    "episodeOfCare.resourceType",
    "episodeOfCare.resource",
    "resourceType"
  ],
  DocumentReference: [
    "author.resource",
    "created",
    "patient.resourceType",
    "patient.resource",
    "display",
    "partition",
    "thumbnails",
    "recognizedText",
    "resourceType"
  ],
  CdaParsingTask: [
    "document.resourceType",
    "document.resource",
    "practitioners.resourceType",
    "practitioners.resource",
    "resourceType"
  ],
  ChatMessage: [
    "author",
    "author.resource",
    "channel",
    "channel.resourceType",
    "channel.resource",
    "sent",
    "thread",
    "thread.resourceType",
    "thread.resource",
    "documents.resourceType",
    "documents.resource",
    "media.resourceType",
    "media.resource",
    "partition",
    "resourceType"
  ],
  Channel: [
    "author",
    "author.resource",
    "created",
    "lastMessageDate",
    "membersHash",
    "messagesCount",
    "threadSource.channel.resourceType",
    "threadSource.channel.resource",
    "threadSource.message.resourceType",
    "threadSource.message.resource",
    "type",
    "display",
    "resourceType"
  ],
  ChannelMembership: [
    "channel.resourceType",
    "channel.resource",
    "member.resource",
    "resourceType"
  ],
  ClinicalDocument: [
    "document.resourceType",
    "document.resource",
    "originalDocument.resourceType",
    "originalDocument.resource",
    "subject",
    "subject.resourceType",
    "subject.resource",
    "webDocument.resourceType",
    "webDocument.resource",
    "partition",
    "practitioners.resourceType",
    "practitioners.resource",
    "resourceType"
  ],
  Communication: [
    "inResponseTo.resourceType",
    "inResponseTo.resource",
    "sender.resource",
    "about.resource",
    "recipients.user.resource",
    "payloads.resourceType",
    "payloads.resource",
    "resourceType"
  ],
  NoteDefinition: [
    "display",
    "created",
    "auditing.services.resourceType",
    "auditing.services.resource",
    "sections.form.resourceType",
    "sections.form.resource",
    "sections.defaultComment.resourceType",
    "sections.defaultComment.resource",
    "services.resourceType",
    "services.resource",
    "programs.resourceType",
    "programs.resource",
    "locations.resourceType",
    "locations.resource",
    "resourceType"
  ],
  NoteTemplate: [
    "author.resourceType",
    "author.resource",
    "created",
    "definition.resourceType",
    "definition.resource",
    "display",
    "sections.form.resourceType",
    "sections.form.resource",
    "services.resourceType",
    "services.resource",
    "resourceType"
  ],
  SignatureConfiguration: [
    "noteDefinition.resourceType",
    "noteDefinition.resource",
    "appliesToServices.resourceType",
    "appliesToServices.resource",
    "resourceType"
  ],
  GroupNoteDefinition: [
    "display",
    "cancelled.resourceType",
    "cancelled.resource",
    "cancelledTemplate.resourceType",
    "cancelledTemplate.resource",
    "created",
    "noShow.resourceType",
    "noShow.resource",
    "noShowTemplate.resourceType",
    "noShowTemplate.resource",
    "show.resourceType",
    "show.resource",
    "showTemplate.resourceType",
    "showTemplate.resource",
    "defaultShowSummaryForm.resourceType",
    "defaultShowSummaryForm.resource",
    "defaultNoShowSummaryForm.resourceType",
    "defaultNoShowSummaryForm.resource",
    "defaultCancelledSummaryForm.resourceType",
    "defaultCancelledSummaryForm.resource",
    "alternativeShowNoteDefinitions.resourceType",
    "alternativeShowNoteDefinitions.resource",
    "alternativeShowNoteTemplates.resourceType",
    "alternativeShowNoteTemplates.resource",
    "resourceType"
  ],
  Note: [
    "partition",
    "created",
    "definition.resource",
    "encounter.resourceType",
    "encounter.resource",
    "partOf.resourceType",
    "partOf.resource",
    "derivedFrom.resourceType",
    "derivedFrom.resource",
    "subject",
    "subject.resource",
    "template.resourceType",
    "template.resource",
    "signatureConfiguration.resourceType",
    "signatureConfiguration.resource",
    "summaryDocument.resourceType",
    "summaryDocument.resource",
    "participants.individual.resourceType",
    "participants.individual.resource",
    "sections.form.resourceType",
    "sections.form.resource",
    "signatures.signatureImage.resourceType",
    "signatures.signatureImage.resource",
    "patientSignatures.signatureImage.resourceType",
    "patientSignatures.signatureImage.resource",
    "reviewers.assignee.resourceType",
    "reviewers.assignee.resource",
    "definitionAssignments.assignee.resourceType",
    "definitionAssignments.assignee.resource",
    "definitionAssignments.definition.resourceType",
    "definitionAssignments.definition.resource",
    "definitionAssignments.service.resourceType",
    "definitionAssignments.service.resource",
    "syncIssueMessage",
    "resourceType"
  ],
  ComplianceQuestionnaire: [
    "created",
    "display",
    "services.resourceType",
    "services.resource",
    "programs.resourceType",
    "programs.resource",
    "definitions.resourceType",
    "definitions.resource",
    "serviceExclusions.resourceType",
    "serviceExclusions.resource",
    "programExclusions.resourceType",
    "programExclusions.resource",
    "definitionExclusions.resourceType",
    "definitionExclusions.resource",
    "locationExclusions.resourceType",
    "locationExclusions.resource",
    "locations.resourceType",
    "locations.resource",
    "resourceType"
  ],
  ComplianceAudit: [
    "partition",
    "note.resourceType",
    "note.resource",
    "patient",
    "patient.resourceType",
    "patient.resource",
    "serviceType.resourceType",
    "serviceType.resource",
    "location.resourceType",
    "location.resource",
    "program.resourceType",
    "program.resource",
    "questionnaire.resourceType",
    "questionnaire.resource",
    "participants.individual.resourceType",
    "participants.individual.resource",
    "resourceType"
  ],
  Coverage: [
    "beneficiary",
    "beneficiary.resourceType",
    "beneficiary.resource",
    "policyHolder.resource",
    "subscriber.resource",
    "partition",
    "resourceType"
  ],
  DocumentTransmission: [
    "requested",
    "subject.resourceType",
    "subject.resource",
    "payload.document.resourceType",
    "payload.document.resource",
    "resourceType"
  ],
  Questionnaire: [
    "created",
    "distribution",
    "display",
    "elements.initial.valueReference.resource",
    "elements.initial.valueSignature.signatureImage.resourceType",
    "elements.initial.valueSignature.signatureImage.resource",
    "elements.initial.valueServiceEvents.serviceType.resourceType",
    "elements.initial.valueServiceEvents.serviceType.resource",
    "elements.initial.valueServiceEvents.sections.form.resourceType",
    "elements.initial.valueServiceEvents.sections.form.resource",
    "elements.answerOptions.serviceTypeFilters.resourceType",
    "elements.answerOptions.serviceTypeFilters.resource",
    "elements.answerOptions.programFilters.resourceType",
    "elements.answerOptions.programFilters.resource",
    "elements.answerOptions.insuranceFilters.resourceType",
    "elements.answerOptions.insuranceFilters.resource",
    "elements.answerOptions.locationFilters.resourceType",
    "elements.answerOptions.locationFilters.resource",
    "elements.answerOptions.serviceLocationFilters.resourceType",
    "elements.answerOptions.serviceLocationFilters.resource",
    "elements.form.resourceType",
    "elements.form.resource",
    "elements.serviceTypeFilters.resourceType",
    "elements.serviceTypeFilters.resource",
    "elements.programFilters.resourceType",
    "elements.programFilters.resource",
    "elements.insuranceFilters.resourceType",
    "elements.insuranceFilters.resource",
    "elements.locationFilters.resourceType",
    "elements.locationFilters.resource",
    "elements.serviceLocationFilters.resourceType",
    "elements.serviceLocationFilters.resource",
    "resourceType"
  ],
  Form: [
    "display",
    "questionnaires.resourceType",
    "questionnaires.resource",
    "resourceType"
  ],
  Group: [
    "location.resourceType",
    "location.resource",
    "serviceType.resourceType",
    "serviceType.resource",
    "program.resourceType",
    "program.resource",
    "definition.resourceType",
    "definition.resource",
    "display",
    "members.episodeOfCare.resourceType",
    "members.episodeOfCare.resource",
    "members.member.resource",
    "alternativeDefinitions.resourceType",
    "alternativeDefinitions.resource",
    "resourceType"
  ],
  Itinerary: [
    "group.resourceType",
    "group.resource",
    "display",
    "resourceType"
  ],
  Medication: [
    "patient",
    "patient.resourceType",
    "patient.resource",
    "administeredBy.resourceType",
    "administeredBy.resource",
    "prescriber.resourceType",
    "prescriber.resource",
    "notes.author.resourceType",
    "notes.author.resource",
    "partition",
    "resourceType"
  ],
  Notation: [
    "resourceType"
  ],
  NoteAudit: [
    "partition",
    "note.resourceType",
    "note.resource",
    "patient",
    "patient.resourceType",
    "patient.resource",
    "serviceType.resourceType",
    "serviceType.resource",
    "location.resourceType",
    "location.resource",
    "program.resourceType",
    "program.resource",
    "participants.individual.resourceType",
    "participants.individual.resource",
    "similarNotes.note.resourceType",
    "similarNotes.note.resource",
    "similarNotes.review.auditor.resourceType",
    "similarNotes.review.auditor.resource",
    "overlappingNotes.note.resourceType",
    "overlappingNotes.note.resource",
    "overlappingNotes.review.auditor.resourceType",
    "overlappingNotes.review.auditor.resource",
    "complianceAuditReviews.review.auditor.resourceType",
    "complianceAuditReviews.review.auditor.resource",
    "complianceAuditReviews.complianceAudit.resourceType",
    "complianceAuditReviews.complianceAudit.resource",
    "resourceType"
  ],
  NoteRule: [
    "display",
    "criteria.display",
    "limitToServices.resourceType",
    "limitToServices.resource",
    "resourceType"
  ],
  Notification: [
    "created",
    "recipient",
    "recipient.resource",
    "partition",
    "entities.resource",
    "resourceType"
  ],
  ObservableRoutines: [
    "patient",
    "patient.resourceType",
    "patient.resource",
    "partition",
    "resourceType"
  ],
  Observation: [
    "encounter.resourceType",
    "encounter.resource",
    "patient",
    "patient.resourceType",
    "patient.resource",
    "display",
    "notes.author.resourceType",
    "notes.author.resource",
    "partition",
    "performer.resource",
    "resourceType"
  ],
  Packet: [
    "display",
    "programs.resourceType",
    "programs.resource",
    "resourceType"
  ],
  QuestionnaireResponse: [
    "author.resource",
    "questionnaire",
    "questionnaire.resourceType",
    "questionnaire.resource",
    "source.resource",
    "partition",
    "resourceType"
  ],
  PacketResponse: [
    "author.resourceType",
    "author.resource",
    "created",
    "encounter.resourceType",
    "encounter.resource",
    "packet.resourceType",
    "packet.resource",
    "patient",
    "patient.resourceType",
    "patient.resource",
    "answers.resourceType",
    "answers.resource",
    "documents.resourceType",
    "documents.resource",
    "partition",
    "resourceType"
  ],
  PersonalPreferences: [
    "person.resource",
    "pins.resource",
    "lastReadNotification.resourceType",
    "lastReadNotification.resource",
    "resourceType"
  ],
  PersonalSignature: [
    "person.resource",
    "signature.resourceType",
    "signature.resource",
    "resourceType"
  ],
  PracticePreferences: [
    "noteCreationForCancelledAppointment.definition.resourceType",
    "noteCreationForCancelledAppointment.definition.resource",
    "noteCreationForCancelledAppointment.serviceType.resourceType",
    "noteCreationForCancelledAppointment.serviceType.resource",
    "emailTestingRecipients.resourceType",
    "emailTestingRecipients.resource",
    "patientSignatureServiceTypeExclusions.resourceType",
    "patientSignatureServiceTypeExclusions.resource",
    "ambientListeningConsent.resourceType",
    "ambientListeningConsent.resource",
    "scribeNoteFormatConfigurations.serviceTypes.resourceType",
    "scribeNoteFormatConfigurations.serviceTypes.resource",
    "defaultGroupNoteDefinition.resourceType",
    "defaultGroupNoteDefinition.resource",
    "appointmentStartNoteServiceTypeExclusions.resourceType",
    "appointmentStartNoteServiceTypeExclusions.resource",
    "resourceType"
  ],
  RelatedPerson: [
    "patient",
    "patient.resourceType",
    "patient.resource",
    "accessPeriod",
    "comments.author.resourceType",
    "comments.author.resource",
    "partition",
    "suspended",
    "resourceType",
    "avatar.resourceType",
    "avatar.resource",
    "photo.resourceType",
    "photo.resource",
    "display",
    "name.display",
    "profile.roles.resourceType",
    "profile.roles.resource"
  ],
  ReportCriteria: [
    "owner.resourceType",
    "owner.resource",
    "display",
    "exclusions.resource",
    "inclusions.resource",
    "resourceType"
  ],
  ReportDelivery: [
    "display",
    "author.resourceType",
    "author.resource",
    "encryptedPassword",
    "encryptedPassword.keyVault.resourceType",
    "encryptedPassword.keyVault.resource",
    "recipients.user.resource",
    "exclusions.resource",
    "inclusions.resource",
    "resourceType"
  ],
  Scribble: [
    "author",
    "author.resourceType",
    "author.resource",
    "created",
    "encounter.resourceType",
    "encounter.resource",
    "patient.resourceType",
    "patient.resource",
    "media.resourceType",
    "media.resource",
    "partition",
    "documents.resourceType",
    "documents.resource",
    "resourceType"
  ],
  Script: [
    "display",
    "programs.resourceType",
    "programs.resource",
    "resourceType"
  ],
  SecurityToken: [
    "owner.resource",
    "patientAllowances.resourceType",
    "patientAllowances.resource",
    "resourceType"
  ],
  SharedScribble: [
    "created",
    "patient.resourceType",
    "patient.resource",
    "encounter.resourceType",
    "encounter.resource",
    "media.resourceType",
    "media.resource",
    "collaborators.resourceType",
    "collaborators.resource",
    "documents.resourceType",
    "documents.resource",
    "resourceType"
  ],
  SnippetTemplate: [
    "display",
    "resourceType"
  ],
  SpeechRuleTemplate: [
    "display",
    "rule.display",
    "resourceType"
  ],
  SpeechRule: [
    "display",
    "rule.display",
    "noteDefinitions.definition.resourceType",
    "noteDefinitions.definition.resource",
    "services.resourceType",
    "services.resource",
    "userExclusions.resourceType",
    "userExclusions.resource",
    "derivedFrom.resourceType",
    "derivedFrom.resource",
    "resourceType"
  ],
  TextExpansion: [
    "author.resourceType",
    "author.resource",
    "display",
    "resourceType"
  ],
  TextExpansionTemplate: [
    "display",
    "resourceType"
  ]
};
const expansionPaths = {
  Appointment: {
    Attendees: [{ resourceType: null, path: "attendees.subject" }],
    Owner: [{ resourceType: "Practitioner", path: "owner" }],
    Location: [{ resourceType: "Location", path: "location" }, { resourceType: "Location", path: "serviceLocation" }],
    Schedule: [{ resourceType: "Schedule", path: "schedule" }],
    Program: [{ resourceType: "Program", path: "program" }]
  },
  CarePlanOutcome: {
    PartOf: [{ resourceType: "CarePlanSession", path: "partOf" }],
    Patient: [{ resourceType: "Patient", path: "patient" }],
    EpisodeOfCare: [{ resourceType: "EpisodeOfCare", path: "episodeOfCare" }]
  },
  CdaParsingTask: {
    Document: [{ resourceType: "DocumentReference", path: "document" }]
  },
  ChannelMembership: {
    Channel: [{ resourceType: "Channel", path: "channel" }],
    Member: [{ resourceType: null, path: "member" }]
  },
  ChatMessage: {
    Author: [{ resourceType: null, path: "author" }],
    Channel: [{ resourceType: "Channel", path: "channel" }],
    Documents: [{ resourceType: "DocumentReference", path: "documents" }],
    Media: [{ resourceType: "Media", path: "media" }],
    Thread: [{ resourceType: "Channel", path: "thread" }]
  },
  ClinicalDocument: {
    Document: [{ resourceType: "DocumentReference", path: "document" }],
    Subject: [{ resourceType: "Patient", path: "subject" }]
  },
  ComplianceAudit: {
    Participants: [{ resourceType: "Practitioner", path: "participants.individual" }],
    Patient: [{ resourceType: "Patient", path: "patient" }],
    Note: [{ resourceType: "Note", path: "note" }],
    Questionnaire: [{ resourceType: "ComplianceQuestionnaire", path: "questionnaire" }]
  },
  ComplianceQuestionnaire: {
    Services: [{ resourceType: "HealthcareService", path: "services" }],
    Programs: [{ resourceType: "Program", path: "programs" }],
    Definitions: [{ resourceType: "NoteDefinition", path: "definitions" }],
    Locations: [{ resourceType: "Location", path: "locations" }]
  },
  Condition: {
    Patient: [{ resourceType: "Patient", path: "patient" }]
  },
  Dialogue: {
    Subject: [{ resourceType: "Patient", path: "subject" }],
    Practitioner: [{ resourceType: "Practitioner", path: "practitioner" }],
    Audio: [{ resourceType: "Media", path: "audio" }],
    Transcription: [{ resourceType: "Transcription", path: "transcription" }]
  },
  DocumentTransmission: {
    Document: [{ resourceType: "DocumentReference", path: "payload.document" }]
  },
  Encounter: {
    Participant: [{ resourceType: "Practitioner", path: "participants.individual" }],
    ServiceType: [{ resourceType: "HealthcareService", path: "serviceType" }],
    Location: [{ resourceType: "Location", path: "location.location" }],
    LocationKind: [{ resourceType: "Location", path: "location.kind" }],
    Appointment: [{ resourceType: "Appointment", path: "appointment" }],
    EpisodeOfCare: [{ resourceType: "EpisodeOfCare", path: "episodeOfCare" }],
    Subject: [{ resourceType: null, path: "subject" }],
    PartOf: [{ resourceType: "Encounter", path: "partOf" }],
    CarePlans: [{ resourceType: "CarePlan", path: "carePlanActivities.carePlan" }],
    Dialogues: [{ resourceType: "Dialogue", path: "dialogues" }],
    Program: [{ resourceType: "Program", path: "program" }]
  },
  EpisodeOfCare: {
    Patient: [{ resourceType: "Patient", path: "patient" }],
    Condition: [{ resourceType: "Condition", path: "diagnoses.condition" }],
    Program: [{ resourceType: "Program", path: "programs.program" }]
  },
  Form: {
    Questionnaires: [{ resourceType: "Questionnaire", path: "questionnaires" }]
  },
  Group: {
    Members: [{ resourceType: null, path: "members.member" }]
  },
  GroupNoteDefinition: {
    Show: [{ resourceType: "NoteDefinition", path: "show" }],
    NoShow: [{ resourceType: "NoteDefinition", path: "noShow" }],
    Cancelled: [{ resourceType: "NoteDefinition", path: "cancelled" }],
    ShowTemplate: [{ resourceType: "NoteTemplate", path: "showTemplate" }],
    NoShowTemplate: [{ resourceType: "NoteTemplate", path: "noShowTemplate" }],
    CancelledTemplate: [{ resourceType: "NoteTemplate", path: "cancelledTemplate" }]
  },
  HealthcareService: {
    Forms: [{ resourceType: "NoteSectionForm", path: "additionalSections.form" }]
  },
  Note: {
    Encounter: [{ resourceType: "Encounter", path: "encounter" }],
    Participants: [{ resourceType: "Practitioner", path: "participants.individual" }],
    Definition: [{ resourceType: null, path: "definition" }],
    Subject: [{ resourceType: null, path: "subject" }],
    PartOf: [{ resourceType: "Note", path: "partOf" }],
    DerivedFrom: [{ resourceType: "CarePlanOutcome", path: "derivedFrom" }],
    SignatureConfiguration: [{ resourceType: "SignatureConfiguration", path: "signatureConfiguration" }],
    Forms: [{ resourceType: "NoteSectionForm", path: "sections.form" }]
  },
  NoteAudit: {
    Participants: [{ resourceType: "Practitioner", path: "participants.individual" }],
    Patient: [{ resourceType: "Patient", path: "patient" }],
    SimilarNotes: [{ resourceType: "Note", path: "similarNotes.note" }],
    OverlappingNotes: [{ resourceType: "Note", path: "overlappingNotes.note" }],
    ComplianceAuditReviews: [{ resourceType: "ComplianceAudit", path: "complianceAuditReviews.complianceAudit" }]
  },
  NoteDefinition: {
    Services: [{ resourceType: "HealthcareService", path: "services" }],
    Forms: [{ resourceType: "NoteSectionForm", path: "sections.form" }]
  },
  NoteSectionForm: {
    Form: [{ resourceType: "NoteSectionForm", path: "elements.form" }]
  },
  NoteTemplate: {
    Author: [{ resourceType: "Practitioner", path: "author" }],
    Definition: [{ resourceType: "NoteDefinition", path: "definition" }],
    Services: [{ resourceType: "HealthcareService", path: "services" }]
  },
  Packet: {
    Forms: [{ resourceType: "Form", path: "forms" }],
    Programs: [{ resourceType: "Program", path: "programs" }]
  },
  PacketResponse: {
    Packets: [{ resourceType: "Packet", path: "packet" }],
    Answers: [{ resourceType: "QuestionnaireResponse", path: "answers" }],
    Authors: [{ resourceType: "Practitioner", path: "author" }]
  },
  Patient: {
    CareTeam: [{ resourceType: null, path: "careTeam.member" }]
  },
  PersonalPreferences: {
    Pins: [{ resourceType: null, path: "pins" }]
  },
  PersonalSignature: {
    Signature: [{ resourceType: "Media", path: "signature" }]
  },
  Practitioner: {
    Supervisors: [{ resourceType: "Practitioner", path: "supervisors.subject" }],
    Avatar: [{ resourceType: "Media", path: "avatar" }],
    Photo: [{ resourceType: "Media", path: "photo" }],
    Signature: [{ resourceType: "Media", path: "signature" }],
    GroupMemberships: [{ resourceType: "Listing", path: "groupMemberships.listing" }],
    EmploymentDepartment: [{ resourceType: "Organization", path: "employments.department" }]
  },
  RelatedPerson: {
    Patient: [{ resourceType: "Patient", path: "patient" }]
  },
  ReportDelivery: {
    Author: [{ resourceType: "Practitioner", path: "author" }],
    Recipients: [{ resourceType: null, path: "recipients.user" }]
  },
  Scribble: {
    Patient: [{ resourceType: "Patient", path: "patient" }],
    Author: [{ resourceType: "Practitioner", path: "author" }],
    Media: [{ resourceType: "Media", path: "media" }]
  },
  SecurityRole: {
    Grants: [{ resourceType: "GrantSet", path: "grants" }]
  },
  SharedScribble: {
    Patient: [{ resourceType: "Patient", path: "patient" }],
    Media: [{ resourceType: "Media", path: "media" }],
    Collaborators: [{ resourceType: "Practitioner", path: "collaborators" }]
  },
  Snippet: {
    Shared: [{ resourceType: "Practitioner", path: "shared" }]
  }
};
const exclusionPaths = {
  Note: {
    Sections: ["sections"]
  },
  Practitioner: {
    Supervisors: ["supervisors"]
  }
};
const SortDirection = {
  Unspecified: "Unspecified",
  Ascending: "Ascending",
  Descending: "Descending"
};
const DateTimePrecision = {
  Full: "Full",
  YearMonthDay: "YearMonthDay",
  YearMonth: "YearMonth",
  Year: "Year"
};
const ResourceSyncStatus = {
  /** A sync is expected. */
  Pending: "Pending",
  /** The resource was successfully synced. */
  Success: "Success",
  /** The resource failed to be synced and no further attempts are scheduled. */
  Failed: "Failed",
  /** The resource synced successfully but had a warning to give. */
  Warning: "Warning",
  /** A sync is needed, but is currently on hold to be scheduled in the future. */
  Hold: "Hold"
};
const ResourcePullOutcome = {
  /** The resource was successfully synced. */
  Success: "Success",
  /** The resource failed to be synced and no further attempts are scheduled. */
  Failed: "Failed",
  /** The resource synced successfully but had a warning to give. */
  Warning: "Warning"
};
const AdministrativeFlags = {
  FullPermissions: "FullPermissions",
  /** @deprecated No longer use */
  UserManagement: "UserManagement",
  RoleManagement: "RoleManagement",
  CanManageUsers: "CanManageUsers",
  CanInviteUsers: "CanInviteUsers",
  CanModifyRoles: "CanModifyRoles",
  ManagePracticeDatabase: "ManagePracticeDatabase",
  ViewAllPractices: "ViewAllPractices",
  ModifyNonPracticeDistribution: "ModifyNonPracticeDistribution",
  AccessAllNotifications: "AccessAllNotifications",
  CanViewPermission: "CanViewPermission",
  ClinicalConfiguration: "ClinicalConfiguration",
  PreferencesMaster: "PreferencesMaster",
  BellsReportMaster: "BellsReportMaster",
  SecurityTokenManagement: "SecurityTokenManagement",
  /** @deprecated No longer use */
  Supervisor: "Supervisor",
  Scribe: "Scribe",
  /** @deprecated Use Scribe */
  AmbientListening: "AmbientListening",
  ComplianceMaster: "ComplianceMaster",
  ManageItineraries: "ManageItineraries",
  CanEmailReports: "CanEmailReports",
  CanEditNoteAudit: "CanEditNoteAudit",
  CanEditComplianceAudit: "CanEditComplianceAudit",
  CanViewAuditReviews: "CanViewAuditReviews",
  SyncIssueMaster: "SyncIssueMaster",
  NoteMaster: "NoteMaster",
  PatientMaster: "PatientMaster",
  ScribbleMaster: "ScribbleMaster"
};
const ClinicalFlags = {
  HealthHistory: "HealthHistory",
  Medications: "Medications",
  Note: "Note",
  OrderResults: "OrderResults",
  Prescribe: "Prescribe",
  /** Used to default all administrators to the All patient population at the API level. */
  PatientPopulationAll: "PatientPopulationAll",
  /** @deprecated Replaced by PatientPopulationLevel configurations */
  CareTeamView: "CareTeamView",
  Supervision: "Supervision",
  QualityAssurance: "QualityAssurance"
};
const InternalFlags = {
  SkipPopulationFiltering: "SkipPopulationFiltering",
  CanRequestNoHistory: "CanRequestNoHistory",
  SkipInclusiveSecurityLabels: "SkipInclusiveSecurityLabels"
};
const MessagingFlags = {
  CanReadAllMessages: "CanReadAllMessages",
  CanEditAllMessages: "CanEditAllMessages",
  CanWriteAnyMessage: "CanWriteAnyMessage",
  CanMessagePatients: "CanMessagePatients",
  CanMessageStaff: "CanMessageStaff",
  CanCreateChannel: "CanCreateChannel",
  CanManageChannel: "CanManageChannel"
};
const SchedulerFlags = {
  ViewScheduler: "ViewScheduler",
  CanManageSchedules: "CanManageSchedules"
};
const SurveyFlags = {
  Metrics: "Metrics",
  Publish: "Publish"
};
const KeyVaultProvider = {
  Rsa: "Rsa",
  Azure: "Azure",
  AwsKms: "AwsKms"
};
const EncryptionKeyPurpose = {
  Default: "Default",
  AttachmentPasswords: "AttachmentPasswords"
};
const ParameterPresence = {
  /** Parameter must be present (must have a value). */
  MustBePresent: "MustBePresent",
  /** Parameter may or may not be present (may not have a value). */
  MaybePresent: "MaybePresent",
  /** Parameter is not present (no value). */
  NotPresent: "NotPresent"
};
const AllergyClinicalStatus = {
  Active: "Active",
  Inactive: "Inactive",
  Resolved: "Resolved"
};
const ContentDistribution = {
  Audited: "Audited",
  Public: "Public"
};
const MediaType = {
  Photo: "Photo",
  Video: "Video",
  Audio: "Audio"
};
const AddressType = {
  /** A physical address that can be visited. */
  Physical: "Physical",
  /** Mailing address, PO Boxes and care-of addresses. */
  Postal: "Postal",
  /** An address that is both physical and postal. */
  PhysicalAndPostal: "PhysicalAndPostal"
};
const AddressUse = {
  Home: "Home",
  Work: "Work"
};
const UserLicense = {
  Mobile: "Mobile",
  Bells: "Bells"
};
const Distribution = {
  Practice: "Practice",
  Readonly: "Readonly",
  Internal: "Internal"
};
const RolePersona = {
  Administrator: "Administrator",
  Billing: "Billing",
  CaseManager: "CaseManager",
  Clerical: "Clerical",
  Clinician: "Clinician"
};
const Permission = {
  /** Permission granted */
  Allow: "Allow",
  /** Read permission */
  Read: "Read",
  /** Create permission */
  Create: "Create",
  /** Update permission */
  Update: "Update",
  /** Delete permission */
  Delete: "Delete"
};
const ContactMethod = {
  /** Supports voice calls. */
  Phone: "Phone",
  /** Supports emails. */
  Email: "Email",
  /** Supports sms text messages, such as Mobile or some landlines. */
  Sms: "Sms",
  /** DIRECT email. */
  Direct: "Direct",
  /** Supports Fax. */
  Fax: "Fax"
};
const ContactPointUse = {
  /**
   * A communication contact point at a home; attempted contacts for business purposes might intrude privacy and chances
   * are one will contact family
   * or other household members instead of the person one wishes to call. Typically used with urgent cases, or if no
   * other contacts are available.
   */
  Home: "Home",
  /** An office contact point. First choice for business related contacts during business hours. */
  Work: "Work",
  /** Contact point that stays with the owner.  Suitable for urgent matters. */
  Mobile: "Mobile",
  /** A temporary contact point. The period can provide more detailed information. */
  Temp: "Temp"
};
const BirthSex = {
  Female: "Female",
  Male: "Male",
  Unknown: "Unknown"
};
const AdministrativeGender = {
  Male: "Male",
  Female: "Female",
  Other: "Other",
  Unknown: "Unknown"
};
const PronounClass = {
  /** he/him/his */
  Masculine: "Masculine",
  /** she/her/hers */
  Feminine: "Feminine",
  /** they/them/theirs */
  Neutral: "Neutral"
};
const LocationMode = {
  /** The Location resource represents a specific instance of a location (e.g. Operating Theatre 1A). */
  Instance: "Instance",
  /**
   * The Location represents a class of locations (e.g. Any Operating Theatre) although this class of
   * locations could be constrained within a specific boundary (such as organization, or parent location, address etc.).
   */
  Kind: "Kind"
};
const LocationRole = {
  Pharmacy: "Pharmacy",
  Telehealth: "Telehealth",
  School: "School",
  HomelessShelter: "HomelessShelter",
  IndianHealthServiceFreestandingFacility: "IndianHealthServiceFreestandingFacility",
  IndianHealthServiceProviderbasedFacility: "IndianHealthServiceProviderbasedFacility",
  TribalFreestandingFacility: "TribalFreestandingFacility",
  TribalProviderbasedFacility: "TribalProviderbasedFacility",
  PrisonCorrectionalFacility: "PrisonCorrectionalFacility",
  Office: "Office",
  Home: "Home",
  AssistedLivingFacility: "AssistedLivingFacility",
  GroupHome: "GroupHome",
  MobileUnit: "MobileUnit",
  TemporaryLodging: "TemporaryLodging",
  WalkinRetailHealthClinic: "WalkinRetailHealthClinic",
  PlaceofEmploymentWorksite: "PlaceofEmploymentWorksite",
  OffCampusOutpatientHospital: "OffCampusOutpatientHospital",
  UrgentCareFacility: "UrgentCareFacility",
  InpatientHospital: "InpatientHospital",
  OnCampusOutpatientHospital: "OnCampusOutpatientHospital",
  EmergencyRoomHospital: "EmergencyRoomHospital",
  AmbulatorySurgicalCenter: "AmbulatorySurgicalCenter",
  BirthingCenter: "BirthingCenter",
  MilitaryTreatmentFacility: "MilitaryTreatmentFacility",
  SkilledNursingFacility: "SkilledNursingFacility",
  NursingFacility: "NursingFacility",
  CustodialCareFacility: "CustodialCareFacility",
  Hospice: "Hospice",
  AmbulanceLand: "AmbulanceLand",
  AmbulanceAirOrWater: "AmbulanceAirOrWater",
  IndependentClinic: "IndependentClinic",
  FederallyQualifiedHealthCenter: "FederallyQualifiedHealthCenter",
  InpatientPsychiatricFacility: "InpatientPsychiatricFacility",
  PsychiatricFacilityPartialHospitalization: "PsychiatricFacilityPartialHospitalization",
  CommunityMentalHealthCenter: "CommunityMentalHealthCenter",
  IntermediateCareFacilityMentallyRetarded: "IntermediateCareFacilityMentallyRetarded",
  ResidentialSubstanceAbuseTreatmentFacility: "ResidentialSubstanceAbuseTreatmentFacility",
  PsychiatricResidentialTreatmentCenter: "PsychiatricResidentialTreatmentCenter",
  NonresidentialSubstanceAbuseTreatmentFacility: "NonresidentialSubstanceAbuseTreatmentFacility",
  NonresidentialOpioidTreatmentFacility: "NonresidentialOpioidTreatmentFacility",
  MassImmunizationCenter: "MassImmunizationCenter",
  ComprehensiveInpatientRehabilitationFacility: "ComprehensiveInpatientRehabilitationFacility",
  ComprehensiveOutpatientRehabilitationFacility: "ComprehensiveOutpatientRehabilitationFacility",
  EndStageRenalDiseaseTreatmentFacility: "EndStageRenalDiseaseTreatmentFacility",
  PublicHealthClinic: "PublicHealthClinic",
  RuralHealthClinic: "RuralHealthClinic",
  IndependentLaboratory: "IndependentLaboratory",
  OtherPlaceOfService: "OtherPlaceOfService",
  TelehealthInPatientHome: "TelehealthInPatientHome"
};
const PatientPopulationLevel = {
  All: "All",
  CareTeam: "CareTeam",
  Program: "Program"
};
const NameUse = {
  /**
   * The formal name as registered in an official (government) registry, but which name might not be commonly used. May
   * be called "legal name".
   */
  Official: "Official",
  /**
   * A temporary name. Name.period can provide more detailed information. This may also be used for temporary names
   * assigned at birth or in emergency situations.
   */
  Temp: "Temp",
  /** A name that is used to address the person in an informal manner, but is not part of their formal or usual name. */
  Nickname: "Nickname",
  /** Anonymous assigned name, alias, or pseudonym (used to protect a person's identity for privacy reasons). */
  Anonymous: "Anonymous",
  /** This name is no longer in use (or was never correct, but retained for records). */
  Old: "Old",
  /**
   * A name used prior to changing name because of marriage. This name use is for use by applications that collect and
   * store names that were used prior to a marriage. Marriage naming customs vary greatly around the world, and are
   * constantly changing. This term is not gender specific. The use of this term does not imply any particular history
   * for a person's name.
   */
  Maiden: "Maiden",
  Preferred: "Preferred"
};
const OrganizationType = {
  StateAgency: "StateAgency",
  InsuranceCompany: "InsuranceCompany",
  Department: "Department",
  HealthcareProvider: "HealthcareProvider"
};
const ListPurpose = {
  StaffGroup: "StaffGroup",
  CareTeam: "CareTeam"
};
const SupervisoryRole = {
  Manager: "Manager",
  Clinical: "Clinical"
};
const ServiceCharacteristics = {
  Billable: "Billable",
  Show: "Show",
  SupportsGroups: "SupportsGroups",
  RequireUnits: "RequireUnits",
  MissedVisit: "MissedVisit"
};
const NoteSectionFormat = {
  Text: "Text",
  Diagnosis: "Diagnosis",
  Form: "Form",
  GoalsObjectives: "GoalsObjectives",
  SessionTime: "SessionTime",
  CtoneCustomDropdown: "CtoneCustomDropdown",
  ItineraryTopics: "ItineraryTopics",
  ClinicalQualityIndicator: "ClinicalQualityIndicator",
  AddOn: "AddOn",
  EvidenceBasedPractices: "EvidenceBasedPractices",
  GoalTracking: "GoalTracking",
  Problems: "Problems"
};
const QuestionnaireCalculation = {
  /** Element that displays an addition compute result from other elements. */
  Sum: "Sum",
  /** Element that displays an multiplication compute result from other elements. */
  Multiply: "Multiply",
  /** Find the amount between two values.  For example, duration between two dates. */
  Difference: "Difference",
  /** Element that displays an average compute result from other elements. */
  Average: "Average",
  /** Copy the output of other question. */
  Copy: "Copy"
};
const SerialConjunction = {
  And: "And",
  Or: "Or",
  None: "None"
};
const QuestionnaireValueToken = {
  Unknown: "Unknown",
  Today: "Today",
  Now: "Now",
  PrimaryPerformer: "PrimaryPerformer",
  Patient: "Patient",
  PatientAge: "PatientAge",
  PatientGender: "PatientGender",
  ServiceType: "ServiceType",
  Location: "Location",
  ServiceLocation: "ServiceLocation",
  Program: "Program",
  Group: "Group",
  GroupUnit: "GroupUnit",
  EpisodeOfCare: "EpisodeOfCare",
  ProgramEnrollment: "ProgramEnrollment",
  EncounterStart: "EncounterStart",
  EncounterEnd: "EncounterEnd",
  EncounterDuration: "EncounterDuration",
  ParentSubject: "ParentSubject"
};
const QuestionnaireElementType = {
  /** Element with a yes/no response. */
  Boolean: "Boolean",
  /** Element with an integer response. */
  Integer: "Integer",
  /** Element with a real number response. */
  Decimal: "Decimal",
  /** Element with a short free-text entry response. */
  String: "String",
  /** Element with a long free-text entry response. */
  Text: "Text",
  /** Element with a date response. */
  Date: "Date",
  /** Element with a date and time response. */
  DateTime: "DateTime",
  /** Element with a date and time response. */
  Time: "Time",
  /** Element with a list of possible choices. */
  Choice: "Choice",
  /** Element with a list of possible choices, with an added free-text entry option. */
  OpenChoice: "OpenChoice",
  /** Element with a ranking response */
  Ranking: "Ranking",
  /** Element with a ContactPoint type response (i.e. Phone number or Email). */
  ContactPoint: "ContactPoint",
  /** Element with an Approximate type response. */
  Approximate: "Approximate",
  /** Element with an Address type response. */
  Address: "Address",
  /** Element with a Patient Reference type response. */
  Patient: "Patient",
  /** Element with a Practitioner Reference type response. */
  Practitioner: "Practitioner",
  /** Element with a Insurance Company Reference type response. */
  InsuranceCompany: "InsuranceCompany",
  /** Element with a Location Reference type response. */
  Location: "Location",
  /** Element that performs a live lookup on the EHR. */
  EhrLookup: "EhrLookup",
  /** Element with a RelatedPerson Reference type response. */
  RelatedPerson: "RelatedPerson",
  /** Element with choices and free text response. */
  TextChoice: "TextChoice",
  /** Element that directly renders the rich text. */
  TextBlock: "TextBlock",
  /** Element with a duration response. */
  Duration: "Duration",
  /** Element for capturing a signature image. */
  Signature: "Signature",
  /** Element for Header. */
  Header: "Header",
  /** Element for a Grid. */
  Grid: "Grid",
  /** Element with a HealthcareService Reference type response. */
  HealthcareService: "HealthcareService",
  /** Element with a DocumentReference Reference type response. */
  DocumentReference: "DocumentReference",
  /** Element with a UsState Coding type response. */
  UsState: "UsState",
  /** Element for a report in table format. */
  TableReport: "TableReport",
  /** Element for a service event grid. */
  ServiceEvents: "ServiceEvents"
};
const GoalElementType = {
  Goal: "Goal",
  Objective: "Objective",
  Intervention: "Intervention"
};
const DiagnosisLimitType = {
  PrimaryHandling: "PrimaryHandling",
  IncludeAllDiagnoses: "IncludeAllDiagnoses",
  RestrictToOne: "RestrictToOne"
};
const RelativeOccassion = {
  CurrentDay: "CurrentDay",
  StartOfWeek: "StartOfWeek",
  EndOfWeek: "EndOfWeek",
  StartOfMonth: "StartOfMonth",
  EndOfMonth: "EndOfMonth",
  StartOfYear: "StartOfYear",
  EndOfYear: "EndOfYear"
};
const DateRangeBoundary = {
  WithinPeriod: "WithinPeriod",
  OutsidePeriod: "OutsidePeriod"
};
const QuestionnaireElementOrientation = {
  Horizontal: "Horizontal",
  Vertical: "Vertical"
};
const QuestionnaireElementPresentation = {
  /** Overrides `Decimal` elements to prefix $ symbol to element's reponse */
  USCurrency: "USCurrency",
  /** Overrides `Integer` elements to be presented using a slider */
  Slider: "Slider",
  /** Overrides `Choice` or `OpenChoice` elements to be presented using a dropdown instead of radio/checkboxes. */
  Dropdown: "Dropdown",
  /** Display stars as the shape in a `Rating` type element */
  Stars: "Stars",
  /** Display smileys as the shape in a `Rating` type element */
  Smiley: "Smiley",
  /** Display thumbs ups as the shape in a `Rating` type element */
  Thumbs: "Thumbs",
  /** Will present quill editor for responding to elements with QuestionnaireElementType.Text */
  FormattedText: "FormattedText",
  /**
   * Will present input which accepts an email address as response to elements with
   * QuestionnaireElementType.ContactPoint
   */
  Email: "Email",
  /**
   * Will present input which accepts an email address as response to elements with
   * QuestionnaireElementType.ContactPoint
   */
  Phone: "Phone",
  /** Will present input which accepts 0 to 10 rating as response to elements with QuestionnaireElementType.Integer */
  Rating: "Rating",
  /** Will present callout with intent of Primary and icon of "info-sign" */
  InformationalCallout: "InformationalCallout",
  /** Will present callout with intent of Warning and icon of "warning-sign" */
  WarningCallout: "WarningCallout",
  /** To render button for Choice type question */
  ToggleButton: "ToggleButton",
  ParagraphBreak: "ParagraphBreak",
  RelatedPersonSignature: "RelatedPersonSignature",
  AdhocPatientSignature: "AdhocPatientSignature",
  PractitionerSignature: "PractitionerSignature",
  LastFirstName: "LastFirstName",
  SocialSecurityNumber: "SocialSecurityNumber",
  PostalCode: "PostalCode",
  /** Overrides `Integer` elements to suffix % symbol to element's reponse */
  Percentage: "Percentage"
};
const QuestionnaireElementRole = {
  /** Element for a duration deduction from session time. */
  DurationDeduction: "DurationDeduction",
  ServiceType: "ServiceType",
  Location: "Location",
  ServiceLocation: "ServiceLocation",
  Program: "Program",
  EpisodeOfCare: "EpisodeOfCare",
  ProgramEnrollment: "ProgramEnrollment",
  EncounterStart: "EncounterStart",
  EncounterEnd: "EncounterEnd",
  EncounterDuration: "EncounterDuration",
  Goal: "Goal",
  Objective: "Objective",
  Intervention: "Intervention",
  Patient: "Patient",
  Group: "Group",
  GroupUnit: "GroupUnit",
  PrimaryPerformer: "PrimaryPerformer"
};
const NoteComposition = {
  Individual: "Individual",
  Group: "Group",
  MultiService: "MultiService"
};
const VisitStatus = {
  Show: "Show",
  NoShow: "NoShow",
  Cancelled: "Cancelled"
};
const NoteSectionFormMode = {
  Form: "Form",
  Narrative: "Narrative",
  Subform: "Subform",
  Import: "Import",
  Assessment: "Assessment"
};
const NoteSectionFormUsage = {
  NoteDefinition: "NoteDefinition",
  HealthcareService: "HealthcareService"
};
const SessionTimeFormat = {
  Duration: "Duration",
  DurationMinutes: "DurationMinutes",
  TimeIncrements: "TimeIncrements"
};
const DayOfWeek = {
  Monday: "Monday",
  Tuesday: "Tuesday",
  Wednesday: "Wednesday",
  Thursday: "Thursday",
  Friday: "Friday",
  Saturday: "Saturday",
  Sunday: "Sunday"
};
const EpisodeOfCareStatus = {
  Active: "Active",
  Completed: "Completed",
  Cancelled: "Cancelled"
};
const ConditionCategory = {
  ProblemList: "ProblemList",
  Encounter: "Encounter"
};
const ConditionStatus = {
  /** The subject is currently experiencing the condition or situation, there is evidence of the condition or situation, or considered to be a significant risk. */
  Active: "Active",
  /** The subject is no longer experiencing the condition or situation and there is no longer evidence or appreciable risk of the condition or situation. */
  Inactive: "Inactive"
};
const DiagnosisRole = {
  Admission: "Admission",
  Discharge: "Discharge",
  ChiefComplaint: "ChiefComplaint",
  AssociatedSymptom: "AssociatedSymptom",
  Billing: "Billing"
};
const RecurrenceType = {
  Daily: "Daily",
  Weekly: "Weekly",
  Monthly: "Monthly",
  Annually: "Annually",
  Explicit: "Explicit"
};
const ParticipantRole = {
  /** The principal or primary performer of the act. */
  PrimaryPerformer: "PrimaryPerformer",
  /**
   * A person assisting in an act through his substantial presence and involvement This includes: assistants,
   * technicians, associates, or whatever the job titles may be.
   */
  SecondaryPerformer: "SecondaryPerformer",
  /** Indicates that the target of the participation is involved in some manner in the act, but does not qualify how. */
  Participation: "Participation",
  /**
   * A Role of a LivingSubject as an actual or potential recipient of health care services from a healthcare provider
   * organization.
   */
  Patient: "Patient",
  Coordinator: "Coordinator"
};
const ParticipationStatus = {
  /** The participant has accepted the appointment. */
  Accepted: "Accepted",
  /** The participant has declined the appointment and will not participate in the appointment. */
  Declined: "Declined",
  /**
   * The participant has tentatively accepted the appointment.
   * This could be automatically created by a system and requires further processing before it can be accepted.
   * There is no commitment that attendance will occur.
   */
  Tentative: "Tentative",
  /**
   * The participant needs to indicate if they accept the appointment by changing this status to one of the other
   * statuses.
   */
  NeedsAction: "NeedsAction"
};
const DocumentationStatus = {
  /** The documentation has not started. */
  NotStarted: "NotStarted",
  /** The documentation is still being authored. */
  InProgress: "InProgress",
  /** The documentation has been complete by the authors. */
  Complete: "Complete"
};
const EncounterDocumentationType = {
  Note: "Note",
  DocumentationNote: "DocumentationNote",
  AssessmentNote: "AssessmentNote",
  ScribeSession: "ScribeSession",
  GroupNote: "GroupNote",
  MultiServiceNote: "MultiServiceNote"
};
const EncounterInteraction = {
  InPerson: "InPerson",
  Telehealth: "Telehealth"
};
const EncounterStatus = {
  Planned: "Planned",
  Arrived: "Arrived",
  Triaged: "Triaged",
  InProgress: "InProgress",
  OnLeave: "OnLeave",
  Finished: "Finished",
  Cancelled: "Cancelled",
  EnteredInError: "EnteredInError",
  Unknown: "Unknown",
  NoShow: "NoShow"
};
const RequestStatus = {
  /** The request has been created but is not yet complete or ready for action. */
  Draft: "Draft",
  /** The request is in force and ready to be acted upon. */
  Active: "Active",
  /** The activity described by the request has been fully performed. No further activity will occur. */
  Completed: "Completed",
  /**
   * The request has been terminated prior to the known full completion of the intended actions. No further activity
   * should occur.
   */
  Cancelled: "Cancelled"
};
const GoalAchievementStatus = {
  /** The goal has not been met and there might or might not have been progress towards target. */
  NotAchieved: "NotAchieved",
  /** The goal is being sought but has not yet been reached. */
  InProgress: "InProgress",
  /** The goal has been met. */
  Achieved: "Achieved",
  /** The goal is not possible to be met. */
  Abandoned: "Abandoned"
};
const EventStatus = {
  Preparation: "Preparation",
  InProgress: "InProgress",
  Succeeded: "Succeeded",
  Failed: "Failed"
};
const AllergySeverity = {
  LowRisk: "LowRisk",
  HighRisk: "HighRisk",
  UnableToAssess: "UnableToAssess"
};
const AllergyType = {
  Allergy: "Allergy",
  Intolerance: "Intolerance"
};
const AllergyVerificationStatus = {
  Unconfirmed: "Unconfirmed",
  Confirmed: "Confirmed",
  Refuted: "Refuted",
  EnteredInError: "EnteredInError"
};
const AllergySortField = {
  Recorded: "Recorded",
  Onset: "Onset"
};
const AnnouncementSortField = {
  Published: "Published"
};
const AppointmentSortField = {
  Start: "Start"
};
const AppointmentExpandables = {
  Attendees: "Attendees",
  Location: "Location",
  Owner: "Owner",
  Schedule: "Schedule",
  Program: "Program"
};
const CompositionStatus = {
  /**
   * This version of the composition is complete and verified by an appropriate person and no further work is planned.
   * Any subsequent updates would be on a new version of the composition.
   */
  Final: "Final",
  /**
   * This is a preliminary composition or document (also known as draft, initial, or interim). The content may be
   * incomplete or unverified.
   */
  Preliminary: "Preliminary",
  /**
   * The composition content or the referenced resources have been modified (edited or added to) subsequent to being
   * released as "final" and the composition is complete and verified by an authorized person.
   */
  Amended: "Amended",
  /**
   * The composition or document was originally created/issued in error, and this is an amendment that marks that the
   * entire series should not be considered as valid.
   */
  EnteredInError: "EnteredInError"
};
const CarePlanOutcomeSortField = {
  Recorded: "Recorded",
  Period: "Period"
};
const CarePlanOutcomeExpandables = {
  PartOf: "PartOf",
  Patient: "Patient",
  EpisodeOfCare: "EpisodeOfCare"
};
const CarePlanSessionSortField = {
  Period: "Period"
};
const CdaParsingTaskExpandables = {
  Document: "Document"
};
const ChannelStatus = {
  Active: "Active",
  Archived: "Archived",
  Concluded: "Concluded"
};
const ChatContentType = {
  AutomatedMessage: "AutomatedMessage",
  SlashCommand: "SlashCommand"
};
const ChannelType = {
  /** A private conversation between a small group of people. */
  Direct: "Direct",
  /** A channel that requires an invitation to join. */
  InviteOnly: "InviteOnly",
  /** A channel that is public to everyone. */
  Open: "Open",
  /** A channel that represents a thread. */
  Thread: "Thread"
};
const ChannelParticipation = {
  /** The user is a member and it is displayed for them. */
  Active: "Active",
  /** The user is still a member of the channel but does not have it displayed. */
  Inactive: "Inactive",
  /** The user has been kicked and cannot join unless reinvited. */
  Kicked: "Kicked"
};
const ChannelMembershipExpandables = {
  Member: "Member",
  Channel: "Channel"
};
const ChatMessageSortField = {
  Sent: "Sent"
};
const ChatMessageExpandables = {
  Channel: "Channel",
  Author: "Author",
  Documents: "Documents",
  Media: "Media",
  Thread: "Thread"
};
const ClinicalDocumentSortField = {
  EncounterStart: "EncounterStart"
};
const ClinicalDocumentExpandables = {
  Document: "Document",
  Subject: "Subject"
};
const CommunicationSortField = {
  Sent: "Sent"
};
const PublicationStatus = {
  Draft: "Draft",
  Active: "Active",
  Retired: "Retired"
};
const NoteDefinitionType = {
  Note: "Note",
  Assessment: "Assessment"
};
const NoteSyncIssue = {
  AppointmentAlreadyLinked: "AppointmentAlreadyLinked",
  BusinessException: "BusinessException",
  AppointmentNotEditable: "AppointmentNotEditable",
  NotFinalized: "NotFinalized"
};
const ComplianceQuestionAnswerOption = {
  Satisfied: "Satisfied",
  NotSatisfied: "NotSatisfied",
  NotApplicable: "NotApplicable"
};
const ComplianceAuditSortField = {
  Created: "Created",
  Patient: "Patient",
  EncounterDate: "EncounterDate"
};
const ComplianceAuditExpandables = {
  Participants: "Participants",
  Patient: "Patient",
  Note: "Note",
  Questionnaire: "Questionnaire"
};
const ComplianceQuestionnaireSortField = {
  Created: "Created",
  Name: "Name"
};
const ComplianceQuestionnaireExpandables = {
  Services: "Services",
  Programs: "Programs",
  Definitions: "Definitions",
  Locations: "Locations"
};
const ConditionSortField = {
  OnsetDate: "OnsetDate",
  Name: "Name"
};
const ConditionExpandables = {
  Patient: "Patient"
};
const ConsentSortField = {
  Created: "Created"
};
const CoverageSortField = {
  Order: "Order"
};
const DialogueSortField = {
  Start: "Start",
  Subject: "Subject"
};
const DialogueExpandables = {
  Subject: "Subject",
  Practitioner: "Practitioner",
  Audio: "Audio",
  Transcription: "Transcription"
};
const DocumentTransmissionExpandables = {
  Document: "Document"
};
const EncounterSortField = {
  EncounterDate: "EncounterDate",
  Subject: "Subject"
};
const EpisodeOfCareExpandables = {
  Patient: "Patient",
  Condition: "Condition",
  Program: "Program"
};
const EncounterExpandables = {
  Participant: "Participant",
  ServiceType: "ServiceType",
  Location: "Location",
  LocationKind: "LocationKind",
  Appointment: "Appointment",
  EpisodeOfCare: "EpisodeOfCare",
  Subject: "Subject",
  PartOf: "PartOf",
  CarePlans: "CarePlans",
  Dialogues: "Dialogues",
  Program: "Program"
};
const PractitionerExcludables = {
  Supervisors: "Supervisors"
};
const PractitionerExpandables = {
  Supervisors: "Supervisors",
  Photo: "Photo",
  Avatar: "Avatar",
  Signature: "Signature",
  GroupMemberships: "GroupMemberships",
  EmploymentDepartment: "EmploymentDepartment"
};
const EpisodeOfCareSortField = {
  Period: "Period"
};
const FormType = {
  /** The form will have one or more questionnaires */
  Questionnaires: "Questionnaires",
  /** The daily routines form for early intervention */
  DailyRoutines: "DailyRoutines"
};
const FormSortField = {
  Name: "Name",
  Effective: "Effective"
};
const FormExpandables = {
  Questionnaires: "Questionnaires"
};
const GroupType = {
  Practitioner: "Practitioner",
  Patient: "Patient"
};
const GroupSortField = {
  Display: "Display",
  Alias: "Alias"
};
const GroupExpandables = {
  Members: "Members"
};
const GroupNoteDefinitionSortField = {
  Display: "Display"
};
const GroupNoteDefinitionExpandables = {
  Show: "Show",
  NoShow: "NoShow",
  Cancelled: "Cancelled",
  ShowTemplate: "ShowTemplate",
  NoShowTemplate: "NoShowTemplate",
  CancelledTemplate: "CancelledTemplate"
};
const HealthcareServiceSortField = {
  TypeText: "TypeText",
  Display: "Display",
  Alias: "Alias"
};
const HealthcareServiceExpandables = {
  Forms: "Forms"
};
const LocationSortField = {
  Name: "Name",
  Alias: "Alias"
};
const MedicationStatus = {
  Active: "Active",
  Inactive: "Inactive",
  EnteredInError: "EnteredInError"
};
const NoteSortField = {
  Definition: "Definition",
  Recorded: "Recorded",
  Created: "Created",
  LastModified: "LastModified",
  Status: "Status",
  Subject: "Subject",
  Signed: "Signed"
};
const NoteExcludables = {
  Sections: "Sections"
};
const NoteExpandables = {
  Encounter: "Encounter",
  Participants: "Participants",
  Definition: "Definition",
  Subject: "Subject",
  PartOf: "PartOf",
  DerivedFrom: "DerivedFrom",
  SignatureConfiguration: "SignatureConfiguration",
  Forms: "Forms"
};
const AuditVerdict = {
  /** Reviewer has confirmed the results of the audit. */
  Confirmed: "Confirmed",
  /** Reviewer has refuted some or all of the results of the audit. */
  Refuted: "Refuted"
};
const NoteAuditSortField = {
  Created: "Created",
  Patient: "Patient",
  EncounterDate: "EncounterDate"
};
const NoteAuditExpandables = {
  Participants: "Participants",
  Patient: "Patient",
  SimilarNotes: "SimilarNotes",
  OverlappingNotes: "OverlappingNotes",
  ComplianceAuditReviews: "ComplianceAuditReviews"
};
const NoteDefinitionSortField = {
  Name: "Name",
  Status: "Status",
  MobileEnabled: "MobileEnabled"
};
const NoteDefinitionExpandables = {
  Services: "Services",
  Forms: "Forms"
};
const NoteRuleCriteriaType = {
  SessionDuration: "SessionDuration",
  ShowStatus: "ShowStatus",
  ServiceDate: "ServiceDate",
  ProgramSelected: "ProgramSelected",
  ProgramLocation: "ProgramLocation",
  LocationSelected: "LocationSelected",
  MissingDiagnosis: "MissingDiagnosis",
  SpanMidnight: "SpanMidnight",
  AppointmentRequired: "AppointmentRequired",
  SessionOverlap: "SessionOverlap",
  Authorization: "Authorization",
  SessionDurationIncrement: "SessionDurationIncrement",
  Insurance: "Insurance",
  ServiceBasedSubformDuration: "ServiceBasedSubformDuration"
};
const NoteRuleOutcomeType = {
  /** Prevent note creation or note signing. */
  Blocker: "Blocker",
  /** Gives user the choice to change their selected service to resolve the rule. */
  ChangeService: "ChangeService",
  /** Gives user the choice to change their selected location to resolve the rule. */
  ChangeLocation: "ChangeLocation",
  /** Warn before note creation or note signing. */
  Warn: "Warn"
};
const NoteSectionFormSortField = {
  Name: "Name"
};
const NoteSectionFormExpandables = {
  Form: "Form"
};
const NoteTemplateSortField = {
  Name: "Name",
  Status: "Status"
};
const NoteTemplateExpandables = {
  Author: "Author",
  Definition: "Definition",
  Services: "Services"
};
const NotificationImportance = {
  Low: "Low",
  Normal: "Normal",
  High: "High"
};
const NotificationTopic = {
  UnreadChatMessages: "UnreadChatMessages",
  SharedScribble: "SharedScribble",
  Snippet: "Snippet",
  NoteSyncIssue: "NoteSyncIssue"
};
const NotificationSortField = {
  Created: "Created",
  Expiration: "Expiration"
};
const ObservationInterpretation = {
  High: "High",
  Normal: "Normal",
  Low: "Low",
  Abnormal: "Abnormal"
};
const ObservationStatus = {
  /** The existence of the observation is registered, but there is no result yet available. */
  Registered: "Registered",
  /** This is an initial or interim observation: data may be incomplete or unverified. */
  Preliminary: "Preliminary",
  /**
   * The observation is complete and there are no further actions needed. Additional information such
   * "released", "signed", etc would be represented using [Provenance](provenance.html) which provides
   * not only the act but also the actors and dates and other related data. These act states would be
   * associated with an observation status of `preliminary` until they are all completed and then a
   * status of `final` would be applied.
   */
  Final: "Final",
  /**
   * Subsequent to being Final, the observation has been modified subsequent. This includes updates/newinformation and
   * corrections.
   */
  Amended: "Amended",
  /**
   * The observation is unavailable because the measurement was not started or not completed (also sometimes called
   * "aborted").
   */
  Cancelled: "Cancelled"
};
const OrganizationSortField = {
  Name: "Name"
};
const PacketPurpose = {
  /** A packet used to open an enrollment */
  Open: "Open",
  /** A packet that amends or adds to an existing enrollment */
  Amend: "Amend",
  /** A packet that will be used to close an enrollment */
  Close: "Close"
};
const PacketSortField = {
  Name: "Name",
  Purpose: "Purpose",
  Effective: "Effective"
};
const PacketExpandables = {
  Forms: "Forms",
  Programs: "Programs"
};
const PacketResponseStatus = {
  /** The response is not complete and in progress */
  Draft: "Draft",
  /** The packet and all the forms are complete */
  Complete: "Complete",
  /** The packet was created in error */
  EnteredInError: "EnteredInError"
};
const QuestionnaireResponseStatus = {
  NotStarted: "NotStarted",
  InProgress: "InProgress",
  Completed: "Completed"
};
const PacketResponseSortField = {
  PacketName: "PacketName",
  Created: "Created"
};
const PacketResponseExpandables = {
  Packets: "Packets",
  Answers: "Answers",
  Authors: "Authors"
};
const PatientSortField = {
  Name: "Name",
  BirthDate: "BirthDate",
  FamilyName: "FamilyName",
  Gender: "Gender",
  GenderIdentity: "GenderIdentity",
  Mrn: "Mrn"
};
const PatientExpandables = {
  CareTeam: "CareTeam"
};
const BellsWebFontAppearance = {
  Normal: "Normal",
  Large: "Large"
};
const BellsWebTheme = {
  Light: "Light",
  Dark: "Dark"
};
const ChatAvailableStatus = {
  Available: "Available",
  Away: "Away",
  Busy: "Busy"
};
const AgendaFidelity = {
  QuarterHour: "QuarterHour",
  HalfHour: "HalfHour",
  Hour: "Hour"
};
const WeekView = {
  CalendarWeek: "CalendarWeek",
  WorkWeek: "WorkWeek"
};
const PersonalPreferencesExpandables = {
  Pins: "Pins"
};
const PersonalSignatureExpandables = {
  Signature: "Signature"
};
const GroupAttendanceStatus = {
  Show: "Show",
  NoShow: "NoShow",
  Cancelled: "Cancelled",
  NotPresent: "NotPresent"
};
const PractitionerSortField = {
  Name: "Name",
  LastModified: "LastModified",
  /** @deprecated Use Email */
  WorkEmail: "WorkEmail",
  Email: "Email"
};
const ProgramSortField = {
  Name: "Name"
};
const QuestionnaireSortField = {
  Created: "Created"
};
const RelatedPersonSortField = {
  Name: "Name",
  ProxyConsentDate: "ProxyConsentDate",
  ProxyConsentExpiration: "ProxyConsentExpiration"
};
const RelatedPeopleExpandables = {
  Patient: "Patient"
};
const ReportCriteriaSortField = {
  Name: "Name",
  LastModified: "LastModified"
};
const ReportDeliverySortField = {
  Author: "Author",
  Report: "Report",
  Display: "Display"
};
const ReportDeliveryExpandables = {
  Author: "Author",
  Recipients: "Recipients"
};
const ScribbleSortField = {
  Created: "Created",
  LastModified: "LastModified",
  LastUpdatedByUser: "LastUpdatedByUser"
};
const ScribbleExpandables = {
  Patient: "Patient",
  Author: "Author",
  Media: "Media"
};
const ScriptSortField = {
  Name: "Name"
};
const SecurityRoleSortField = {
  Name: "Name"
};
const SecurityRoleExpandables = {
  Grants: "Grants"
};
const SharedScribbleSortField = {
  Created: "Created",
  LastModified: "LastModified",
  LastUpdatedByUser: "LastUpdatedByUser"
};
const SharedScribbleExpandables = {
  Patient: "Patient",
  Media: "Media",
  Collaborators: "Collaborators"
};
const SnippetSortField = {
  Name: "Name"
};
const SnippetExpandables = {
  Shared: "Shared"
};
const SpeechRuleCategory = {
  AnyService: "AnyService",
  AttendantCare: "AttendantCare",
  CaseManagement: "CaseManagement",
  CPST: "CPST",
  OutpatientTherapy: "OutpatientTherapy",
  PeerSupport: "PeerSupport",
  SubstanceUse: "SubstanceUse",
  TargetedCaseManagement: "TargetedCaseManagement"
};
const SnippetTemplateSortField = {
  Category: "Category",
  SortDisplay: "SortDisplay",
  Name: "Name"
};
const SpeechRuleType = {
  /** Verbiage that reads like an assessment. */
  PatientAssessment: "PatientAssessment",
  /** Usage of a verb with the actor/agent of the verb being a staff noun. */
  StaffVerb: "StaffVerb",
  /** Usage of a verb in combination with a specific object and the actor/agent of the verb being a staff noun. */
  StaffVerbObject: "StaffVerbObject",
  /**
   * Usage of a verb where the object is a patient noun or the patient's name and the actor/agent of the verb being a
   * staff noun.
   */
  StaffVerbPatient: "StaffVerbPatient",
  /** Repeated usage of a verb with the actor/agent of the verb being a staff noun. */
  StaffVerbRepeated: "StaffVerbRepeated",
  /** Usage of a patient noun (patient, client, consumer, etc). */
  PatientNoun: "PatientNoun",
  /** Usage of a noun. */
  Noun: "Noun",
  /** Usage of a noun without an adjective. */
  LonelyNoun: "LonelyNoun",
  /** Non-third person verbiage. */
  NonThirdPerson: "NonThirdPerson",
  /** A sentence that begins with a past tense verb, thus is missing the actor of the verb. */
  MissingActor: "MissingActor",
  /** Usage of an acronym. */
  Acronym: "Acronym",
  /** Usage of a literal text. */
  Literal: "Literal"
};
const Tense = {
  /**
   * Future tense denotes actions that will happen after a reference point.
   * e.g. "I WILL take the patient to the clinic."
   */
  Future: "Future",
  /**
   * Past tense denotes actions that happened before a reference point.
   * e.g. "I TOOK the patient to the clinic."
   */
  Past: "Past",
  /**
   * Present tense denotes actions that are in progress in a reference point.
   * e.g. "I am TAKING the patient to the clinic."
   */
  Present: "Present"
};
const SpeechRuleTemplateSortField = {
  Category: "Category",
  SortDisplay: "SortDisplay"
};
const TextExpansionSortField = {
  Name: "Name"
};
const TextExpansionTemplateSortField = {
  Category: "Category",
  SortDisplay: "SortDisplay",
  Name: "Name"
};
const PermissionFlags = {
  AdministrativeFlags,
  ClinicalFlags,
  InternalFlags,
  MessagingFlags,
  SchedulerFlags,
  SurveyFlags
};
const knownCodings = {
  "restfulOperation": {
    "code": "rest",
    "display": "Restful Operation",
    "system": "http://terminology.hl7.org/CodeSystem/audit-event-type"
  },
  "ids": {
    "bellsSupportPractitionerId": "01G36NW0HTAN0DV2WJ8HH4PXQK",
    "serviceRoleId": "01D8S75V6HCC9VJVBAZKW6845P",
    "supportRoleId": "01GTFCGQEF79XKXPX12Z1648NC",
    "bellsRoleId": "01EGTX131TJEQME0VRQRX0H1DJ",
    "bellsUnityRoleId": "01J0KVVY453EKTH3AQN1M4HKFX",
    "administrativeRoleId": "01D6XVBQJKNJW0MQM1FJFY3DAD",
    "supervisorRoleId": "01GQ06HMARA0J2PBW9FXT420RX",
    "scribeRoleId": "01HW84ZWXZ6JNT25WV6S7V18ZM",
    "qualityAssuranceRoleId": "01JRK66C591J5WSW790JZ9MXKN"
  },
  "storage": {
    "syncImportContainerName": "sync-import",
    "cleanupBackupContainerName": "cleanup-backup"
  },
  "securityTags": {
    "user": "apollo:user"
  },
  "subStatusCodes": {
    "expiredSecurityToken": 1002,
    "invalidSecurityToken": 1001
  },
  "identifierTypes": {
    "conm": {
      "code": "CONM",
      "display": "Change of Name Document",
      "system": "http://terminology.hl7.org/ValueSet/v2-0203"
    },
    "dea": {
      "code": "DEA",
      "display": "Drug Enforcement Administration registration number",
      "system": "http://terminology.hl7.org/ValueSet/v2-0203"
    },
    "lacsn": {
      "code": "LACSN",
      "display": "Laboratory Accession ID",
      "system": "http://terminology.hl7.org/ValueSet/v2-0203"
    },
    "ma": {
      "code": "MA",
      "display": "Patient Medicaid number",
      "system": "http://terminology.hl7.org/ValueSet/v2-0203"
    },
    "mc": {
      "code": "MC",
      "display": "Patient Medicare number",
      "system": "http://terminology.hl7.org/ValueSet/v2-0203"
    },
    "mr": {
      "code": "MR",
      "display": "Medical record number",
      "system": "http://terminology.hl7.org/ValueSet/v2-0203"
    },
    "nadean": {
      "code": "NADEAN",
      "display": "Narcotic Addiction DEA Number",
      "system": "http://terminology.hl7.org/ValueSet/v2-0203"
    },
    "npi": {
      "code": "NPI",
      "display": "Drug Enforcement Administration registration number",
      "system": "http://terminology.hl7.org/ValueSet/v2-0203"
    },
    "pt": {
      "code": "PT",
      "display": "Patient external identifier",
      "system": "http://terminology.hl7.org/ValueSet/v2-0203"
    },
    "xx": {
      "code": "XX",
      "display": "Organization identifier",
      "system": "http://terminology.hl7.org/ValueSet/v2-0203"
    }
  },
  "licenseTypes": {
    "csac": {
      "code": "CSAC",
      "display": "Certified Substance Abuse Counselor",
      "system": "http://terminology.hl7.org/ValueSet/v2-0203"
    },
    "csw": {
      "code": "CSW",
      "display": "Certified Social Worker -or- Clinical Social Worker",
      "system": "http://terminology.hl7.org/ValueSet/v2-0203"
    },
    "cswc": {
      "code": "CSW-C",
      "display": "Certified SocialWorker - Clinical",
      "system": "http://terminology.hl7.org/ValueSet/v2-0203"
    },
    "lac": {
      "code": "LAC",
      "display": "Licensed Associate Counselor",
      "system": "http://terminology.hl7.org/ValueSet/v2-0203"
    },
    "ladac": {
      "code": "LADAC",
      "display": "Licensed Alcohol and Drug Abuse Counselor",
      "system": "http://terminology.hl7.org/ValueSet/v2-0203"
    },
    "ladc": {
      "code": "LADC",
      "display": "Licensed Alcohol and Drug Counselor",
      "system": "http://terminology.hl7.org/ValueSet/v2-0203"
    },
    "lamft": {
      "code": "LAMFT",
      "display": "Licensed Associate Marriage and Family Therapist",
      "system": "http://terminology.hl7.org/ValueSet/v2-0203"
    },
    "lapc": {
      "code": "LAPC",
      "display": "Licensed Associate Professional Counselor",
      "system": "http://terminology.hl7.org/ValueSet/v2-0203"
    },
    "lasac": {
      "code": "LASAC",
      "display": "Licensed Associate Substance Abuse Counselor",
      "system": "http://terminology.hl7.org/ValueSet/v2-0203"
    },
    "lcadc": {
      "code": "LCADC",
      "display": "Licensed Clinical Alcohol and Drug Counselor",
      "system": "http://terminology.hl7.org/ValueSet/v2-0203"
    },
    "lcas": {
      "code": "LCAS",
      "display": "Licensed Clinical Addictions Specialist",
      "system": "http://terminology.hl7.org/ValueSet/v2-0203"
    },
    "lcat": {
      "code": "LCAT",
      "display": "Licensed Creative Arts Therapist",
      "system": "http://terminology.hl7.org/ValueSet/v2-0203"
    },
    "lcdc": {
      "code": "LCDC",
      "display": "Licensed Chemical Dependency Counselor",
      "system": "http://terminology.hl7.org/ValueSet/v2-0203"
    },
    "lcdp": {
      "code": "LCDP",
      "display": "Licensed Chemical Dependency Professional",
      "system": "http://terminology.hl7.org/ValueSet/v2-0203"
    },
    "lcmft": {
      "code": "LCMFT",
      "display": "Licensed Clinical Marriage and Family Therapist",
      "system": "http://terminology.hl7.org/ValueSet/v2-0203"
    },
    "lcmhc": {
      "code": "LCMHC",
      "display": "Licensed Clinical Mental Health Counselor",
      "system": "http://terminology.hl7.org/ValueSet/v2-0203"
    },
    "lcp": {
      "code": "LCP",
      "display": "Licensed Clinical Psychologist -or- Licensed Counseling Professional",
      "system": "http://terminology.hl7.org/ValueSet/v2-0203"
    },
    "lcpc": {
      "code": "LCPC",
      "display": "Licensed Clinical Professional Counselor",
      "system": "http://terminology.hl7.org/ValueSet/v2-0203"
    },
    "lcsw": {
      "code": "LCSW",
      "display": "Licensed Clinical Social Worker",
      "system": "http://terminology.hl7.org/ValueSet/v2-0203"
    },
    "lcswc": {
      "code": "LCSW-C",
      "display": "Licensed Certified Social Worker-Clinical",
      "system": "http://terminology.hl7.org/ValueSet/v2-0203"
    },
    "lgsw": {
      "code": "LGSW",
      "display": "Licensed Graduate Social Worker",
      "system": "http://terminology.hl7.org/ValueSet/v2-0203"
    },
    "licsw": {
      "code": "LICSW",
      "display": "Licensed Independent Clinical Social Worker",
      "system": "http://terminology.hl7.org/ValueSet/v2-0203"
    },
    "limft": {
      "code": "LIMFT",
      "display": "Licensed Independent Marriage and Family Therapist",
      "system": "http://terminology.hl7.org/ValueSet/v2-0203"
    },
    "lisw": {
      "code": "LISW",
      "display": "Licensed Independent Social Worker",
      "system": "http://terminology.hl7.org/ValueSet/v2-0203"
    },
    "liswcp": {
      "code": "LISW-CP",
      "display": "Licensed Independent SocialWorker - ClinicalPractice",
      "system": "http://terminology.hl7.org/ValueSet/v2-0203"
    },
    "llp": {
      "code": "LLP",
      "display": "Limited Licensed Psychologist",
      "system": "http://terminology.hl7.org/ValueSet/v2-0203"
    },
    "lmft": {
      "code": "LMFT",
      "display": "Licensed Marriage and Family Therapist",
      "system": "http://terminology.hl7.org/ValueSet/v2-0203"
    },
    "lmhc": {
      "code": "LMHC",
      "display": "Licensed Mental Health Counselor",
      "system": "http://terminology.hl7.org/ValueSet/v2-0203"
    },
    "lmhp": {
      "code": "LMHP",
      "display": "Licensed Mental Health Practitioner",
      "system": "http://terminology.hl7.org/ValueSet/v2-0203"
    },
    "lmsw": {
      "code": "LMSW",
      "display": "Licensed Master Social Worker",
      "system": "http://terminology.hl7.org/ValueSet/v2-0203"
    },
    "lmswacp": {
      "code": "LMSW-ACP",
      "display": "Licensed Master SocialWorker - AdvancedClinical Practitioner",
      "system": "http://terminology.hl7.org/ValueSet/v2-0203"
    },
    "lpc": {
      "code": "LPC",
      "display": "Licensed Professional Counselor",
      "system": "http://terminology.hl7.org/ValueSet/v2-0203"
    },
    "lpcc": {
      "code": "LPCC",
      "display": "Licensed Professional Clinical Counselor",
      "system": "http://terminology.hl7.org/ValueSet/v2-0203"
    },
    "lpcmh": {
      "code": "LPCMH",
      "display": "Licensed Professional Counselor of Mental Health",
      "system": "http://terminology.hl7.org/ValueSet/v2-0203"
    },
    "lscsw": {
      "code": "LSCSW",
      "display": "Licensed Specialist Clinical Social Worker",
      "system": "http://terminology.hl7.org/ValueSet/v2-0203"
    },
    "lsw": {
      "code": "LSW",
      "display": "Licensed Social Worker",
      "system": "http://terminology.hl7.org/ValueSet/v2-0203"
    },
    "mft": {
      "code": "MFT",
      "display": "Marriage and Family Therapist",
      "system": "http://terminology.hl7.org/ValueSet/v2-0203"
    },
    "other": {
      "code": "other",
      "display": "Other",
      "system": "apollo:special-types"
    }
  },
  "contactRelationshipTypes": {
    "emergencyContact": {
      "code": "C",
      "display": "Emergency Contact",
      "system": "http://terminology.hl7.org/CodeSystem/v2-0131"
    },
    "employer": {
      "code": "E",
      "display": "Employer",
      "system": "http://terminology.hl7.org/CodeSystem/v2-0131"
    },
    "insuranceCompany": {
      "code": "ins",
      "display": "Insurance Company",
      "system": "http://terminology.hl7.org/CodeSystem/v2-0131"
    }
  },
  "communicationTopics": {
    "unsignedNoteReminder": {
      "code": "unsigned-note-reminder",
      "display": "Unsigned Note Reminder",
      "system": "apollo:communication-topic"
    },
    "reports": {
      "code": "report",
      "display": "Reports",
      "system": "apollo:communication-topic"
    },
    "staffInvite": {
      "code": "staff-invite",
      "display": "Staff Invite",
      "system": "apollo:communication-topic"
    },
    "survey": {
      "code": "survey",
      "display": "Survey",
      "system": "apollo:communication-topic"
    },
    "userReport": {
      "code": "user-report",
      "display": "User Report",
      "system": "apollo:communication-topic"
    }
  },
  "raceTypes": {
    "americanIndianOrAlaskaNative": {
      "code": "1002-5",
      "display": "American Indian or Alaska Native",
      "system": "urn:oid:2.16.840.1.113883.6.238"
    },
    "asian": {
      "code": "2028-9",
      "display": "Asian",
      "system": "urn:oid:2.16.840.1.113883.6.238"
    },
    "asku": {
      "code": "ASKU",
      "display": "Asked but no answer",
      "system": "http://terminology.hl7.org/CodeSystem/v3-NullFlavor"
    },
    "blackOrAfricanAmerican": {
      "code": "2054-5",
      "display": "Black or African American",
      "system": "urn:oid:2.16.840.1.113883.6.238"
    },
    "nativeHawaiian": {
      "code": "2076-8",
      "display": "Native Hawaiian or Other Pacific Islander",
      "system": "urn:oid:2.16.840.1.113883.6.238"
    },
    "white": {
      "code": "2106-3",
      "display": "White",
      "system": "urn:oid:2.16.840.1.113883.6.238"
    }
  },
  "genderIdentity": {
    "female": {
      "code": "female",
      "display": "female",
      "system": "http://hl7.org/fhir/gender-identity"
    },
    "male": {
      "code": "male",
      "display": "male",
      "system": "http://hl7.org/fhir/gender-identity"
    },
    "nonBinary": {
      "code": "non-binary",
      "display": "non-binary",
      "system": "http://hl7.org/fhir/gender-identity"
    },
    "nonDisclose": {
      "code": "non-disclose",
      "display": "does not wish to disclose",
      "system": "http://hl7.org/fhir/gender-identity"
    },
    "other": {
      "code": "other",
      "display": "other",
      "system": "http://hl7.org/fhir/gender-identity"
    },
    "transgenderFemale": {
      "code": "transgender-female",
      "display": "transgender female",
      "system": "http://hl7.org/fhir/gender-identity"
    },
    "transgenderMale": {
      "code": "transgender-male",
      "display": "transgender male",
      "system": "http://hl7.org/fhir/gender-identity"
    }
  },
  "ethinicityTypes": {
    "hispanicOrLatino": {
      "code": "2135-2",
      "display": "Hispanic or Latino",
      "system": "urn:oid:2.16.840.1.113883.6.238"
    },
    "nonHispanicOrLatino": {
      "code": "2186-5",
      "display": "Non Hispanic or Latino",
      "system": "urn:oid:2.16.840.1.113883.6.238"
    }
  },
  "appointmentStatuses": {
    "proposed": {
      "code": "proposed",
      "display": "Proposed",
      "system": "http://hl7.org/fhir/ValueSet/appointmentstatus"
    },
    "pending": {
      "code": "pending",
      "display": "Pending",
      "system": "http://hl7.org/fhir/ValueSet/appointmentstatus"
    },
    "booked": {
      "code": "booked",
      "display": "Booked",
      "system": "http://hl7.org/fhir/ValueSet/appointmentstatus"
    },
    "arrived": {
      "code": "arrived",
      "display": "Arrived",
      "system": "http://hl7.org/fhir/ValueSet/appointmentstatus"
    },
    "fulfilled": {
      "code": "fulfilled",
      "display": "Fulfilled",
      "system": "http://hl7.org/fhir/ValueSet/appointmentstatus"
    },
    "cancelled": {
      "code": "cancelled",
      "display": "Cancelled",
      "system": "http://hl7.org/fhir/ValueSet/appointmentstatus"
    },
    "noShow": {
      "code": "noshow",
      "display": "No Show",
      "system": "http://hl7.org/fhir/ValueSet/appointmentstatus"
    },
    "enteredInError": {
      "code": "entered-in-error",
      "display": "Entered in error",
      "system": "http://hl7.org/fhir/ValueSet/appointmentstatus"
    },
    "checkedIn": {
      "code": "checked-in",
      "display": "Checked In",
      "system": "http://hl7.org/fhir/ValueSet/appointmentstatus"
    },
    "waitlist": {
      "code": "waitlist",
      "display": "Waitlisted",
      "system": "http://hl7.org/fhir/ValueSet/appointmentstatus"
    }
  },
  "subscriberRelationship": {
    "child": {
      "code": "child",
      "display": "Child",
      "system": "http://hl7.org/fhir/ValueSet/subscriber-relationship"
    },
    "common": {
      "code": "common",
      "display": "Common Law Spouse",
      "system": "http://hl7.org/fhir/ValueSet/subscriber-relationship"
    },
    "grandchild": {
      "code": "13",
      "display": "Grandchild",
      "system": "ctone:relationship"
    },
    "grandparent": {
      "code": "19",
      "display": "Grandparent",
      "system": "ctone:relationship"
    },
    "handicappedDependent": {
      "code": "10",
      "display": "Handicapped Dependent",
      "system": "ctone:relationship"
    },
    "injured": {
      "code": "injured",
      "display": "Injured Party",
      "system": "http://hl7.org/fhir/ValueSet/subscriber-relationship"
    },
    "nieceNephew": {
      "code": "14",
      "display": "Niece/Nephew",
      "system": "ctone:relationship"
    },
    "other": {
      "code": "other",
      "display": "Other",
      "system": "http://hl7.org/fhir/ValueSet/subscriber-relationship"
    },
    "parent": {
      "code": "parent",
      "display": "Parent",
      "system": "http://hl7.org/fhir/ValueSet/subscriber-relationship"
    },
    "self": {
      "code": "self",
      "display": "Self",
      "system": "http://hl7.org/fhir/ValueSet/subscriber-relationship"
    },
    "sponsoredDependent": {
      "code": "16",
      "display": "Sponsored Dependent",
      "system": "ctone:relationship"
    },
    "spouse": {
      "code": "spouse",
      "display": "Spouse",
      "system": "http://hl7.org/fhir/ValueSet/subscriber-relationship"
    },
    "unknown": {
      "code": "9",
      "display": "Unknown",
      "system": "ctone:relationship"
    },
    "wardOfCourt": {
      "code": "7",
      "display": "Ward of Court",
      "system": "ctone:relationship"
    }
  },
  "sexualOrientation": {
    "asexual": {
      "code": "765288000",
      "display": "Asexual",
      "system": "http://loinc.org"
    },
    "bisexual": {
      "code": "42035005",
      "display": "Bisexual",
      "system": "http://loinc.org"
    },
    "declineToAnswer": {
      "code": "ASKU",
      "display": "DeclineToAnswer",
      "system": "http://loinc.org"
    },
    "gayHomosexual": {
      "code": "38628009",
      "display": "Gay/Homosexual",
      "system": "http://loinc.org"
    },
    "heterosexual": {
      "code": "20430005",
      "display": "Heterosexual",
      "system": "http://loinc.org"
    },
    "lesbian": {
      "code": "89217008",
      "display": "Lesbian",
      "system": "http://loinc.org"
    },
    "questioning": {
      "code": "766822004",
      "display": "Questioning",
      "system": "http://loinc.org"
    }
  },
  "iddDailyActivities": {
    "bathTime": {
      "code": "bath-time",
      "display": "Bath time",
      "system": "remhealth:idd-daily-activities"
    },
    "childCare": {
      "code": "child-care",
      "display": "Child care",
      "system": "remhealth:idd-daily-activities"
    },
    "doctorsAppointment": {
      "code": "doctors-appointment",
      "display": "Doctor's appointment",
      "system": "remhealth:idd-daily-activities"
    },
    "downtime": {
      "code": "downtime",
      "display": "Downtime/hanging out",
      "system": "remhealth:idd-daily-activities"
    },
    "dressing": {
      "code": "dressing",
      "display": "Dressing",
      "system": "remhealth:idd-daily-activities"
    },
    "dropOff": {
      "code": "drop-off",
      "display": "Drop off",
      "system": "remhealth:idd-daily-activities"
    },
    "errands": {
      "code": "errands",
      "display": "Running errands",
      "system": "remhealth:idd-daily-activities"
    },
    "familyActivities": {
      "code": "family-activities",
      "display": "Family activities",
      "system": "remhealth:idd-daily-activities"
    },
    "goingHome": {
      "code": "going-home",
      "display": "Coming and going home",
      "system": "remhealth:idd-daily-activities"
    },
    "mealTime": {
      "code": "mealtime",
      "display": "Mealtime",
      "system": "remhealth:idd-daily-activities"
    },
    "napTime": {
      "code": "naptime",
      "display": "Naptime",
      "system": "remhealth:idd-daily-activities"
    },
    "pickUp": {
      "code": "pick-up",
      "display": "Pick up",
      "system": "remhealth:idd-daily-activities"
    },
    "playTime": {
      "code": "playtime",
      "display": "Playtime",
      "system": "remhealth:idd-daily-activities"
    },
    "reading": {
      "code": "reading",
      "display": "Reading",
      "system": "remhealth:idd-daily-activities"
    },
    "socialGatherings": {
      "code": "social-gatherings",
      "display": "Social and community gatherings",
      "system": "remhealth:idd-daily-activities"
    },
    "speaking": {
      "code": "speaking",
      "display": "Speaking",
      "system": "remhealth:idd-daily-activities"
    },
    "wakingUp": {
      "code": "waking-up",
      "display": "Waking up",
      "system": "remhealth:idd-daily-activities"
    },
    "work": {
      "code": "work",
      "display": "Work/school schedule",
      "system": "remhealth:idd-daily-activities"
    },
    "writing": {
      "code": "writing",
      "display": "Writing",
      "system": "remhealth:idd-daily-activities"
    }
  },
  "relationship": {
    "adoptedDaughter": {
      "code": "DAUADOPT",
      "display": "Adopted Daughter",
      "system": "http://terminology.hl7.org/CodeSystem/v3-RoleCode"
    },
    "adoptedSon": {
      "code": "SONADOPT",
      "display": "Adopted Son",
      "system": "http://terminology.hl7.org/CodeSystem/v3-RoleCode"
    },
    "adoptiveFather": {
      "code": "ADOPTF",
      "display": "Adoptive Father",
      "system": "http://terminology.hl7.org/CodeSystem/v3-RoleCode"
    },
    "adoptiveMother": {
      "code": "ADOPTM",
      "display": "Adoptive Mother",
      "system": "http://terminology.hl7.org/CodeSystem/v3-RoleCode"
    },
    "authorizedRepresentative": {
      "code": "RESPRSN",
      "display": "Authorized Representative",
      "system": "http://terminology.hl7.org/CodeSystem/v3-RoleCode"
    },
    "boyfriend": {
      "code": "BF",
      "display": "Boyfriend",
      "system": "apollo:relationship"
    },
    "brother": {
      "code": "BRO",
      "display": "Brother",
      "system": "http://terminology.hl7.org/CodeSystem/v3-RoleCode"
    },
    "daughter": {
      "code": "DAU",
      "display": "Daughter",
      "system": "http://terminology.hl7.org/CodeSystem/v3-RoleCode"
    },
    "father": {
      "code": "FTH",
      "display": "Father",
      "system": "http://terminology.hl7.org/CodeSystem/v3-RoleCode"
    },
    "fosterDaughter": {
      "code": "DAUFOST",
      "display": "Foster Daughter",
      "system": "http://terminology.hl7.org/CodeSystem/v3-RoleCode"
    },
    "fosterFather": {
      "code": "FTHFOST",
      "display": "Foster Father",
      "system": "http://terminology.hl7.org/CodeSystem/v3-RoleCode"
    },
    "fosterMother": {
      "code": "MTHFOST",
      "display": "Foster Mother",
      "system": "http://terminology.hl7.org/CodeSystem/v3-RoleCode"
    },
    "fosterSon": {
      "code": "SONFOST",
      "display": "Foster Son",
      "system": "http://terminology.hl7.org/CodeSystem/v3-RoleCode"
    },
    "girlfriend": {
      "code": "GF",
      "display": "Girlfriend",
      "system": "apollo:relationship"
    },
    "grandchild": {
      "code": "GRNDCHILD",
      "display": "Grandchild",
      "system": "http://terminology.hl7.org/CodeSystem/v3-RoleCode"
    },
    "grandparent": {
      "code": "GRPRN",
      "display": "Grandparent",
      "system": "http://terminology.hl7.org/CodeSystem/v3-RoleCode"
    },
    "guardian": {
      "code": "GUARD",
      "display": "Guardian",
      "system": "http://terminology.hl7.org/CodeSystem/v3-RoleCode"
    },
    "husband": {
      "code": "HUSB",
      "display": "Husband",
      "system": "http://terminology.hl7.org/CodeSystem/v3-RoleCode"
    },
    "interpreter": {
      "code": "INTRP",
      "display": "Interpreter",
      "system": "http://terminology.hl7.org/CodeSystem/v3-RoleCode"
    },
    "mother": {
      "code": "MTH",
      "display": "Mother",
      "system": "http://terminology.hl7.org/CodeSystem/v3-RoleCode"
    },
    "other": {
      "code": "OTH",
      "display": "Other",
      "system": "http://terminology.hl7.org/CodeSystem/v3-NullFlavor"
    },
    "parent": {
      "code": "PRN",
      "display": "Parent",
      "system": "http://terminology.hl7.org/CodeSystem/v3-RoleCode"
    },
    "patient": {
      "code": "PT",
      "display": "Patient",
      "system": "http://terminology.hl7.org/CodeSystem/v3-RoleCode"
    },
    "powerOfAttorney": {
      "code": "POWATT",
      "display": "Power of Attorney",
      "system": "http://terminology.hl7.org/CodeSystem/v3-RoleCode"
    },
    "significantOther": {
      "code": "SIGOTHR",
      "display": "Significant Other",
      "system": "http://terminology.hl7.org/CodeSystem/v3-RoleCode"
    },
    "sister": {
      "code": "SIS",
      "display": "Sister",
      "system": "http://terminology.hl7.org/CodeSystem/v3-RoleCode"
    },
    "schoolStaff": {
      "code": "SCH",
      "display": "School Staff",
      "system": "apollo:relationship"
    },
    "son": {
      "code": "SON",
      "display": "Son",
      "system": "http://terminology.hl7.org/CodeSystem/v3-RoleCode"
    },
    "uncle": {
      "code": "UNCLE",
      "display": "Uncle",
      "system": "http://terminology.hl7.org/CodeSystem/v3-RoleCode"
    },
    "wife": {
      "code": "WIFE",
      "display": "Wife",
      "system": "http://terminology.hl7.org/CodeSystem/v3-RoleCode"
    }
  },
  "documentType": {
    "assessmentNote": {
      "code": "51848-0",
      "display": "Assessment note",
      "system": "http://hl7.org/fhir/ValueSet/c80-doc-typecodes"
    },
    "mentalHealthProgressNotes": {
      "code": "34904-3",
      "display": "Mental health Progress note",
      "system": "http://hl7.org/fhir/ValueSet/c80-doc-typecodes"
    },
    "planOfCareNote": {
      "code": "56447-6",
      "display": "Plan of care note",
      "system": "http://hl7.org/fhir/ValueSet/c80-doc-typecodes"
    }
  },
  "noteSectionTypes": {
    "assessment": {
      "text": "Assessment",
      "codings": [
        {
          "code": "51848-0",
          "display": "Assessment",
          "system": "http://loinc.org"
        }
      ],
      "identifiers": [],
      "extensions": []
    },
    "diagnosis": {
      "text": "Chief Complaint Reported",
      "codings": [
        {
          "code": "10154-3",
          "display": "Chief complaint Reported",
          "system": "http://loinc.org"
        }
      ],
      "identifiers": [],
      "extensions": []
    },
    "familyHistory": {
      "text": "Family History",
      "codings": [
        {
          "code": "10157-6",
          "display": "Family History",
          "system": "http://loinc.org"
        }
      ],
      "identifiers": [],
      "extensions": []
    },
    "generic": {
      "text": "Generic",
      "codings": [
        {
          "code": "generic",
          "display": "Generic",
          "system": "bells:note-section-type"
        }
      ],
      "identifiers": [],
      "extensions": []
    },
    "goals": {
      "text": "Goals",
      "codings": [
        {
          "code": "61146-7",
          "display": "Goals",
          "system": "http://loinc.org"
        }
      ],
      "identifiers": [],
      "extensions": []
    },
    "healthHistory": {
      "text": "History of Past Illness",
      "codings": [
        {
          "code": "11348-0",
          "display": "History of Past illness",
          "system": "http://loinc.org"
        }
      ],
      "identifiers": [],
      "extensions": []
    },
    "instructions": {
      "text": "Instructions",
      "codings": [
        {
          "code": "69730-0",
          "display": "Instructions",
          "system": "http://loinc.org"
        }
      ],
      "identifiers": [],
      "extensions": []
    },
    "interventions": {
      "text": "Interventions Narrative",
      "codings": [
        {
          "code": "62387-6",
          "display": "Interventions Narrative",
          "system": "http://loinc.org"
        }
      ],
      "identifiers": [],
      "extensions": []
    },
    "medications": {
      "text": "History of Medication Use",
      "codings": [
        {
          "code": "10160-0",
          "display": "History of Medication Use",
          "system": "http://loinc.org"
        }
      ],
      "identifiers": [],
      "extensions": []
    },
    "objective": {
      "text": "Objective Narrative",
      "codings": [
        {
          "code": "61149-1",
          "display": "Objective Narrative",
          "system": "http://loinc.org"
        }
      ],
      "identifiers": [],
      "extensions": []
    },
    "orders": {
      "text": "History of Procedures",
      "codings": [
        {
          "code": "47519-4",
          "display": "History of Procedures",
          "system": "http://loinc.org"
        }
      ],
      "identifiers": [],
      "extensions": []
    },
    "physicalExam": {
      "text": "Physical Findings",
      "codings": [
        {
          "code": "29545-1",
          "display": "Physical findings",
          "system": "http://loinc.org"
        }
      ],
      "identifiers": [],
      "extensions": []
    },
    "plan": {
      "text": "Plan of Care",
      "codings": [
        {
          "code": "18776-5",
          "display": "Plan of Care",
          "system": "http://loinc.org"
        }
      ],
      "identifiers": [],
      "extensions": []
    },
    "results": {
      "text": "Relevant Diagnostic Tests",
      "codings": [
        {
          "code": "30954-2",
          "display": "Relevant diagnostic tests",
          "system": "http://loinc.org"
        }
      ],
      "identifiers": [],
      "extensions": []
    },
    "reviewOfSystems": {
      "text": "Review of Systems",
      "codings": [
        {
          "code": "10187-3",
          "display": "Review of systems",
          "system": "http://loinc.org"
        }
      ],
      "identifiers": [],
      "extensions": []
    },
    "subjective": {
      "text": "Subjective Narrative",
      "codings": [
        {
          "code": "61150-9",
          "display": "Subjective Narrative",
          "system": "http://loinc.org"
        }
      ],
      "identifiers": [],
      "extensions": []
    },
    "superbill": {
      "text": "Superbill",
      "codings": [
        {
          "code": "super-bill",
          "display": "Superbill",
          "system": "bells:note-section-type"
        }
      ],
      "identifiers": [],
      "extensions": []
    },
    "vitals": {
      "text": "Vital Signs",
      "codings": [
        {
          "code": "8716-3",
          "display": "Vital Signs",
          "system": "http://loinc.org"
        }
      ],
      "identifiers": [],
      "extensions": []
    }
  },
  "careTeamRole": {
    "caseManager": {
      "code": "999480451000087102",
      "display": "Case Manager",
      "system": "http://snomed.info/sct"
    },
    "ctOneManagingTherapist": {
      "code": "ClientStatus/ManagingTherapist",
      "display": "Managing Therapist",
      "system": "ctone:care-team-role"
    },
    "nursePractitioner": {
      "code": "224571005",
      "display": "Nurse Practitioner",
      "system": "http://snomed.info/sct"
    },
    "prescriber": {
      "code": "59058001",
      "display": "Prescriber",
      "system": "http://snomed.info/sct"
    },
    "primaryCarePhysician": {
      "code": "446050000",
      "display": "Primary Care Physician",
      "system": "http://snomed.info/sct"
    },
    "psychiatrist": {
      "code": "80584001",
      "display": "Psychiatrist",
      "system": "http://snomed.info/sct"
    },
    "psychotherapist": {
      "code": "224588003",
      "display": "Psychotherapist",
      "system": "http://snomed.info/sct"
    },
    "specialistPhysician": {
      "code": "69280009",
      "display": "Specialist Physician",
      "system": "http://snomed.info/sct"
    }
  },
  "placeOfServices": {
    "ambulanceAirOrWater": {
      "code": "42",
      "display": "Ambulance - Air or Water",
      "system": "https://www.cms.gov/Medicare/Coding/place-of-service-codes/Place_of_Service_Code_Set"
    },
    "ambulanceLand": {
      "code": "41",
      "display": "Ambulance - Land",
      "system": "https://www.cms.gov/Medicare/Coding/place-of-service-codes/Place_of_Service_Code_Set"
    },
    "ambulatorySurgicalCenter": {
      "code": "24",
      "display": "Ambulatory Surgical Center",
      "system": "https://www.cms.gov/Medicare/Coding/place-of-service-codes/Place_of_Service_Code_Set"
    },
    "assistedLivingFacility": {
      "code": "13",
      "display": "Assisted Living Facility",
      "system": "https://www.cms.gov/Medicare/Coding/place-of-service-codes/Place_of_Service_Code_Set"
    },
    "birthingCenter": {
      "code": "25",
      "display": "Birthing Center",
      "system": "https://www.cms.gov/Medicare/Coding/place-of-service-codes/Place_of_Service_Code_Set"
    },
    "communityMentalHealthCenter": {
      "code": "53",
      "display": "Community Mental Health Center",
      "system": "https://www.cms.gov/Medicare/Coding/place-of-service-codes/Place_of_Service_Code_Set"
    },
    "comprehensiveInpatientRehabilitationFacility": {
      "code": "61",
      "display": "Comprehensive Inpatient Rehabilitation Facility",
      "system": "https://www.cms.gov/Medicare/Coding/place-of-service-codes/Place_of_Service_Code_Set"
    },
    "comprehensiveOutpatientRehabilitationFacility": {
      "code": "62",
      "display": "Comprehensive Outpatient Rehabilitation Facility",
      "system": "https://www.cms.gov/Medicare/Coding/place-of-service-codes/Place_of_Service_Code_Set"
    },
    "custodialCareFacility": {
      "code": "33",
      "display": "Custodial Care Facility",
      "system": "https://www.cms.gov/Medicare/Coding/place-of-service-codes/Place_of_Service_Code_Set"
    },
    "emergencyRoomHospital": {
      "code": "23",
      "display": "Emergency Room - Hospital",
      "system": "https://www.cms.gov/Medicare/Coding/place-of-service-codes/Place_of_Service_Code_Set"
    },
    "endStageRenalDiseaseTreatmentFacility": {
      "code": "65",
      "display": "End Stage Renal Disease Treatment Facility",
      "system": "https://www.cms.gov/Medicare/Coding/place-of-service-codes/Place_of_Service_Code_Set"
    },
    "federallyQualifiedHealthCenter": {
      "code": "50",
      "display": "Federally Qualified Health Center",
      "system": "https://www.cms.gov/Medicare/Coding/place-of-service-codes/Place_of_Service_Code_Set"
    },
    "groupHome": {
      "code": "14",
      "display": "Group Home",
      "system": "https://www.cms.gov/Medicare/Coding/place-of-service-codes/Place_of_Service_Code_Set"
    },
    "home": {
      "code": "12",
      "display": "Home",
      "system": "https://www.cms.gov/Medicare/Coding/place-of-service-codes/Place_of_Service_Code_Set"
    },
    "homelessShelter": {
      "code": "04",
      "display": "Homeless Shelter",
      "system": "https://www.cms.gov/Medicare/Coding/place-of-service-codes/Place_of_Service_Code_Set"
    },
    "hospice": {
      "code": "34",
      "display": "Hospice",
      "system": "https://www.cms.gov/Medicare/Coding/place-of-service-codes/Place_of_Service_Code_Set"
    },
    "independentClinic": {
      "code": "49",
      "display": "Independent Clinic",
      "system": "https://www.cms.gov/Medicare/Coding/place-of-service-codes/Place_of_Service_Code_Set"
    },
    "independentLaboratory": {
      "code": "81",
      "display": "Independent Laboratory",
      "system": "https://www.cms.gov/Medicare/Coding/place-of-service-codes/Place_of_Service_Code_Set"
    },
    "indianHealthServiceFreestandingFacility": {
      "code": "05",
      "display": "Indian Health Service Free-standing Facility",
      "system": "https://www.cms.gov/Medicare/Coding/place-of-service-codes/Place_of_Service_Code_Set"
    },
    "indianHealthServiceProviderbasedFacility": {
      "code": "06",
      "display": "Indian Health Service Provider-based Facility",
      "system": "https://www.cms.gov/Medicare/Coding/place-of-service-codes/Place_of_Service_Code_Set"
    },
    "inpatientHospital": {
      "code": "21",
      "display": "Inpatient Hospital",
      "system": "https://www.cms.gov/Medicare/Coding/place-of-service-codes/Place_of_Service_Code_Set"
    },
    "inpatientPsychiatricFacility": {
      "code": "51",
      "display": "Inpatient Psychiatric Facility",
      "system": "https://www.cms.gov/Medicare/Coding/place-of-service-codes/Place_of_Service_Code_Set"
    },
    "intermediateCareFacilityMentallyRetarded": {
      "code": "54",
      "display": "Intermediate Care Facility/Individuals with Intellectual Disabilities",
      "system": "https://www.cms.gov/Medicare/Coding/place-of-service-codes/Place_of_Service_Code_Set"
    },
    "massImmunizationCenter": {
      "code": "60",
      "display": "Mass Immunization Center",
      "system": "https://www.cms.gov/Medicare/Coding/place-of-service-codes/Place_of_Service_Code_Set"
    },
    "militaryTreatmentFacility": {
      "code": "26",
      "display": "Military Treatment Facility",
      "system": "https://www.cms.gov/Medicare/Coding/place-of-service-codes/Place_of_Service_Code_Set"
    },
    "mobileUnit": {
      "code": "15",
      "display": "Mobile Unit",
      "system": "https://www.cms.gov/Medicare/Coding/place-of-service-codes/Place_of_Service_Code_Set"
    },
    "nonresidentialOpioidTreatmentFacility": {
      "code": "58",
      "display": "Non-residential Opioid Treatment Facility",
      "system": "https://www.cms.gov/Medicare/Coding/place-of-service-codes/Place_of_Service_Code_Set"
    },
    "nonresidentialSubstanceAbuseTreatmentFacility": {
      "code": "57",
      "display": "Non-residential Substance Abuse Treatment Facility",
      "system": "https://www.cms.gov/Medicare/Coding/place-of-service-codes/Place_of_Service_Code_Set"
    },
    "nursingFacility": {
      "code": "32",
      "display": "Nursing Facility",
      "system": "https://www.cms.gov/Medicare/Coding/place-of-service-codes/Place_of_Service_Code_Set"
    },
    "offCampusOutpatientHospital": {
      "code": "19",
      "display": "Off Campus - Outpatient Hospital",
      "system": "https://www.cms.gov/Medicare/Coding/place-of-service-codes/Place_of_Service_Code_Set"
    },
    "office": {
      "code": "11",
      "display": "Office",
      "system": "https://www.cms.gov/Medicare/Coding/place-of-service-codes/Place_of_Service_Code_Set"
    },
    "onCampusOutpatientHospital": {
      "code": "22",
      "display": "On Campus - Outpatient Hospital",
      "system": "https://www.cms.gov/Medicare/Coding/place-of-service-codes/Place_of_Service_Code_Set"
    },
    "otherPlaceOfService": {
      "code": "99",
      "display": "Other Place of Service",
      "system": "https://www.cms.gov/Medicare/Coding/place-of-service-codes/Place_of_Service_Code_Set"
    },
    "pharmacy": {
      "code": "01",
      "display": "Pharmacy",
      "system": "https://www.cms.gov/Medicare/Coding/place-of-service-codes/Place_of_Service_Code_Set"
    },
    "placeofEmploymentWorksite": {
      "code": "18",
      "display": "Place of Employment - Worksite",
      "system": "https://www.cms.gov/Medicare/Coding/place-of-service-codes/Place_of_Service_Code_Set"
    },
    "prisonCorrectionalFacility": {
      "code": "09",
      "display": "Prison/Correctional Facility",
      "system": "https://www.cms.gov/Medicare/Coding/place-of-service-codes/Place_of_Service_Code_Set"
    },
    "psychiatricFacilityPartialHospitalization": {
      "code": "52",
      "display": "Psychiatric Facility - Partial Hospitalization",
      "system": "https://www.cms.gov/Medicare/Coding/place-of-service-codes/Place_of_Service_Code_Set"
    },
    "psychiatricResidentialTreatmentCenter": {
      "code": "56",
      "display": "Psychiatric Residential Treatment Center",
      "system": "https://www.cms.gov/Medicare/Coding/place-of-service-codes/Place_of_Service_Code_Set"
    },
    "publicHealthClinic": {
      "code": "71",
      "display": "Public Health Clinic",
      "system": "https://www.cms.gov/Medicare/Coding/place-of-service-codes/Place_of_Service_Code_Set"
    },
    "residentialSubstanceAbuseTreatmentFacility": {
      "code": "55",
      "display": "Residential Substance Abuse Treatment Facility",
      "system": "https://www.cms.gov/Medicare/Coding/place-of-service-codes/Place_of_Service_Code_Set"
    },
    "ruralHealthClinic": {
      "code": "72",
      "display": "Rural Health Clinic",
      "system": "https://www.cms.gov/Medicare/Coding/place-of-service-codes/Place_of_Service_Code_Set"
    },
    "school": {
      "code": "03",
      "display": "School",
      "system": "https://www.cms.gov/Medicare/Coding/place-of-service-codes/Place_of_Service_Code_Set"
    },
    "skilledNursingFacility": {
      "code": "31",
      "display": "Skilled Nursing Facility",
      "system": "https://www.cms.gov/Medicare/Coding/place-of-service-codes/Place_of_Service_Code_Set"
    },
    "telehealth": {
      "code": "02",
      "display": "Telehealth Provided Other than in Patient's Home",
      "system": "https://www.cms.gov/Medicare/Coding/place-of-service-codes/Place_of_Service_Code_Set"
    },
    "telehealthInPatientHome": {
      "code": "10",
      "display": "Telehealth Provided in Patient's Home",
      "system": "https://www.cms.gov/Medicare/Coding/place-of-service-codes/Place_of_Service_Code_Set"
    },
    "temporaryLodging": {
      "code": "16",
      "display": "Temporary Lodging",
      "system": "https://www.cms.gov/Medicare/Coding/place-of-service-codes/Place_of_Service_Code_Set"
    },
    "tribalFreestandingFacility": {
      "code": "07",
      "display": "Tribal Free-standing Facility",
      "system": "https://www.cms.gov/Medicare/Coding/place-of-service-codes/Place_of_Service_Code_Set"
    },
    "tribalProviderbasedFacility": {
      "code": "08",
      "display": "Tribal Provider-based Facility",
      "system": "https://www.cms.gov/Medicare/Coding/place-of-service-codes/Place_of_Service_Code_Set"
    },
    "urgentCareFacility": {
      "code": "20",
      "display": "Urgent Care Facility",
      "system": "https://www.cms.gov/Medicare/Coding/place-of-service-codes/Place_of_Service_Code_Set"
    },
    "walkinRetailHealthClinic": {
      "code": "17",
      "display": "Walk-in Retail Health Clinic",
      "system": "https://www.cms.gov/Medicare/Coding/place-of-service-codes/Place_of_Service_Code_Set"
    }
  },
  "auditEvents": {
    "applicationActivities": {
      "code": "110100",
      "display": "Application Activity",
      "system": "http://dicom.nema.org/resources/ontology/DCM"
    },
    "appointmentSelected": {
      "code": "110110",
      "display": "Appointment details opened",
      "system": "http://terminology.hl7.org/CodeSystem/audit-event-type"
    },
    "chartOpen": {
      "code": "110111",
      "display": "Patient chart opened",
      "system": "http://terminology.hl7.org/CodeSystem/audit-event-type"
    },
    "emergencyOverrideStarted": {
      "code": "110127",
      "display": "Emergency Override Started",
      "system": "http://dicom.nema.org/resources/ontology/DCM"
    },
    "emergencyOverrideStopped": {
      "code": "110138",
      "display": "Emergency Override Stopped",
      "system": "http://dicom.nema.org/resources/ontology/DCM"
    },
    "exportUnsignedNotes": {
      "code": "110106",
      "display": "Export unsigned notes",
      "system": "http://terminology.hl7.org/CodeSystem/audit-event-type"
    },
    "groupNoteOpen": {
      "code": "110113",
      "display": "Group note opened",
      "system": "http://terminology.hl7.org/CodeSystem/audit-event-type"
    },
    "messagePatient": {
      "code": "110114",
      "display": "Message with patient viewed",
      "system": "http://terminology.hl7.org/CodeSystem/audit-event-type"
    },
    "noteOpen": {
      "code": "110115",
      "display": "Patient note opened",
      "system": "http://terminology.hl7.org/CodeSystem/audit-event-type"
    },
    "patientListOpened": {
      "code": "110112",
      "display": "Patient list opened",
      "system": "http://terminology.hl7.org/CodeSystem/audit-event-type"
    },
    "patientSearch": {
      "code": "110116",
      "display": "Patient search",
      "system": "http://terminology.hl7.org/CodeSystem/audit-event-type"
    },
    "scribbleAddPatient": {
      "code": "110117",
      "display": "Scribbles add patient from search",
      "system": "http://terminology.hl7.org/CodeSystem/audit-event-type"
    }
  },
  "restfulSubTypes": {
    "create": {
      "code": "create",
      "display": "Create",
      "system": "http://hl7.org/fhir/restful-interaction"
    },
    "update": {
      "code": "update",
      "display": "Update",
      "system": "http://hl7.org/fhir/restful-interaction"
    },
    "patch": {
      "code": "patch",
      "display": "Patch",
      "system": "http://hl7.org/fhir/restful-interaction"
    },
    "delete": {
      "code": "delete",
      "display": "Delete",
      "system": "http://hl7.org/fhir/restful-interaction"
    },
    "read": {
      "code": "read",
      "display": "Read",
      "system": "http://hl7.org/fhir/restful-interaction"
    },
    "historicalRead": {
      "code": "vread",
      "display": "Historical Read",
      "system": "http://hl7.org/fhir/restful-interaction"
    },
    "search": {
      "code": "search-type",
      "display": "Search",
      "system": "http://hl7.org/fhir/restful-interaction"
    },
    "bundleSearch": {
      "code": "search-system",
      "display": "Search all types",
      "system": "http://hl7.org/fhir/restful-interaction"
    }
  },
  "visitStatuses": {
    "show": {
      "code": "Show",
      "display": "Show",
      "system": "apollo:visit-status"
    },
    "noShow": {
      "code": "NoShow",
      "display": "No Show",
      "system": "apollo:visit-status"
    },
    "cancelled": {
      "code": "Cancelled",
      "display": "Cancelled",
      "system": "apollo:visit-status"
    }
  },
  "groupNoteSectionTypes": {
    "cancelled": {
      "text": "Cancelled",
      "codings": [
        {
          "code": "Cancelled",
          "display": "Cancelled",
          "system": "apollo:visit-status"
        }
      ],
      "identifiers": [],
      "extensions": []
    },
    "noShow": {
      "text": "No Show",
      "codings": [
        {
          "code": "NoShow",
          "display": "No Show",
          "system": "apollo:visit-status"
        }
      ],
      "identifiers": [],
      "extensions": []
    },
    "show": {
      "text": "Show",
      "codings": [
        {
          "code": "Show",
          "display": "Show",
          "system": "apollo:visit-status"
        }
      ],
      "identifiers": [],
      "extensions": []
    }
  },
  "participationType": {
    "participation": {
      "text": "Participation",
      "codings": [
        {
          "code": "PART",
          "display": "Participation",
          "system": "http://terminology.hl7.org/CodeSystem/v3-ParticipationType"
        }
      ],
      "identifiers": [],
      "extensions": []
    },
    "patient": {
      "text": "patient",
      "codings": [
        {
          "code": "PAT",
          "display": "patient",
          "system": "http://terminology.hl7.org/CodeSystem/v3-RoleClass"
        }
      ],
      "identifiers": [],
      "extensions": []
    },
    "primaryPerformer": {
      "text": "primary performer",
      "codings": [
        {
          "code": "PPRF",
          "display": "primary performer",
          "system": "http://terminology.hl7.org/CodeSystem/v3-ParticipationType"
        }
      ],
      "identifiers": [],
      "extensions": []
    },
    "secondaryPerformer": {
      "text": "secondary performer",
      "codings": [
        {
          "code": "SPRF",
          "display": "secondary performer",
          "system": "http://terminology.hl7.org/CodeSystem/v3-ParticipationType"
        }
      ],
      "identifiers": [],
      "extensions": []
    }
  },
  "reportTypes": {
    "patientAccess": {
      "code": "patient-acccess",
      "display": "Patient Access",
      "system": "apollo:report-types"
    },
    "patientServiceUsage": {
      "code": "patient-service-usage",
      "display": "Patient Service Usage",
      "system": "apollo:report-types"
    },
    "recommendationSummary": {
      "code": "recommendation-summary",
      "display": "Recommendation Summary",
      "system": "apollo:report-types"
    },
    "signedNotes": {
      "code": "signed-notes",
      "display": "Signed Notes",
      "system": "apollo:report-types"
    },
    "staffServiceUsage": {
      "code": "staff-service-usage",
      "display": "Staff Service Usage",
      "system": "apollo:report-types"
    },
    "syncIssues": {
      "code": "sync-issues",
      "display": "Sync Issues",
      "system": "apollo:report-types"
    },
    "unsignedNotes": {
      "code": "unsigned-notes",
      "display": "Unsigned Notes",
      "system": "apollo:report-types"
    },
    "unsignedNoteReminder": {
      "code": "unsigned-note-reminder",
      "display": "Unsigned Note Reminder",
      "system": "apollo:report-types"
    },
    "auditSummary": {
      "code": "audit-summary",
      "display": "Audit Summary",
      "system": "apollo:report-types"
    },
    "virtualScribe": {
      "code": "virtual-scribe",
      "display": "Virtual Scribe",
      "system": "apollo:report-types"
    }
  },
  "reportCriteriaAttributes": {
    "includeSupervisees": {
      "code": "include-supervisees",
      "display": "Include Supervisees",
      "system": "apollo:report-criteria-attribute"
    }
  },
  "noteAuditEvents": {
    "overlappingNotes": {
      "code": "reviewed-overlapping",
      "display": "Overlapping Notes",
      "system": "bells:audit-review-filter"
    },
    "similarNotes": {
      "code": "reviewed-similar",
      "display": "Similar Notes",
      "system": "bells:audit-review-filter"
    },
    "complianceAuditReviews": {
      "code": "reviewed-compliance-audits",
      "display": "Quality Audits",
      "system": "bells:audit-review-filter"
    }
  },
  "contractTopics": {
    "ambientListeningConsent": {
      "code": "ambient-listening",
      "display": "Ambient Listening Consent",
      "system": "apollo:consent"
    }
  },
  "usStates": {
    "codes": {
      "al": "Alabama",
      "ak": "Alaska",
      "as": "American Samoa",
      "az": "Arizona",
      "ar": "Arkansas",
      "ca": "California",
      "co": "Colorado",
      "ct": "Connecticut",
      "de": "Delaware",
      "dc": "District of Columbia",
      "fm": "Federated States of Micronesia",
      "fl": "Florida",
      "ga": "Georgia",
      "gu": "Guam",
      "hi": "Hawaii",
      "id": "Idaho",
      "il": "Illinois",
      "in": "Indiana",
      "ia": "Iowa",
      "ks": "Kansas",
      "ky": "Kentucky",
      "la": "Louisiana",
      "me": "Maine",
      "mh": "Marshall Islands",
      "md": "Maryland",
      "ma": "Massachusetts",
      "mi": "Michigan",
      "mn": "Minnesota",
      "ms": "Mississippi",
      "mo": "Missouri",
      "mt": "Montana",
      "ne": "Nebraska",
      "nv": "Nevada",
      "nh": "New Hampshire",
      "nj": "New Jersey",
      "nm": "New Mexico",
      "ny": "New York",
      "nc": "North Carolina",
      "nd": "North Dakota",
      "mp": "Northern Mariana Islands",
      "oh": "Ohio",
      "ok": "Oklahoma",
      "or": "Oregon",
      "pw": "Palau",
      "pa": "Pennsylvania",
      "pr": "Puerto Rico",
      "ri": "Rhode Island",
      "sc": "South Carolina",
      "sd": "South Dakota",
      "tn": "Tennessee",
      "tx": "Texas",
      "ut": "Utah",
      "vt": "Vermont",
      "vi": "Virgin Islands",
      "va": "Virginia",
      "wa": "Washington",
      "wv": "West Virginia",
      "wi": "Wisconsin",
      "wy": "Wyoming",
      "ae": "Armed Forces Europe, the Middle East, and Canada",
      "ap": "Armed Forces Pacific",
      "aa": "Armed Forces Americas (except Canada)"
    },
    "al": {
      "code": "AL",
      "display": "Alabama",
      "system": "http://hl7.org/fhir/us/core/ValueSet/us-core-usps-state"
    },
    "ak": {
      "code": "AK",
      "display": "Alaska",
      "system": "http://hl7.org/fhir/us/core/ValueSet/us-core-usps-state"
    },
    "as": {
      "code": "AS",
      "display": "American Samoa",
      "system": "http://hl7.org/fhir/us/core/ValueSet/us-core-usps-state"
    },
    "az": {
      "code": "AZ",
      "display": "Arizona",
      "system": "http://hl7.org/fhir/us/core/ValueSet/us-core-usps-state"
    },
    "ar": {
      "code": "AR",
      "display": "Arkansas",
      "system": "http://hl7.org/fhir/us/core/ValueSet/us-core-usps-state"
    },
    "ca": {
      "code": "CA",
      "display": "California",
      "system": "http://hl7.org/fhir/us/core/ValueSet/us-core-usps-state"
    },
    "co": {
      "code": "CO",
      "display": "Colorado",
      "system": "http://hl7.org/fhir/us/core/ValueSet/us-core-usps-state"
    },
    "ct": {
      "code": "CT",
      "display": "Connecticut",
      "system": "http://hl7.org/fhir/us/core/ValueSet/us-core-usps-state"
    },
    "de": {
      "code": "DE",
      "display": "Delaware",
      "system": "http://hl7.org/fhir/us/core/ValueSet/us-core-usps-state"
    },
    "dc": {
      "code": "DC",
      "display": "District of Columbia",
      "system": "http://hl7.org/fhir/us/core/ValueSet/us-core-usps-state"
    },
    "fm": {
      "code": "FM",
      "display": "Federated States of Micronesia",
      "system": "http://hl7.org/fhir/us/core/ValueSet/us-core-usps-state"
    },
    "fl": {
      "code": "FL",
      "display": "Florida",
      "system": "http://hl7.org/fhir/us/core/ValueSet/us-core-usps-state"
    },
    "ga": {
      "code": "GA",
      "display": "Georgia",
      "system": "http://hl7.org/fhir/us/core/ValueSet/us-core-usps-state"
    },
    "gu": {
      "code": "GU",
      "display": "Guam",
      "system": "http://hl7.org/fhir/us/core/ValueSet/us-core-usps-state"
    },
    "hi": {
      "code": "HI",
      "display": "Hawaii",
      "system": "http://hl7.org/fhir/us/core/ValueSet/us-core-usps-state"
    },
    "id": {
      "code": "ID",
      "display": "Idaho",
      "system": "http://hl7.org/fhir/us/core/ValueSet/us-core-usps-state"
    },
    "il": {
      "code": "IL",
      "display": "Illinois",
      "system": "http://hl7.org/fhir/us/core/ValueSet/us-core-usps-state"
    },
    "in": {
      "code": "IN",
      "display": "Indiana",
      "system": "http://hl7.org/fhir/us/core/ValueSet/us-core-usps-state"
    },
    "ia": {
      "code": "IA",
      "display": "Iowa",
      "system": "http://hl7.org/fhir/us/core/ValueSet/us-core-usps-state"
    },
    "ks": {
      "code": "KS",
      "display": "Kansas",
      "system": "http://hl7.org/fhir/us/core/ValueSet/us-core-usps-state"
    },
    "ky": {
      "code": "KY",
      "display": "Kentucky",
      "system": "http://hl7.org/fhir/us/core/ValueSet/us-core-usps-state"
    },
    "la": {
      "code": "LA",
      "display": "Louisiana",
      "system": "http://hl7.org/fhir/us/core/ValueSet/us-core-usps-state"
    },
    "me": {
      "code": "ME",
      "display": "Maine",
      "system": "http://hl7.org/fhir/us/core/ValueSet/us-core-usps-state"
    },
    "mh": {
      "code": "MH",
      "display": "Marshall Islands",
      "system": "http://hl7.org/fhir/us/core/ValueSet/us-core-usps-state"
    },
    "md": {
      "code": "MD",
      "display": "Maryland",
      "system": "http://hl7.org/fhir/us/core/ValueSet/us-core-usps-state"
    },
    "ma": {
      "code": "MA",
      "display": "Massachusetts",
      "system": "http://hl7.org/fhir/us/core/ValueSet/us-core-usps-state"
    },
    "mi": {
      "code": "MI",
      "display": "Michigan",
      "system": "http://hl7.org/fhir/us/core/ValueSet/us-core-usps-state"
    },
    "mn": {
      "code": "MN",
      "display": "Minnesota",
      "system": "http://hl7.org/fhir/us/core/ValueSet/us-core-usps-state"
    },
    "ms": {
      "code": "MS",
      "display": "Mississippi",
      "system": "http://hl7.org/fhir/us/core/ValueSet/us-core-usps-state"
    },
    "mo": {
      "code": "MO",
      "display": "Missouri",
      "system": "http://hl7.org/fhir/us/core/ValueSet/us-core-usps-state"
    },
    "mt": {
      "code": "MT",
      "display": "Montana",
      "system": "http://hl7.org/fhir/us/core/ValueSet/us-core-usps-state"
    },
    "ne": {
      "code": "NE",
      "display": "Nebraska",
      "system": "http://hl7.org/fhir/us/core/ValueSet/us-core-usps-state"
    },
    "nv": {
      "code": "NV",
      "display": "Nevada",
      "system": "http://hl7.org/fhir/us/core/ValueSet/us-core-usps-state"
    },
    "nh": {
      "code": "NH",
      "display": "New Hampshire",
      "system": "http://hl7.org/fhir/us/core/ValueSet/us-core-usps-state"
    },
    "nj": {
      "code": "NJ",
      "display": "New Jersey",
      "system": "http://hl7.org/fhir/us/core/ValueSet/us-core-usps-state"
    },
    "nm": {
      "code": "NM",
      "display": "New Mexico",
      "system": "http://hl7.org/fhir/us/core/ValueSet/us-core-usps-state"
    },
    "ny": {
      "code": "NY",
      "display": "New York",
      "system": "http://hl7.org/fhir/us/core/ValueSet/us-core-usps-state"
    },
    "nc": {
      "code": "NC",
      "display": "North Carolina",
      "system": "http://hl7.org/fhir/us/core/ValueSet/us-core-usps-state"
    },
    "nd": {
      "code": "ND",
      "display": "North Dakota",
      "system": "http://hl7.org/fhir/us/core/ValueSet/us-core-usps-state"
    },
    "mp": {
      "code": "MP",
      "display": "Northern Mariana Islands",
      "system": "http://hl7.org/fhir/us/core/ValueSet/us-core-usps-state"
    },
    "oh": {
      "code": "OH",
      "display": "Ohio",
      "system": "http://hl7.org/fhir/us/core/ValueSet/us-core-usps-state"
    },
    "ok": {
      "code": "OK",
      "display": "Oklahoma",
      "system": "http://hl7.org/fhir/us/core/ValueSet/us-core-usps-state"
    },
    "or": {
      "code": "OR",
      "display": "Oregon",
      "system": "http://hl7.org/fhir/us/core/ValueSet/us-core-usps-state"
    },
    "pw": {
      "code": "PW",
      "display": "Palau",
      "system": "http://hl7.org/fhir/us/core/ValueSet/us-core-usps-state"
    },
    "pa": {
      "code": "PA",
      "display": "Pennsylvania",
      "system": "http://hl7.org/fhir/us/core/ValueSet/us-core-usps-state"
    },
    "pr": {
      "code": "PR",
      "display": "Puerto Rico",
      "system": "http://hl7.org/fhir/us/core/ValueSet/us-core-usps-state"
    },
    "ri": {
      "code": "RI",
      "display": "Rhode Island",
      "system": "http://hl7.org/fhir/us/core/ValueSet/us-core-usps-state"
    },
    "sc": {
      "code": "SC",
      "display": "South Carolina",
      "system": "http://hl7.org/fhir/us/core/ValueSet/us-core-usps-state"
    },
    "sd": {
      "code": "SD",
      "display": "South Dakota",
      "system": "http://hl7.org/fhir/us/core/ValueSet/us-core-usps-state"
    },
    "tn": {
      "code": "TN",
      "display": "Tennessee",
      "system": "http://hl7.org/fhir/us/core/ValueSet/us-core-usps-state"
    },
    "tx": {
      "code": "TX",
      "display": "Texas",
      "system": "http://hl7.org/fhir/us/core/ValueSet/us-core-usps-state"
    },
    "ut": {
      "code": "UT",
      "display": "Utah",
      "system": "http://hl7.org/fhir/us/core/ValueSet/us-core-usps-state"
    },
    "vt": {
      "code": "VT",
      "display": "Vermont",
      "system": "http://hl7.org/fhir/us/core/ValueSet/us-core-usps-state"
    },
    "vi": {
      "code": "VI",
      "display": "Virgin Islands",
      "system": "http://hl7.org/fhir/us/core/ValueSet/us-core-usps-state"
    },
    "va": {
      "code": "VA",
      "display": "Virginia",
      "system": "http://hl7.org/fhir/us/core/ValueSet/us-core-usps-state"
    },
    "wa": {
      "code": "WA",
      "display": "Washington",
      "system": "http://hl7.org/fhir/us/core/ValueSet/us-core-usps-state"
    },
    "wv": {
      "code": "WV",
      "display": "West Virginia",
      "system": "http://hl7.org/fhir/us/core/ValueSet/us-core-usps-state"
    },
    "wi": {
      "code": "WI",
      "display": "Wisconsin",
      "system": "http://hl7.org/fhir/us/core/ValueSet/us-core-usps-state"
    },
    "wy": {
      "code": "WY",
      "display": "Wyoming",
      "system": "http://hl7.org/fhir/us/core/ValueSet/us-core-usps-state"
    },
    "ae": {
      "code": "AE",
      "display": "Armed Forces Europe, the Middle East, and Canada",
      "system": "http://hl7.org/fhir/us/core/ValueSet/us-core-usps-state"
    },
    "ap": {
      "code": "AP",
      "display": "Armed Forces Pacific",
      "system": "http://hl7.org/fhir/us/core/ValueSet/us-core-usps-state"
    },
    "aa": {
      "code": "AA",
      "display": "Armed Forces Americas (except Canada)",
      "system": "http://hl7.org/fhir/us/core/ValueSet/us-core-usps-state"
    }
  },
  "systemOfOrigins": {
    "myAvatar": {
      "code": "avatar",
      "display": "myAvatar",
      "system": "carefabric://system-of-origin"
    },
    "myEvolv": {
      "code": "evolv",
      "display": "myEvolv",
      "system": "carefabric://system-of-origin"
    },
    "telehealth": {
      "code": "telehealth",
      "display": "Telehealth",
      "system": "carefabric://system-of-origin"
    }
  },
  "scribeNoteFormat": {
    "birp": {
      "code": "BIRP",
      "display": "Behavior, Intervention, Response, Plan",
      "system": "bells:scribe-note-format"
    },
    "dap": {
      "code": "DAP",
      "display": "Data, Assessment, Plan",
      "system": "bells:scribe-note-format"
    },
    "girpp": {
      "code": "GIRPP",
      "display": "Goal, Intervention, Response, Progress, Plan",
      "system": "bells:scribe-note-format"
    },
    "sirp": {
      "code": "SIRP",
      "display": "Situation, Intervention, Response, Plan",
      "system": "bells:scribe-note-format"
    },
    "behavioraL_SOAP": {
      "code": "BEHAVIORAL_SOAP",
      "display": "Subjective, Objective, Assessment, Plan",
      "system": "bells:scribe-note-format"
    },
    "hp": {
      "code": "HP",
      "display": "History and Physical",
      "system": "bells:scribe-note-format"
    },
    "physicaL_SOAP": {
      "code": "PHYSICAL_SOAP",
      "display": "Subjective, Objective, Assessment, Plan",
      "system": "bells:scribe-note-format"
    }
  }
};
const systems$1 = {
  "allergyIntolerance": "http://hl7.org/fhir/ValueSet/allergyintolerance-code",
  "altClientId": "ctone:altclient-id",
  "appointmentStatus": "http://hl7.org/fhir/ValueSet/appointmentstatus",
  "auditEventType": "http://terminology.hl7.org/CodeSystem/audit-event-type",
  "auditEventSubType": "http://hl7.org/fhir/ValueSet/audit-event-sub-type",
  "bellsNoteSectionType": "bells:note-section-type",
  "bellsAuditReviewFilter": "bells:audit-review-filter",
  "clientId": "ctone:client-id",
  "cmsPlaceOfService": "https://www.cms.gov/Medicare/Coding/place-of-service-codes/Place_of_Service_Code_Set",
  "communicationTopic": "apollo:communication-topic",
  "consent": "apollo:consent",
  "countyCode": "ctone:county-code",
  "cpt": "http://www.ama-assn.org/go/cpt",
  "ctoneCareTeamRole": "ctone:care-team-role",
  "ctoneRelationship": "ctone:relationship",
  "customRelationship": "apollo:relationship",
  "dcm": "http://dicom.nema.org/resources/ontology/DCM",
  "documentTypes": "http://hl7.org/fhir/ValueSet/c80-doc-typecodes",
  "dsm4": "http://hl7.org/fhir/sid/dsm4",
  "dsm5": "http://hl7.org/fhir/sid/dsm5",
  "fhirContactRole": "http://terminology.hl7.org/CodeSystem/v2-0131",
  "fhirIdentifierType": "http://terminology.hl7.org/ValueSet/v2-0203",
  "genderIdentity": "http://hl7.org/fhir/gender-identity",
  "groupCode": "ctone:group-code",
  "guarantorId": "ctone:guarantor-id",
  "hcpcs": "https://bluebutton.cms.gov/resources/codesystem/hcpcs",
  "helpJuice": "https://helpjuice.com/",
  "icd10": "http://hl7.org/fhir/sid/icd-10-cm",
  "icd9": "http://hl7.org/fhir/sid/icd-9-cm",
  "iddDailyActivities": "remhealth:idd-daily-activities",
  "ietfLanguage": "urn:ietf:bcp:47",
  "loinc": "http://loinc.org",
  "longTermGoal": "ctone:long-term-goal",
  "mammothAccountId": "mammoth:account-id",
  "medicaidIdentifier": "apollo:medicaid-unknown-state",
  "medicareIdentifier": "http://hl7.org/fhir/sid/us-mbi",
  "nullFlavor": "http://terminology.hl7.org/CodeSystem/v3-NullFlavor",
  "reportCriteriaAttribute": "apollo:report-criteria-attribute",
  "systemOfOrigin": "carefabric://system-of-origin",
  "participationType": "http://terminology.hl7.org/CodeSystem/v3-ParticipationType",
  "patientId": "apollo:patient-id",
  "personalPronouns": "https://loinc.org/LL5144-2",
  "program": "ctone:program",
  "raceAndEthnicityCdc": "urn:oid:2.16.840.1.113883.6.238",
  "relationship": "http://terminology.hl7.org/CodeSystem/v3-RoleCode",
  "reportTypes": "apollo:report-types",
  "resourceTypes": "apollo:resource-types",
  "restfulOperation": "http://hl7.org/fhir/restful-interaction",
  "rxNorm": "http://www.nlm.nih.gov/research/umls/rxnorm",
  "scribeNoteFormat": "bells:scribe-note-format",
  "scriptSurePatient": "scriptsure:patients",
  "shortTermGoal": "ctone:short-term-goal",
  "snomed": "http://snomed.info/sct",
  "socialSecurityNumber": "http://hl7.org/fhir/sid/us-ssn",
  "specialType": "apollo:special-types",
  "staffId": "apollo:staff-id",
  "subscriberRelationship": "http://hl7.org/fhir/ValueSet/subscriber-relationship",
  "ucum": "http://unitsofmeasure.org/",
  "unknownOrigin": "apollo:unknown-origin",
  "usState": "http://hl7.org/fhir/us/core/ValueSet/us-core-usps-state",
  "visitStatus": "apollo:visit-status",
  "windowsLogin": "apollo:windows-login",
  "staffSignatureCustomHeader": "000A00000000000000000000000000000028CC10000000000000025807D00000000000000000000000000000000000000000000000000000",
  "oids": {
    "icd9": "urn:oid:2.16.840.1.113883.6.103",
    "icd10": "urn:oid:2.16.840.1.113883.6.90",
    "snomed": "urn:oid:2.16.840.1.113883.6.96",
    "loinc": "urn:oid:2.16.840.1.113883.6.1",
    "hcpcs": "urn:oid:2.16.840.1.113883.6.285",
    "raceAndEthnicityCdc": "urn:oid:2.16.840.1.113883.6.238",
    "genderIdentity": "urn:oid:2.16.840.1.113883.4.642.3.972",
    "nullFlavor": "urn:oid:2.16.840.1.113883.5.1008"
  }
};
const defaults = {
  "practicePreferences": {
    "allowMarkAllReview": true,
    "customNoteOptions": [],
    "filterDxByProgram": false,
    "locationLabel": {
      "plural": "Locations",
      "singular": "Location"
    },
    "militaryTimeInPrintouts": false,
    "checkOverlappingSessions": false,
    "noteSignWarningTimeout": 36,
    "sessionToSignIncludesWeekends": true,
    "allowSessionTimeInputDefault": true,
    "requirePlaceOfService": true,
    "hidePlaceOfService": false,
    "enrollmentLabel": {
      "plural": "Enrollments",
      "singular": "Enrollment"
    },
    "patientLabel": {
      "plural": "Patients",
      "singular": "Patient"
    },
    "patientPopulationLevel": "All",
    "sessionTimeoutInMinutes": 30,
    "sessionLabel": {
      "plural": "Sessions",
      "singular": "Session"
    },
    "problemLabel": {
      "plural": "Problems",
      "singular": "Problem"
    },
    "practitionerLabel": {
      "plural": "Practitioners",
      "singular": "Practitioner"
    },
    "appointmentStatuses": [],
    "allowReportEmails": false,
    "restrictReportEmailsToAdmins": false,
    "appointmentCreation": false,
    "hideNoShowCancelledAppointments": false,
    "allowAppointmentCancellation": false,
    "scheduleMonthlyUserReport": true,
    "levelsOfAssistance": [],
    "numberOfPromptsCodes": [],
    "patientSignatureServiceTypeExclusions": [],
    "allowNotePrintout": true,
    "filterNoteCategoryByProgram": true,
    "alwaysMaskMobileCalls": false,
    "allowMobilePatientSignature": false,
    "emailTestingRecipients": [],
    "allowGroupNoteAdminEdit": false,
    "defaultAsDocumentationNote": false,
    "showSyncIssueWidgetOnDashboard": false,
    "forceGroupNoteServiceOverride": false,
    "defaultScribeNoteFormat": {
      "code": "GIRPP",
      "display": "Goal, Intervention, Response, Progress, Plan",
      "system": "bells:scribe-note-format"
    },
    "scribeNoteFormatConfigurations": [],
    "allowNotesFromAppointments": true,
    "appointmentStartNoteServiceTypeExclusions": [],
    "streamlinedStartNote": false
  },
  "personalPreferences": {
    "bellsWebFontAppearance": "Normal",
    "bellsWebTheme": "Light",
    "doNotSurvey": false,
    "militaryTime": false,
    "notificationOptOut": false,
    "pins": [],
    "scheduler": {},
    "textExpansions": true,
    "mobileTextExpansions": false
  }
};
const headerNames = {
  "exposed": [
    "x-rh-continuation",
    "x-rh-if-none-exists",
    "x-rh-response",
    "x-total-count",
    "x-request-charge",
    "x-request-time",
    "x-request-query-time",
    "x-request-query-trips",
    "x-request-population-time",
    "x-time-to-live",
    "x-rh-sync",
    "x-automation",
    "Retry-After",
    "x-rh-security-token",
    "RequestId"
  ],
  "varyCache": [
    "x-rh-continuation",
    "x-rh-response",
    "Prefer",
    "x-rh-profile",
    "x-rh-partition-key",
    "x-rh-sync",
    "x-rh-security-token",
    "x-rh-user-system"
  ],
  "automation": "x-automation",
  "continuationToken": "x-rh-continuation",
  "crossPartition": "x-rh-enable-cross-partition",
  "ifNoneExists": "x-rh-if-none-exists",
  "noHistory": "x-rh-no-history",
  "partitionKey": "x-rh-partition-key",
  "populationTime": "x-request-population-time",
  "prefer": "Prefer",
  "preferenceApplied": "Preference-Applied",
  "profile": "x-rh-profile",
  "queryThrottles": "x-request-query-throttled",
  "queryTime": "x-request-query-time",
  "queryTrips": "x-request-query-trips",
  "requestCharge": "x-request-charge",
  "requestTime": "x-request-time",
  "response": "x-rh-response",
  "retryAfter": "Retry-After",
  "securityToken": "x-rh-security-token",
  "sync": "x-rh-sync",
  "timeToLive": "x-time-to-live",
  "totalCount": "x-total-count",
  "userSystem": "x-rh-user-system"
};
const claims = {
  "astraeus": {
    "Infra": "astraeus.infra",
    "NonceHash": "astraeus.nonce",
    "Profile": "astraeus.profile",
    "Role": "astraeus.role",
    "UserAgent": "astraeus.ua",
    "Zone": "astraeus.zone"
  },
  "azureAD": {
    "AppUserID": "aad.appUserId",
    "Key": "aad.key",
    "ObjectID": "aad.objectId",
    "Tenant": "aad.tenant"
  },
  "careFabric": {
    "Key": "carefabric.key",
    "ScopeID": "carefabric.scopeId",
    "UserID": "carefabric.userId"
  },
  "jwt": {
    "Audience": "aud",
    "AuthenticationMethod": "amr",
    "AuthenticationTime": "auth_time",
    "ClientId": "client_id",
    "Email": "email",
    "FirstName": "given_name",
    "IdentityProvider": "idp",
    "Issuer": "iss",
    "JwtId": "jti",
    "LastName": "family_name",
    "PreferredUsername": "preferred_username",
    "Role": "role",
    "Scope": "scope",
    "Subject": "sub"
  },
  "ldap": {
    "Domain": "ldap.domain",
    "Forest": "ldap.forest",
    "Key": "ldap.key",
    "Sid": "ldap.sid",
    "SidHistory": "ldap.sidHistory"
  },
  "okta": {
    "AuthServer": "okta.authServer",
    "Identifier": "okta.identifier",
    "Key": "okta.key",
    "Source": "okta.source"
  },
  "rh": {
    "ClientAccount": "rh.client.account",
    "GrantType": "rh.client.grant-type",
    "InfrastructureRole": "rh.client.role",
    "IPAddress": "rh.ip_address",
    "Practice": "rh.practice",
    "ProfileList": "rh.profiles",
    "StaffRole": "rh.staff.role",
    "SurveyCode": "rh.survey_code",
    "Zone": "rh.zone"
  }
};
const principals = {
  "NobodyAccountID": "effeeffe-effe-effe-effe-effeeffeeffe",
  "SystemAccountID": "feeffeef-feef-feef-feef-feeffeeffeef"
};
const associateRoles = {
  "Administrator": "administrator",
  "Developer": "developer",
  "Executive": "executive",
  "Implementation": "implementation",
  "Marketing": "marketing",
  "Product": "product",
  "QualityAssurance": "qualityassurance",
  "Support": "support",
  "SysAdmin": "sysadmin",
  "User": "user"
};
const associateRoleAccessPolicies = {
  "externalAccess": [
    "administrator",
    "implementation",
    "support",
    "sysadmin"
  ],
  "internalAccess": [
    "administrator",
    "developer",
    "implementation",
    "product",
    "qualityassurance",
    "support",
    "sysadmin"
  ]
};
const zone = {
  "unzoned": "Unzoned",
  "development": "Development",
  "qa": "QA",
  "stg": "Stg",
  "prod": "Prod",
  "int": "Int",
  "azr": "Azr",
  "train": "Train",
  "sdl": "SDL",
  "phx": "PHX",
  "ctone-dev": "CTOneDev",
  "ctone-prod": "CTOneProd",
  "bells-dev": "BellsDev",
  "bells-prod": "BellsProd",
  "ctone-uat": "CTOneUat"
};
const internalZones = {
  "development": "Development",
  "int": "Int",
  "qa": "QA",
  "stg": "Stg",
  "sdl": "SDL",
  "ctone-dev": "CTOneDev",
  "bells-dev": "BellsDev"
};
const externalZones = {
  "prod": "Prod",
  "train": "Train",
  "phx": "PHX",
  "ctone-uat": "CTOneUat",
  "ctone-prod": "CTOneProd",
  "bells-prod": "BellsProd"
};
const deploymentZones = {
  "int": "Int",
  "qa": "QA",
  "stg": "Stg",
  "train": "Train",
  "prod": "Prod",
  "sdl": "SDL",
  "phx": "PHX",
  "ctone-dev": "CTOneDev",
  "ctone-uat": "CTOneUat",
  "ctone-prod": "CTOneProd",
  "bells-dev": "BellsDev",
  "bells-prod": "BellsProd"
};
const activeZones = {
  "development": "Development",
  "qa": "QA",
  "stg": "Stg",
  "prod": "Prod",
  "int": "Int",
  "azr": "Azr",
  "train": "Train",
  "sdl": "SDL",
  "phx": "PHX",
  "ctone-dev": "CTOneDev",
  "ctone-prod": "CTOneProd",
  "bells-dev": "BellsDev",
  "bells-prod": "BellsProd",
  "ctone-uat": "CTOneUat"
};
function instantFromDate(date) {
  if (!date) {
    return date;
  }
  return instantFromDateTime(DateTime.fromJSDate(date));
}
function instantFromDateTime(dateTime) {
  if (!dateTime) {
    return dateTime;
  }
  return dateTime.toUTC().toFormat("yyyy'-'LL'-'dd'T'HH':'mm':'ss'Z'");
}
function instantToDate(value) {
  if (!value) {
    return value;
  }
  return instantToDateTime(value).toJSDate();
}
function instantToDateTime(value) {
  if (!value) {
    return value;
  }
  return DateTime.fromISO(value.toString());
}
function instantPlus(date, duration) {
  const luxonDuration = isLuxonDurationLike(duration) ? duration : durationToLuxon(duration);
  return instantFromDateTime(instantToDateTime(date).plus(luxonDuration));
}
function instantMinus(date, duration) {
  const luxonDuration = isLuxonDurationLike(duration) ? duration : durationToLuxon(duration);
  return instantFromDateTime(instantToDateTime(date).minus(luxonDuration));
}
function instantDiff(x, y, returnUnit) {
  const duration = instantToDateTime(x).diff(instantToDateTime(y));
  if (returnUnit) {
    return duration.as(returnUnit);
  }
  return durationFromLuxon(duration);
}
function instantDiffNow(date, returnUnit) {
  if (returnUnit) {
    return instantDiff(date, Instant.now(), returnUnit);
  }
  return instantDiff(date, Instant.now());
}
const Instant = {
  now() {
    return instantFromDateTime(DateTime.now());
  },
  utc(year, month, dayOfMonth, hour, minute, second) {
    if (year < 0 || !Number.isSafeInteger(year)) {
      throw new Error(`Invalid year: ${year}`);
    }
    return instantFromDateTime(DateTime.utc(year, month, dayOfMonth, hour, minute, second));
  },
  fromDate: instantFromDate,
  fromDateTime: instantFromDateTime,
  toDate: instantToDate,
  toDateTime: instantToDateTime,
  plus: instantPlus,
  minus: instantMinus,
  /**
   * Returns a duration between two instants.  Duration returned will be a negative value
   * if x is before y.
   */
  diff: instantDiff,
  diffNow: instantDiffNow
};
function localDateFromDate(date) {
  if (!date) {
    return date;
  }
  return localDateFromDateTime(DateTime.fromJSDate(date));
}
function localDateFromDateTime(dateTime) {
  if (!dateTime) {
    return dateTime;
  }
  return dateTime.toFormat("yyyy'-'LL'-'dd");
}
function localDateToDate(value) {
  if (!value) {
    return value;
  }
  return localDateToDateTime(value).toJSDate();
}
function localDateToDateTime(value) {
  if (!value) {
    return value;
  }
  return DateTime.fromFormat(value.toString(), "yyyy'-'LL'-'dd");
}
function localDatePlus(date, duration) {
  return localDateFromDateTime(localDateToDateTime(date).plus(duration));
}
function localDateMinus(date, duration) {
  return localDateFromDateTime(localDateToDateTime(date).minus(duration));
}
function localDateDiff(x, y, returnUnit) {
  const duration = localDateToDateTime(x).diff(localDateToDateTime(y));
  if (returnUnit) {
    return duration.as(returnUnit);
  }
  return durationFromLuxon(duration);
}
function localDateDiffToday(date, returnUnit) {
  if (returnUnit) {
    return localDateDiff(date, LocalDate.today(), returnUnit);
  }
  return localDateDiff(date, LocalDate.today());
}
const LocalDate = {
  today() {
    return localDateFromDateTime(DateTime.now());
  },
  create(year, month, dayOfMonth) {
    if (year < 0 || !Number.isSafeInteger(year)) {
      throw new Error(`Invalid year: ${year}`);
    }
    return localDateFromDateTime(DateTime.utc(year, month, dayOfMonth, 0, 0, 0));
  },
  fromDate: localDateFromDate,
  fromDateTime: localDateFromDateTime,
  toDate: localDateToDate,
  toDateTime: localDateToDateTime,
  /** @alias LocalDate.plus(date, { year: 1 }) */
  nextYear(date) {
    return localDatePlus(date, { year: 1 });
  },
  /** @alias LocalDate.plus(date, { day: 1 }) */
  nextDay(date) {
    return localDatePlus(date, { day: 1 });
  },
  /** @alias LocalDate.minus(date, { year: 1 }) */
  previousYear(date) {
    return localDateMinus(date, { year: 1 });
  },
  /** @alias LocalDate.minus(date, { day: 1 }) */
  previousDay(date) {
    return localDateMinus(date, { day: 1 });
  },
  plus: localDatePlus,
  minus: localDateMinus,
  diff: localDateDiff,
  diffToday: localDateDiffToday
};
function localTimeFromDate(date) {
  if (!date) {
    return date;
  }
  return localTimeFromDateTime(DateTime.fromJSDate(date));
}
function localTimeFromDateTime(dateTime) {
  if (!dateTime) {
    return dateTime;
  }
  return dateTime.toFormat("HH':'mm':'ss");
}
function localTimeToDate(time) {
  if (time === void 0 || time === null) {
    return time;
  }
  const { hour, minute, second } = localTimeToParts(time);
  return new Date(1900, 0, 0, hour, minute, second, 0);
}
function localTimeToDateTime(time) {
  if (time === void 0 || time === null) {
    return time;
  }
  const { hour, minute, second } = localTimeToParts(time);
  return DateTime.local(1900, 1, 1, hour, minute, second, 0);
}
function localTimeToParts(time) {
  if (!time) {
    return time;
  }
  const parts = time.split(":");
  return {
    hour: parseInt(parts[0], 10),
    minute: parseInt(parts[1], 10),
    second: parseInt(parts[2], 10)
  };
}
const LocalTime = {
  now() {
    return localTimeFromDateTime(DateTime.now());
  },
  create(hour, minute, second) {
    const hourStr = hour.toFixed(0).toString().padStart(2, "0");
    const minuteStr = minute.toFixed(0).toString().padStart(2, "0");
    const secondStr = second.toFixed(0).toString().padStart(2, "0");
    return `${hourStr}:${minuteStr}:${secondStr}`;
  },
  toDate: localTimeToDate,
  toDateTime: localTimeToDateTime,
  toParts: localTimeToParts,
  fromDate: localTimeFromDate,
  fromDateTime: localTimeFromDateTime
};
function offsetDateTimeFromInstant(instant) {
  if (!instant) {
    return instant;
  }
  return offsetDateTimeFromDateTime(instantToDateTime(instant).toUTC());
}
function offsetDateTimeFromDate(date) {
  if (!date) {
    return date;
  }
  return offsetDateTimeFromDateTime(DateTime.fromJSDate(date));
}
function offsetDateTimeFromDateTime(dateTime) {
  if (!dateTime) {
    return dateTime;
  }
  return dateTime.toFormat("yyyy'-'LL'-'dd'T'HH':'mm':'ss") + formatLongOffset(dateTime.toFormat("ZZ"));
}
function createOffsetDateTime(instant, offsetMinutes) {
  return offsetDateTimeFromDateTime(instantToDateTime(instant).setZone(offsetMinutesToUtcZone(offsetMinutes)));
}
function offsetDateTimeToDate(value) {
  if (!value) {
    return value;
  }
  return offsetDateTimeToDateTime(value).toJSDate();
}
function offsetDateTimeToDateTime(value) {
  if (!value) {
    return value;
  }
  const valueString = stripFractionalSeconds(value.toString());
  if (valueString.endsWith("Z")) {
    return DateTime.fromFormat(valueString, "yyyy'-'LL'-'dd'T'HH':'mm':'ss'Z'", { zone: "UTC" }).setZone(Settings.defaultZone);
  }
  return DateTime.fromFormat(valueString, "yyyy'-'LL'-'dd'T'HH':'mm':'ssZZ");
}
function offsetMinutesToUtcZone(offsetMinutes) {
  const sign = offsetMinutes > 0 ? "+" : "-";
  const hours = Math.floor(Math.abs(offsetMinutes) / 60).toFixed(0).toString().padStart(2, "0");
  const mins = Math.floor(Math.abs(offsetMinutes) % 60).toFixed(0).toString().padStart(2, "0");
  return "UTC" + sign + hours + ":" + mins;
}
const OffsetDateTime = {
  now() {
    return offsetDateTimeFromDateTime(DateTime.now());
  },
  toLocal(instant) {
    return createOffsetDateTime(instant, DateTime.now().offset);
  },
  create: createOffsetDateTime,
  fromInstant: offsetDateTimeFromInstant,
  fromDate: offsetDateTimeFromDate,
  fromDateTime: offsetDateTimeFromDateTime,
  toDate: offsetDateTimeToDate,
  toDateTime: offsetDateTimeToDateTime
};
function isUtcOffset(offset) {
  switch (offset) {
    case "Z":
    case "UTC":
    case "+00":
    case "-00":
    case "+00:00":
    case "-00:00":
      return true;
    default:
      return false;
  }
}
function formatLongOffset(offset) {
  if (isUtcOffset(offset)) {
    return "Z";
  }
  if (offset.split(":").length === 1) {
    return offset + ":00";
  }
  return offset;
}
function formatShortOffset(offset) {
  if (isUtcOffset(offset)) {
    return "Z";
  }
  const offsetParts = offset.split(":");
  if (offsetParts.length === 1) {
    return offset;
  }
  const minOffset = offsetParts[1];
  if (minOffset === "00") {
    return offsetParts[0];
  }
  return offset;
}
function formatZoneName(zoneName, shortOffset) {
  if (zoneName.startsWith("Etc/GMT-") || zoneName.startsWith("Etc/GMT+")) {
    return "UTC" + shortOffset;
  }
  return zoneName;
}
function offsetTimeFromDate(date) {
  if (!date) {
    return date;
  }
  return offsetTimeFromDateTime(DateTime.fromJSDate(date));
}
function offsetTimeFromDateTime(dateTime) {
  if (!dateTime) {
    return dateTime;
  }
  return dateTime.toFormat("HH':'mm':'ss");
}
function createOffsetTime(localTime, offsetMinutes) {
  if (offsetMinutes === 0)
    return DateTime.fromISO(`2000-01-01T${stripFractionalSeconds(localTime.toString())}Z`, { zone: "UTC" }).toFormat("HH':'mm':'ss'Z'");
  const m = DateTime.fromISO(`2000-01-01T${stripFractionalSeconds(localTime.toString())}Z`, { zone: "UTC" }).setZone(offsetMinutesToUtcZone(offsetMinutes), { keepLocalTime: true });
  const offset = m.toFormat("ZZ");
  return m.toFormat("HH':'mm':'ss") + formatShortOffset(offset);
}
function offsetTimeToDate(time) {
  if (time === void 0 || time === null) {
    return time;
  }
  const { hour, minute, second } = offsetTimeToParts(time);
  return new Date(1900, 0, 0, hour, minute, second, 0);
}
function offsetTimeToDateTime(time) {
  if (time === void 0 || time === null) {
    return time;
  }
  const { hour, minute, second, offsetMinutes } = offsetTimeToParts(time);
  return DateTime.local(1900, 1, 1, hour, minute, second, 0).setZone(offsetMinutesToUtcZone(offsetMinutes), { keepLocalTime: true });
}
function offsetTimeToParts(time) {
  if (!time) {
    return time;
  }
  const parts = time.split(":");
  return {
    hour: parseInt(parts[0], 10),
    minute: parseInt(parts[1], 10),
    second: parseInt(parts[2].slice(0, 2), 10),
    offsetMinutes: parseInt(parts[2].slice(2), 10)
  };
}
const OffsetTime = {
  now() {
    return offsetTimeFromDateTime(DateTime.now());
  },
  toLocal(localTime) {
    return createOffsetTime(localTime, DateTime.now().offset);
  },
  create: createOffsetTime,
  toDate: offsetTimeToDate,
  toDateTime: offsetTimeToDateTime,
  toParts: offsetTimeToParts,
  fromDate: offsetTimeFromDate,
  fromDateTime: offsetTimeFromDateTime
};
function zonedDateTimeFromDateTime(dateTime, zone2) {
  if (!dateTime) {
    return dateTime;
  }
  const zonedDateTime = dateTime.setZone(zone2);
  const shortOffset = formatShortOffset(zonedDateTime.toFormat("ZZ"));
  return zonedDateTime.toFormat("yyyy'-'LL'-'dd'T'HH':'mm':'ss") + shortOffset + " " + formatZoneName(zone2, shortOffset);
}
function zonedDateTimeFromDate(date, zone2) {
  if (!date) {
    return date;
  }
  return zonedDateTimeFromDateTime(DateTime.fromJSDate(date), zone2);
}
function zonedDateTimeToDate(value) {
  if (!value) {
    return value;
  }
  return zonedDateTimeToDateTime(value).toJSDate();
}
function zonedDateTimeToDateTime(value) {
  if (!value) {
    return value;
  }
  const parts = value.toString().split(" ");
  return offsetDateTimeToDateTime(parts[0]);
}
function zonedDateTimeToDateTimeZone(value) {
  return DateTime.fromJSDate(zonedDateTimeToDate(value)).zoneName;
}
function getLocalZone() {
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  if (timeZone) {
    return timeZone;
  }
  const offset = (/* @__PURE__ */ new Date()).getTimezoneOffset();
  const hours = Math.floor(Math.abs(offset) / 60);
  const min = Math.floor(Math.abs(offset) % 60);
  return "UTC" + (offset > 0 ? "-" : "+") + hours.toString().padStart(2, "0") + ":" + min.toString().padStart(2, "0");
}
const numbers = new Set("0123456789".split(""));
function stripFractionalSeconds(value) {
  const decimalIndex = value.indexOf(".");
  if (decimalIndex === -1) {
    return value;
  }
  let continueIndex = decimalIndex + 1;
  while (continueIndex < value.length) {
    if (!numbers.has(value[continueIndex])) {
      break;
    }
    continueIndex++;
  }
  return value.slice(0, decimalIndex) + value.slice(continueIndex);
}
const ZonedDateTime = {
  now() {
    return ZonedDateTime.create(Instant.now());
  },
  /**
   * @param instant  Instant value
   * @param timezone Optional timezone override.  If none provided, browser's current timezone will be used.
   */
  create(instant, timeZone) {
    const zone2 = timeZone ?? getLocalZone();
    return zonedDateTimeFromDate(Instant.toDate(instant), zone2);
  },
  add(value, add) {
    const zone2 = zonedDateTimeToDateTimeZone(value);
    return !zone2 ? value : zonedDateTimeFromDateTime(zonedDateTimeToDateTime(value).plus(durationToLuxon(add)), zone2);
  },
  /**
   * Returns a duration between two dates.  Duration returned will be a negative value
   * if x is before y.
   */
  diff(x, y) {
    return durationFromLuxon(zonedDateTimeToDateTime(x).diff(zonedDateTimeToDateTime(y)));
  },
  localZone: getLocalZone,
  fromDate: zonedDateTimeFromDate,
  fromDateTime: zonedDateTimeFromDateTime,
  toDate: zonedDateTimeToDate,
  toDateTime: zonedDateTimeToDateTime
};
function approximateFromFull(value) {
  if (isDate(value)) {
    return approximateFromFull(OffsetDateTime.fromDate(value));
  }
  if (isDateTime(value)) {
    return approximateFromFull(OffsetDateTime.fromDateTime(value));
  }
  if (!value) {
    return value;
  }
  return value.toString();
}
function approximateFromLocalDate(value) {
  if (isDate(value)) {
    return approximateFromLocalDate(LocalDate.fromDate(value));
  }
  if (isDateTime(value)) {
    return approximateFromLocalDate(LocalDate.fromDateTime(value));
  }
  if (!value) {
    return value;
  }
  return value.toString();
}
function approximateFromDate(date, precision) {
  if (!date) {
    return date;
  }
  return approximateFromDateTime(DateTime.fromJSDate(date), precision);
}
function approximateFromDateTime(dateTime, precision) {
  if (!dateTime) {
    return dateTime;
  }
  switch (precision) {
    case DateTimePrecision.Year:
      return dateTime.toFormat("yyyy");
    case DateTimePrecision.YearMonth:
      return dateTime.toFormat("yyyy'-'LL");
    case DateTimePrecision.YearMonthDay:
      return approximateFromLocalDate(dateTime);
    default:
      return approximateFromFull(dateTime);
  }
}
function approximateToDate(value) {
  if (!value) {
    return value;
  }
  return approximateToDateTime(value).toJSDate();
}
function approximateToDateTime(value) {
  if (!value) {
    return value;
  }
  switch (getApproximatePrecision(value)) {
    case DateTimePrecision.Year:
      return DateTime.fromFormat(value.toString(), "yyyy");
    case DateTimePrecision.YearMonth:
      return DateTime.fromFormat(value.toString(), "yyyy'-'LL");
    case DateTimePrecision.YearMonthDay:
      return localDateToDateTime(value.toString());
    default:
      return offsetDateTimeToDateTime(value.toString());
  }
}
function approximatePlus(value, duration) {
  if (!value) {
    return value;
  }
  const precision = getApproximatePrecision(value);
  const luxonDuration = isLuxonDurationLike(duration) ? duration : durationToLuxon(duration);
  return approximateFromDateTime(approximateToDateTime(value).plus(luxonDuration), precision);
}
function approximateMinus(value, duration) {
  if (!value) {
    return value;
  }
  const precision = getApproximatePrecision(value);
  const luxonDuration = isLuxonDurationLike(duration) ? duration : durationToLuxon(duration);
  return approximateFromDateTime(approximateToDateTime(value).minus(luxonDuration), precision);
}
function getApproximatePrecision(approximate) {
  if (approximate.indexOf("T") !== -1) {
    return DateTimePrecision.Full;
  }
  switch (approximate.length) {
    case 10:
      return DateTimePrecision.YearMonthDay;
    case 7:
      return DateTimePrecision.YearMonth;
    case 4:
      return DateTimePrecision.Year;
    default:
      throw new Error("Invalid Approximate: " + approximate);
  }
}
const Approximate = {
  now() {
    return approximateFromFull(OffsetDateTime.now());
  },
  today() {
    return approximateFromLocalDate(LocalDate.today());
  },
  fromFull: approximateFromFull,
  fromLocalDate: approximateFromLocalDate,
  fromDate: approximateFromDate,
  fromDateTime: approximateFromDateTime,
  toDate: approximateToDate,
  toDateTime: approximateToDateTime,
  plus: approximatePlus,
  minus: approximateMinus,
  getPrecision: getApproximatePrecision,
  yearMonthDay(year, month, dayOfMonth) {
    const yearStr = year.toFixed(0).toString().padStart(4, "0");
    const monthStr = month.toFixed(0).toString().padStart(2, "0");
    const dayOfMonthStr = dayOfMonth.toFixed(0).toString().padStart(2, "0");
    return `${yearStr}-${monthStr}-${dayOfMonthStr}`;
  },
  yearMonth(year, month) {
    const yearStr = year.toFixed(0).toString().padStart(4, "0");
    const monthStr = month.toFixed(0).toString().padStart(2, "0");
    return `${yearStr}-${monthStr}`;
  },
  year(year) {
    const yearStr = year.toFixed(0).toString().padStart(4, "0");
    return yearStr;
  }
};
const Tzdb = {
  local: getLocalZone
};
function durationFromLuxon(duration) {
  if (!duration) {
    return duration;
  }
  return Duration.fromSeconds(duration.as("seconds"));
}
function durationToLuxon(duration) {
  if (!duration) {
    return duration;
  }
  const parts = durationToParts(duration);
  const luxonDuration = Duration$1.fromObject({
    hours: parts.hours,
    minutes: parts.minutes,
    seconds: parts.seconds
  }).shiftTo("hours", "minutes", "seconds");
  if (parts.negative) {
    return luxonDuration.negate();
  }
  return luxonDuration;
}
function durationNegate(duration) {
  if (!duration) {
    return duration;
  }
  const parts = durationToParts(duration);
  return Duration.fromParts({
    negative: !parts.negative,
    hours: parts.hours,
    minutes: parts.minutes,
    seconds: parts.seconds
  });
}
function durationToParts(duration) {
  if (!duration) {
    return duration;
  }
  let value = duration.toString();
  const negative = value[0] === "-";
  if (negative) {
    value = value.slice(1);
  }
  const parts = value.split(":");
  if (parts.length !== 3) {
    throw new Error(`"${duration}" is an invalid duration format.`);
  }
  const hours = parseInt(parts[0], 10);
  if (!Number.isSafeInteger(hours)) {
    throw new Error(`"${duration}" has an invalid hour part: ${parts[0]}`);
  }
  const minutes = parseInt(parts[1], 10);
  if (!Number.isSafeInteger(minutes) || minutes < 0 || minutes > 59) {
    throw new Error(`"${duration}" has an invalid minute part: ${parts[1]}`);
  }
  const seconds = parseFloat(parts[2]);
  if (!Number.isFinite(seconds) || seconds < 0 || seconds >= 60) {
    throw new Error(`"${duration}" has an invalid second part: ${parts[2]}`);
  }
  return {
    negative,
    hours,
    minutes,
    seconds
  };
}
const unitList = ["years", "months", "weeks", "days", "hours", "minutes", "seconds", "milliseconds"];
function humanizeDuration(duration, options = {}) {
  const { maxUnits = 2, minUnit = "milliseconds" } = options;
  let d = isLuxonDuration(duration) ? duration : durationToLuxon(duration);
  d = d.shiftTo(...unitList);
  let units = [
    d.years !== 0 ? `${d.years} year${Math.abs(d.years) === 1 ? "" : "s"}` : "",
    d.months !== 0 ? `${d.months} month${Math.abs(d.months) === 1 ? "" : "s"}` : "",
    d.weeks !== 0 ? `${d.weeks} week${Math.abs(d.weeks) === 1 ? "" : "s"}` : "",
    d.days !== 0 ? `${d.days} day${Math.abs(d.days) === 1 ? "" : "s"}` : "",
    d.hours !== 0 ? `${d.hours} hr${Math.abs(d.hours) === 1 ? "" : "s"}` : "",
    d.minutes !== 0 ? `${d.minutes} min${Math.abs(d.minutes) === 1 ? "" : "s"}` : "",
    d.seconds !== 0 ? `${d.seconds} sec${Math.abs(d.seconds) === 1 ? "" : "s"}` : "",
    d.milliseconds !== 0 ? `${d.milliseconds} ms` : ""
  ];
  units = units.slice(0, 1 + unitList.indexOf(minUnit));
  return units.filter((p) => !!p).slice(0, maxUnits).join(" ");
}
function plusWeekdays(date, durationObject) {
  const duration = Duration$1.isDuration(durationObject) ? durationObject : typeof durationObject === "string" ? durationToLuxon(durationObject) : Duration$1.fromDurationLike(durationObject);
  const completeWeekdays = Math.floor(duration.as("days"));
  const remainingTime = duration.minus({ days: completeWeekdays });
  let businessDaysLeftToAdd = completeWeekdays;
  while (businessDaysLeftToAdd > 0) {
    date = date.plus({ days: 1 });
    if (date.weekday <= 5) {
      businessDaysLeftToAdd--;
    }
  }
  if (date.weekday === 5) {
    const remainingTimeFriday = date.startOf("day").plus({ days: 1 }).diff(date);
    if (remainingTime > remainingTimeFriday) {
      date = date.plus({ days: 2 });
    }
  }
  date = date.plus(remainingTime);
  return date;
}
function weekdayDuration(startDate, endDate) {
  let negative = false;
  if (startDate > endDate) {
    let swap = endDate;
    endDate = startDate;
    startDate = swap;
    negative = true;
  }
  endDate = endDate.setZone(startDate.zone);
  if (startDate.weekday > 5 && endDate.weekday > 5 && endDate.diff(startDate).as("hours") <= 48) {
    return Duration$1.fromDurationLike({ hours: 0 });
  }
  const weekdayStart = trimWeekendStart(startDate);
  const weekdayEnd = trimWeekendEnd(endDate);
  const totalDuration = weekdayEnd.diff(weekdayStart);
  const numberOfWeeks = Math.floor(totalDuration.as("days") / 7) + (weekdayEnd.weekday < weekdayStart.weekday ? 1 : 0);
  const numberOfWeekendDays = numberOfWeeks * 2;
  const duration = totalDuration.minus({ days: numberOfWeekendDays });
  return negative ? duration.negate() : duration;
}
function trimWeekendStart(date) {
  return date.weekday <= 5 ? date : date.startOf("week").plus({ days: 7 });
}
function trimWeekendEnd(date) {
  return date.weekday <= 5 ? date : date.startOf("week").plus({ day: 5 });
}
const Duration = {
  fromLuxon: durationFromLuxon,
  toLuxon: durationToLuxon,
  negate: durationNegate,
  toParts: durationToParts,
  humanize: humanizeDuration,
  /**
   * Create a Duration.
   * @param hours Number of hours.  Use negative hours to represent negative duration.  Only whole numbers allowed.
   * @param minutes Number of minutes.
   * @param seconds Number of seconds.
   */
  create(hours, minutes, seconds = 0) {
    const totalSeconds = Math.abs(seconds) + Math.abs(minutes) * 60 + Math.abs(hours) * 3600;
    return Duration.fromSeconds(round(totalSeconds * (hours < 0 ? -1 : 1), 3));
  },
  /**
   * Create a Duration from hours.
   * @param hours Number of hours.  Use negative hours to represent negative duration.  Only whole numbers allowed.
   */
  fromHours(hours) {
    return Duration.fromParts({
      negative: hours < 0,
      hours: Math.floor(Math.abs(hours)),
      minutes: Math.floor(Math.abs(hours * 60 % 60)),
      seconds: round(Math.abs(hours * 3600 % 60), 3)
    });
  },
  fromMinutes(minutes) {
    return Duration.fromParts({
      negative: minutes < 0,
      hours: Math.floor(Math.abs(minutes / 60)),
      minutes: Math.floor(Math.abs(minutes % 60)),
      seconds: round(Math.abs(minutes * 60 % 60), 3)
    });
  },
  fromSeconds(seconds) {
    return Duration.fromParts({
      negative: seconds < 0,
      hours: Math.floor(Math.abs(seconds / 3600)),
      minutes: Math.floor(Math.abs(seconds / 60 % 60)),
      seconds: round(Math.abs(seconds % 60), 3)
    });
  },
  fromParts(parts) {
    const hourStr = Math.abs(parts.hours).toFixed(0).toString();
    const secondParts = round(parts.seconds, 3).toString().split(".");
    const fractionStr = secondParts.length > 1 ? "." + secondParts[1].slice(0, 3) : "";
    const minuteStr = Math.floor(parts.minutes).toString().padStart(2, "0");
    const secondStr = Math.floor(parts.seconds).toString().padStart(2, "0");
    return `${parts.negative ? "-" : ""}${hourStr}:${minuteStr}:${secondStr}${fractionStr}`;
  }
};
function round(value, places) {
  const factor = Math.pow(10, places);
  return Math.round((value + Number.EPSILON) * factor) / factor;
}
function isDate(value) {
  return value instanceof Date;
}
function isDateTime(value) {
  return DateTime.isDateTime(value);
}
function isLuxonDuration(value) {
  return Duration$1.isDuration(value);
}
function isLuxonDurationLike(value) {
  return typeof value !== "string";
}
function trimNestedResources(obj) {
  for (const name in obj) {
    if (name === "_original" || name === "resource") {
      continue;
    }
    const value = obj[name];
    if (isNestedResource(value)) {
      obj[name] = createReferenceFromResource(value);
    } else if (typeof value === "object" && !!value) {
      trimNestedResources(value);
    }
  }
}
function createReferenceFromResource(value) {
  return {
    id: value.id,
    display: value.display,
    partition: value.partition,
    resourceType: value.resourceType
  };
}
function isNestedResource(value) {
  return typeof value === "object" && !!value && "resourceType" in value && "id" in value && "meta" in value;
}
function trimExpandedReferences$1(resourceType, obj) {
  const restorations = [];
  getReferences(resourceType, obj).forEach((expandable) => {
    const expandedResource = expandable.resource;
    if (expandedResource) {
      restorations.push(() => expandable.resource = expandedResource);
      delete expandable.resource;
    }
  });
  return () => {
    restorations.forEach((restoreAction) => restoreAction());
  };
}
function distinctResources(resources) {
  const filtered = [];
  resources.forEach((resource) => {
    var _a, _b;
    if (
      // Where we don't already have the latest
      !((_a = resource.meta) == null ? void 0 : _a.isHistorical) && !filtered.some((r) => {
        var _a2;
        return !((_a2 = r.meta) == null ? void 0 : _a2.isHistorical) && r.id === resource.id;
      }) || ((_b = resource.meta) == null ? void 0 : _b.isHistorical) && !filtered.some((r) => r.meta && resource.meta && r.meta.versionId === resource.meta.versionId)
    ) {
      filtered.push(resource);
    }
  });
  return filtered;
}
function getReferencesAtPath(resource, path) {
  return traversePath(resource, path.split("."), (value) => {
    if (!value) {
      return value;
    }
    if (Array.isArray(value)) {
      return value.map(fixNestedResource);
    }
    return fixNestedResource(value);
  });
}
function fixNestedResource(value) {
  if (isNestedResource(value)) {
    return createReferenceFromResource(value);
  }
  return value;
}
function isExpandableResource(resourceType) {
  return resourceType in expansionPaths;
}
function getUnexpandedExpandablesByNames(resourceType, resource, expansionNames) {
  return expansionNames.reduce((expandables, expansionName) => {
    return [...expandables, ...getExpandablesByName(resourceType, resource, expansionName).filter((r) => r.resourceType && !r.resource)];
  }, []);
}
function getExpandedExpandablesByNames(resourceType, resource, expansionNames) {
  return expansionNames.reduce((expandables, expansionName) => {
    return [...expandables, ...getExpandablesByName(resourceType, resource, expansionName).filter((r) => !!r.resource)];
  }, []);
}
function getExpandablesByName(resourceType, resource, expansionName) {
  if (!isExpandableResource(resourceType)) {
    return [];
  }
  const pathConfig = expansionPaths[resourceType];
  if (!pathConfig[expansionName]) {
    throw new Error(`${expansionName} is not a configured expandable for ${resourceType}.`);
  }
  return pathConfig[expansionName].reduce((sum, path) => {
    const references = getReferencesAtPath(resource, path.path);
    const resourceType2 = path.resourceType;
    if (resourceType2) {
      references.forEach((reference) => {
        if (!reference.resourceType) {
          reference.resourceType = resourceType2;
        }
      });
    }
    return sum.concat(...references);
  }, []);
}
function getExpandables(resource) {
  const paths = referencePaths[resource.resourceType];
  return paths.reduce((sum, path) => {
    return sum.concat(...getReferencesAtPath(resource, path));
  }, []);
}
function getReferences(resourceOrType, maybeResource) {
  const resourceType = typeof resourceOrType === "string" ? resourceOrType : resourceOrType.resourceType;
  const resource = typeof resourceOrType === "string" ? maybeResource : resourceOrType;
  if (!(resourceType in referencePaths)) {
    return [];
  }
  const paths = referencePaths[resourceType];
  return paths.flatMap((path) => {
    return getReferencesAtPath(resource, path);
  });
}
function isReference(maybeReference) {
  return !!maybeReference && typeof maybeReference === "object" && !("meta" in maybeReference);
}
function traversePath(item, paths, transformer) {
  if (paths.length === 0) {
    return [];
  }
  const path = paths[0];
  if (paths.length > 1) {
    const restPath = paths.slice(1);
    const data2 = item[path];
    if (!data2) {
      return [];
    }
    if (Array.isArray(data2)) {
      return data2.flatMap((i) => traversePath(i, restPath, transformer));
    }
    const value = traversePath(data2, restPath, transformer);
    return value ? Array.isArray(value) ? value : [value] : [];
  }
  let data = item[path];
  if (transformer && data) {
    const transformed = transformer(data);
    if (transformed) {
      data = item[path] = transformed;
    }
  }
  if (Array.isArray(data)) {
    return data;
  }
  return data ? [data] : [];
}
const ctoneScheme = "ctone://";
const carefabricScheme = "carefabric://";
const bellsScheme = "bells://";
function isCtOne(system, resourceType) {
  const parsed = parseCtOne(system);
  if (parsed) {
    return resourceType ? parsed[1].toLowerCase() === resourceType.toLowerCase() : true;
  }
  return false;
}
function parseCtOne(system) {
  return parseEntityScheme(system, ctoneScheme);
}
function isCareFabric(system, entityType) {
  const parsed = parseCareFabric(system);
  if (parsed) {
    return entityType ? parsed[1].toLowerCase() === entityType.toLowerCase() : true;
  }
  return false;
}
function parseCareFabric(system) {
  return parseEntityScheme(system, carefabricScheme);
}
function isBells(system, resourceType) {
  const parsed = parseBells(system);
  if (parsed) {
    return resourceType ? parsed[1].toLowerCase() === resourceType.toLowerCase() : true;
  }
  return false;
}
function parseBells(system) {
  return parseEntityScheme(system, bellsScheme);
}
function isEhrResourceSystem(system, resourceType) {
  return isCareFabric(system, resourceType) || isCtOne(system, resourceType);
}
function parseResourceSystem(system) {
  return parseCareFabric(system) || parseCtOne(system);
}
function isExternalIdentifier(identifier) {
  return identifier.system ? isEhrResourceSystem(identifier.system) : false;
}
function getExternalIdentifier(identifiers) {
  return identifiers == null ? void 0 : identifiers.find(isExternalIdentifier);
}
function isEhrResourceIdentifier(identifier, resourceType) {
  return identifier.system ? isEhrResourceSystem(identifier.system, resourceType) : false;
}
function parseEntityScheme(system, scheme) {
  if (!system.startsWith(scheme)) {
    return false;
  }
  system = system.slice(scheme.length);
  const parts = system.split("/");
  if (parts.length !== 2) {
    return false;
  }
  const networkId = parts[0].toUpperCase();
  const entityType = parts[1];
  if (!networkId.trim() || !entityType.trim()) {
    return false;
  }
  return [networkId, entityType];
}
function ctOne(networkId, resourceType) {
  return `${ctoneScheme}${networkId.toLowerCase()}/${resourceType}`;
}
function careFabric(networkId, entityType) {
  return `${carefabricScheme}${networkId.toLowerCase()}/${entityType}`;
}
function bells(networkId, resourceType) {
  return `${bellsScheme}${networkId.toLowerCase()}/${resourceType}`;
}
const ehrSystems = {
  ctOne,
  careFabric,
  bells
};
const roleIdToGrantSets = /* @__PURE__ */ new Map([
  ["01D6XWPP3BEWC008YYHVA19NCJ", [40, 51, 38, 60]],
  ["01DG5TFX733HP7C5P8V88XHJRW", [45]],
  ["01EGTX131TJEQME0VRQRX0H1DJ", [45, 2, 38, 60, 69, 62, 24, 25, 3]],
  ["01J0KVVY453EKTH3AQN1M4HKFX", [45, 58, 60, 69]],
  ["01GQ06HMARA0J2PBW9FXT420RX", [45, 2, 38, 60, 69, 62, 24, 25, 3, 17, 61, 68]],
  ["01D6XVBQJKNJW0MQM1FJFY3DAD", [69, 7, 65, 29, 4, 19, 63, 13, 1, 54, 26, 27, 30, 22, 31, 18, 28, 21, 3, 12, 53, 32, 15, 14, 17, 16, 68, 64, 61, 6, 5, 33, 23]],
  ["01JRK66C591J5WSW790JZ9MXKN", [1, 26, 27, 32, 14, 16, 68, 64, 61, 6, 5, 23]],
  ["01DJTWP0KC578WW1J538SCH0BZ", [37, 69, 62, 24, 25, 66, 20]],
  ["01D6XVBM5M2EB25AF2BCRZB9ND", [37, 69, 62, 24, 25, 66, 20, 67]],
  ["01DJTWS1P6GAHHKENWAHMWEQVH", [37, 69, 62, 24, 25, 66, 20]],
  ["01E1CSGZJ9GRBECSS78AZ0DWC7", [10]],
  ["01GTFCGQEF79XKXPX12Z1648NC", [45, 2, 38, 60, 69, 62, 24, 25, 36, 3]],
  ["01D7ZR51KTRRQF79MWH85AK4KX", [9]],
  ["01D7ZR57BZ3B0908D0AE3T49Z5", [2, 59, 43, 44, 11, 56, 55, 57, 42, 69, 34, 65, 29, 4, 19, 60, 0, 27, 63, 13, 22, 31, 18, 21, 52]],
  ["01DV6FMBJG56BB2GE5TVREKS68", [46, 60, 59, 2]],
  ["01D7ZR5BGWPR7BKYP41AJMSFJ4", [2, 59, 42, 37, 69, 34, 65, 29, 35, 60, 0, 27, 15, 52]],
  ["01D7ZR5ZTZ0WV90NEX0E3322S7", [2, 59, 42, 69, 34, 65, 29, 35, 60, 0, 27, 15]],
  ["01E44MV2BPD47N6VN0RAXGCBFK", [2, 59, 42, 69, 34, 65, 29, 20, 35, 60, 0, 56, 55, 57]],
  ["01D7ZR6DW1TFJAKPQCPV9JY3G2", [2, 59, 42, 69, 34, 65, 29, 20, 35, 60, 0]],
  ["01D7ZR6S3TSX6GVYQ2STAKEMJ3", [2, 59, 42, 69, 65, 20, 60, 0]],
  ["01D8S75V6HCC9VJVBAZKW6845P", [59, 43, 44, 30, 69, 34, 65, 29, 60, 8]],
  ["01EEBH7S1BWPTFZKGCFNF35WSE", [59, 43, 44, 69, 39, 29, 34, 41]],
  ["01HW84ZWXZ6JNT25WV6S7V18ZM", [52]]
]);
const grantSetNames = [
  "Access all notifications",
  "All Patient Population",
  "bells",
  "Bells Reporting",
  "Create message channel",
  "Edit compliance audits",
  "Edit note audits",
  "Edit users",
  "Event Handling",
  "Full permissions",
  "Import CDA",
  "Key Vault Management",
  "Manage Bells Reports",
  "Manage clinical configuration",
  "Manage compliance settings",
  "Manage Contracts",
  "Manage email reports",
  "Manage itineraries",
  "Manage locations",
  "Manage message channel",
  "Manage my schedule",
  "Manage note templates",
  "Manage note types",
  "Manage notes",
  "Manage patient health history",
  "Manage patient notes",
  "Manage patient population",
  "Manage practice preferences",
  "Manage programs",
  "Manage roles/permissions",
  "Manage scribbles",
  "Manage service types",
  "Manage signature configurations",
  "Manage sync issues",
  "Manage users",
  "Manage users schedule",
  "Merge patients",
  "Message clients",
  "Message staff",
  "Messaging super admin",
  "Patient Login",
  "Personal preferences super admin",
  "Portal Reporting",
  "Practice Configuration",
  "Practice Management",
  "Practitioner Login",
  "Publish announcements",
  "Read patients",
  "Read practitioners",
  "Read related people",
  "Registry access",
  "remarkable.me",
  "Scribe user",
  "Security Token Management",
  "Skip Inclusive Security Labels",
  "Snippet Library Management",
  "Speech Rule Library Management",
  "Text Expansion Library Management",
  "unity",
  "View all practices",
  "View announcements",
  "View audit reviews",
  "View patient health information",
  "View permissions",
  "View quality assurance",
  "View role list",
  "View scheduler",
  "View signature configurations",
  "View supervision",
  "View user list"
];
const grantSetPermissions = /* @__PURE__ */ new Map([
  [51, [49, 65, 63, 66, 64, 81, 79, 82, 80, 121, 119, 122, 120, 125, 123, 126, 124, 136, 163, 167, 239, 255, 253, 256, 254, 267, 279]],
  [2, [49, 47, 50, 48, 53, 57, 55, 58, 56, 61, 59, 62, 60, 105, 136, 139, 143, 151, 163, 183, 187, 211, 209, 212, 210, 235, 239, 279, 283, 281, 284, 282, 267, 265, 268, 266, 271, 269, 272, 270, 295, 293, 296, 294, 303, 307, 299, 315, 311, 309, 312, 310, 287, 285, 288, 286]],
  [58, [53, 136, 139, 163, 211, 235, 239, 279, 283, 281, 284, 282, 295, 293, 296, 294, 303, 307, 299, 315, 311, 309, 312, 310]],
  [47, [223]],
  [48, [239]],
  [49, [255]],
  [40, [227, 225, 228, 226, 199, 197, 200, 198, 223, 224]],
  [45, [227, 225, 228, 226, 199, 197, 200, 198, 239, 240]],
  [42, [81, 239, 69, 73, 77]],
  [3, [263, 261, 264, 262, 85]],
  [12, [263, 261, 264, 262, 5]],
  [0, [4]],
  [1, [31]],
  [54, [35]],
  [53, [26]],
  [69, [239, 159, 167, 279]],
  [7, [239, 240, 159, 157, 160, 158, 167, 165, 168, 166, 279, 10, 9, 83, 85, 86]],
  [34, [239, 237, 240, 238, 159, 157, 160, 158, 167, 165, 168, 166, 279, 10, 9, 83, 85, 86]],
  [65, [239, 279, 136, 23]],
  [29, [239, 240, 279, 277, 280, 278, 136, 23, 11]],
  [63, [239, 279, 280, 136, 13]],
  [38, [239, 69, 67, 70, 68, 73, 71, 74, 72, 77, 75, 78, 76, 227, 121, 119, 122, 120, 167, 165, 168, 166, 41]],
  [37, [223, 69, 67, 70, 68, 73, 71, 74, 72, 77, 75, 78, 76, 227, 121, 119, 122, 120, 167, 165, 168, 166, 40]],
  [10, [121, 119, 122, 120, 63, 81]],
  [9, [31, 36, 16, 17, 8, 6, 27, 20, 21, 24, 33, 32]],
  [8, [49, 47, 50, 48, 53, 51, 54, 52, 65, 63, 66, 64, 69, 73, 57, 55, 58, 56, 61, 59, 62, 60, 81, 79, 82, 80, 85, 83, 86, 84, 89, 87, 90, 88, 93, 91, 94, 92, 97, 95, 98, 96, 101, 99, 102, 100, 109, 107, 110, 108, 113, 111, 114, 112, 117, 115, 118, 116, 121, 119, 122, 120, 125, 123, 126, 124, 133, 131, 134, 132, 129, 127, 130, 128, 139, 137, 140, 138, 143, 151, 149, 152, 150, 147, 145, 148, 146, 163, 161, 164, 162, 167, 165, 168, 166, 175, 173, 176, 174, 179, 177, 180, 178, 183, 181, 184, 182, 191, 189, 192, 190, 195, 199, 197, 200, 198, 203, 201, 204, 202, 211, 209, 212, 210, 215, 213, 216, 214, 219, 217, 220, 218, 223, 221, 224, 222, 227, 225, 228, 226, 235, 233, 236, 234, 239, 243, 241, 244, 242, 247, 245, 248, 246, 251, 255, 253, 256, 254, 263, 267, 265, 268, 266, 271, 269, 272, 270, 283, 281, 284, 282, 287, 285, 288, 286, 291, 289, 292, 290, 295, 319, 317, 320, 318, 275, 273, 276, 274, 46, 31, 36, 34, 13, 17, 9, 10, 11, 28, 19, 4, 14, 22, 21, 24, 26]],
  [50, [2, 307, 299, 315]],
  [43, [28]],
  [44, [171, 169, 172, 170, 28, 18]],
  [11, [155, 153, 156, 154]],
  [56, [307, 305, 308, 306]],
  [59, [28]],
  [60, [2]],
  [46, [2, 0, 3, 1]],
  [4, [239, 69, 67, 70, 68, 73, 71, 74, 72, 77, 75, 78, 76, 227, 121, 119, 122, 120, 167, 165, 168, 166, 37]],
  [19, [239, 69, 67, 70, 68, 73, 71, 74, 72, 77, 75, 78, 76, 121, 119, 122, 120, 167, 165, 168, 166, 39]],
  [39, [69, 67, 70, 68, 73, 71, 74, 72, 77, 75, 78, 76, 121, 119, 122, 120, 167, 165, 168, 166, 43, 42, 38, 41, 40, 39, 37]],
  [41, [227, 225, 228, 226, 22]],
  [27, [235, 233, 236, 234, 22]],
  [30, [271, 269, 272, 270, 287, 285, 288, 286, 24]],
  [13, [239, 147, 247, 215, 213, 216, 214, 135, 243, 14]],
  [62, [223, 255, 147, 247, 215, 243, 129, 251, 203, 207, 175, 97, 133, 109, 57, 113, 319, 101]],
  [24, [223, 224, 255, 133, 147, 247, 215, 243, 219, 217, 220, 218, 129, 127, 130, 251, 249, 252, 250, 203, 201, 204, 202, 207, 205, 208, 206, 97, 95, 98, 96, 29]],
  [36, [223, 224, 222]],
  [25, [223, 239, 255, 147, 247, 215, 243, 183, 143, 195, 133, 191, 291, 319, 129, 127, 130, 128, 175, 173, 176, 174, 113, 111, 114, 112, 207, 205, 208, 206, 97, 95, 98, 96, 231, 229, 232, 230, 259, 257, 260, 258, 57, 55, 58, 56, 275, 29, 30]],
  [66, [223, 239, 255, 139, 49, 267, 45]],
  [20, [223, 239, 255, 139, 137, 140, 138, 49, 47, 50, 48, 267, 45]],
  [35, [223, 239, 255, 139, 137, 140, 138, 49, 47, 50, 48, 267, 45, 44]],
  [22, [143, 141, 144, 142, 139, 140, 93, 91, 94, 92, 183, 181, 184, 182, 187, 185, 188, 186, 191, 189, 192, 190, 303, 301, 304, 302, 151, 149, 152, 150]],
  [31, [147, 148]],
  [18, [163, 164]],
  [28, [243, 244]],
  [21, [195, 193, 196, 194]],
  [26, [235, 233, 236, 234, 223, 224, 239, 240, 21]],
  [55, [299, 297, 300, 298]],
  [57, [315, 313, 316, 314]],
  [67, [291]],
  [32, [291, 289, 292, 290]],
  [52, [113, 111, 114, 112, 319, 317, 320, 318, 167, 165, 168, 166, 101, 99, 102, 100, 275, 273, 276, 274, 25]],
  [15, [105, 103, 106, 104]],
  [14, [93, 91, 94, 92, 15]],
  [17, [151, 149, 152, 150, 17]],
  [16, [151, 149, 152, 150, 8]],
  [68, [223, 239, 175, 33]],
  [64, [93, 89, 179, 32]],
  [61, [93, 89, 179, 12]],
  [6, [179, 180, 178, 7]],
  [5, [89, 90, 88, 6]],
  [33, [175, 176, 174, 129, 130, 128, 27]],
  [23, [175, 174, 129, 128, 31, 20]]
]);
const permissionIndex = [
  "Announcement:Create",
  "Announcement:Delete",
  "Announcement:Read",
  "Announcement:Update",
  "apollo:contract-enum:administrativeflags:AccessAllNotifications:Allow",
  "apollo:contract-enum:administrativeflags:BellsReportMaster:Allow",
  "apollo:contract-enum:administrativeflags:CanEditComplianceAudit:Allow",
  "apollo:contract-enum:administrativeflags:CanEditNoteAudit:Allow",
  "apollo:contract-enum:administrativeflags:CanEmailReports:Allow",
  "apollo:contract-enum:administrativeflags:CanInviteUsers:Allow",
  "apollo:contract-enum:administrativeflags:CanManageUsers:Allow",
  "apollo:contract-enum:administrativeflags:CanModifyRoles:Allow",
  "apollo:contract-enum:administrativeflags:CanViewAuditReviews:Allow",
  "apollo:contract-enum:administrativeflags:CanViewPermission:Allow",
  "apollo:contract-enum:administrativeflags:ClinicalConfiguration:Allow",
  "apollo:contract-enum:administrativeflags:ComplianceMaster:Allow",
  "apollo:contract-enum:administrativeflags:FullPermissions:Allow",
  "apollo:contract-enum:administrativeflags:ManageItineraries:Allow",
  "apollo:contract-enum:administrativeflags:ManagePracticeDatabase:Allow",
  "apollo:contract-enum:administrativeflags:ModifyNonPracticeDistribution:Allow",
  "apollo:contract-enum:administrativeflags:NoteMaster:Allow",
  "apollo:contract-enum:administrativeflags:PatientMaster:Allow",
  "apollo:contract-enum:administrativeflags:PreferencesMaster:Allow",
  "apollo:contract-enum:administrativeflags:RoleManagement:Allow",
  "apollo:contract-enum:administrativeflags:ScribbleMaster:Allow",
  "apollo:contract-enum:administrativeflags:Scribe:Allow",
  "apollo:contract-enum:administrativeflags:SecurityTokenManagement:Allow",
  "apollo:contract-enum:administrativeflags:SyncIssueMaster:Allow",
  "apollo:contract-enum:administrativeflags:ViewAllPractices:Allow",
  "apollo:contract-enum:clinicalflags:HealthHistory:Allow",
  "apollo:contract-enum:clinicalflags:Note:Allow",
  "apollo:contract-enum:clinicalflags:PatientPopulationAll:Allow",
  "apollo:contract-enum:clinicalflags:QualityAssurance:Allow",
  "apollo:contract-enum:clinicalflags:Supervision:Allow",
  "apollo:contract-enum:internalflags:CanRequestNoHistory:Allow",
  "apollo:contract-enum:internalflags:SkipInclusiveSecurityLabels:Allow",
  "apollo:contract-enum:internalflags:SkipPopulationFiltering:Allow",
  "apollo:contract-enum:messagingflags:CanCreateChannel:Allow",
  "apollo:contract-enum:messagingflags:CanEditAllMessages:Allow",
  "apollo:contract-enum:messagingflags:CanManageChannel:Allow",
  "apollo:contract-enum:messagingflags:CanMessagePatients:Allow",
  "apollo:contract-enum:messagingflags:CanMessageStaff:Allow",
  "apollo:contract-enum:messagingflags:CanReadAllMessages:Allow",
  "apollo:contract-enum:messagingflags:CanWriteAnyMessage:Allow",
  "apollo:contract-enum:schedulerflags:CanManageSchedules:Allow",
  "apollo:contract-enum:schedulerflags:ViewScheduler:Allow",
  "apollo:contract-enum:surveyflags:Metrics:Allow",
  "Appointment:Create",
  "Appointment:Delete",
  "Appointment:Read",
  "Appointment:Update",
  "CarePlan:Create",
  "CarePlan:Delete",
  "CarePlan:Read",
  "CarePlan:Update",
  "CarePlanOutcome:Create",
  "CarePlanOutcome:Delete",
  "CarePlanOutcome:Read",
  "CarePlanOutcome:Update",
  "CarePlanSession:Create",
  "CarePlanSession:Delete",
  "CarePlanSession:Read",
  "CarePlanSession:Update",
  "CdaParsingTask:Create",
  "CdaParsingTask:Delete",
  "CdaParsingTask:Read",
  "CdaParsingTask:Update",
  "Channel:Create",
  "Channel:Delete",
  "Channel:Read",
  "Channel:Update",
  "ChannelMembership:Create",
  "ChannelMembership:Delete",
  "ChannelMembership:Read",
  "ChannelMembership:Update",
  "ChatMessage:Create",
  "ChatMessage:Delete",
  "ChatMessage:Read",
  "ChatMessage:Update",
  "ClinicalDocument:Create",
  "ClinicalDocument:Delete",
  "ClinicalDocument:Read",
  "ClinicalDocument:Update",
  "Communication:Create",
  "Communication:Delete",
  "Communication:Read",
  "Communication:Update",
  "ComplianceAudit:Create",
  "ComplianceAudit:Delete",
  "ComplianceAudit:Read",
  "ComplianceAudit:Update",
  "ComplianceQuestionnaire:Create",
  "ComplianceQuestionnaire:Delete",
  "ComplianceQuestionnaire:Read",
  "ComplianceQuestionnaire:Update",
  "Condition:Create",
  "Condition:Delete",
  "Condition:Read",
  "Condition:Update",
  "Consent:Create",
  "Consent:Delete",
  "Consent:Read",
  "Consent:Update",
  "Contract:Create",
  "Contract:Delete",
  "Contract:Read",
  "Contract:Update",
  "Coverage:Create",
  "Coverage:Delete",
  "Coverage:Read",
  "Coverage:Update",
  "Dialogue:Create",
  "Dialogue:Delete",
  "Dialogue:Read",
  "Dialogue:Update",
  "District:Create",
  "District:Delete",
  "District:Read",
  "District:Update",
  "DocumentReference:Create",
  "DocumentReference:Delete",
  "DocumentReference:Read",
  "DocumentReference:Update",
  "DocumentTransmission:Create",
  "DocumentTransmission:Delete",
  "DocumentTransmission:Read",
  "DocumentTransmission:Update",
  "Encounter:Create",
  "Encounter:Delete",
  "Encounter:Read",
  "Encounter:Update",
  "EpisodeOfCare:Create",
  "EpisodeOfCare:Delete",
  "EpisodeOfCare:Read",
  "EpisodeOfCare:Update",
  "Form:Read",
  "GrantSet:Read",
  "Group:Create",
  "Group:Delete",
  "Group:Read",
  "Group:Update",
  "GroupNoteDefinition:Create",
  "GroupNoteDefinition:Delete",
  "GroupNoteDefinition:Read",
  "GroupNoteDefinition:Update",
  "HealthcareService:Create",
  "HealthcareService:Delete",
  "HealthcareService:Read",
  "HealthcareService:Update",
  "Itinerary:Create",
  "Itinerary:Delete",
  "Itinerary:Read",
  "Itinerary:Update",
  "KeyVault:Create",
  "KeyVault:Delete",
  "KeyVault:Read",
  "KeyVault:Update",
  "Listing:Create",
  "Listing:Delete",
  "Listing:Read",
  "Listing:Update",
  "Location:Create",
  "Location:Delete",
  "Location:Read",
  "Location:Update",
  "Media:Create",
  "Media:Delete",
  "Media:Read",
  "Media:Update",
  "Notation:Create",
  "Notation:Delete",
  "Notation:Read",
  "Notation:Update",
  "Note:Create",
  "Note:Delete",
  "Note:Read",
  "Note:Update",
  "NoteAudit:Create",
  "NoteAudit:Delete",
  "NoteAudit:Read",
  "NoteAudit:Update",
  "NoteDefinition:Create",
  "NoteDefinition:Delete",
  "NoteDefinition:Read",
  "NoteDefinition:Update",
  "NoteRule:Create",
  "NoteRule:Delete",
  "NoteRule:Read",
  "NoteRule:Update",
  "NoteSectionForm:Create",
  "NoteSectionForm:Delete",
  "NoteSectionForm:Read",
  "NoteSectionForm:Update",
  "NoteTemplate:Create",
  "NoteTemplate:Delete",
  "NoteTemplate:Read",
  "NoteTemplate:Update",
  "Notification:Create",
  "Notification:Delete",
  "Notification:Read",
  "Notification:Update",
  "ObservableRoutines:Create",
  "ObservableRoutines:Delete",
  "ObservableRoutines:Read",
  "ObservableRoutines:Update",
  "Observation:Create",
  "Observation:Delete",
  "Observation:Read",
  "Observation:Update",
  "Organization:Create",
  "Organization:Delete",
  "Organization:Read",
  "Organization:Update",
  "Packet:Create",
  "Packet:Delete",
  "Packet:Read",
  "Packet:Update",
  "PacketResponse:Create",
  "PacketResponse:Delete",
  "PacketResponse:Read",
  "PacketResponse:Update",
  "Patient:Create",
  "Patient:Delete",
  "Patient:Read",
  "Patient:Update",
  "PersonalPreferences:Create",
  "PersonalPreferences:Delete",
  "PersonalPreferences:Read",
  "PersonalPreferences:Update",
  "PersonalSignature:Create",
  "PersonalSignature:Delete",
  "PersonalSignature:Read",
  "PersonalSignature:Update",
  "PracticePreferences:Create",
  "PracticePreferences:Delete",
  "PracticePreferences:Read",
  "PracticePreferences:Update",
  "Practitioner:Create",
  "Practitioner:Delete",
  "Practitioner:Read",
  "Practitioner:Update",
  "Program:Create",
  "Program:Delete",
  "Program:Read",
  "Program:Update",
  "Questionnaire:Create",
  "Questionnaire:Delete",
  "Questionnaire:Read",
  "Questionnaire:Update",
  "QuestionnaireResponse:Create",
  "QuestionnaireResponse:Delete",
  "QuestionnaireResponse:Read",
  "QuestionnaireResponse:Update",
  "RelatedPerson:Create",
  "RelatedPerson:Delete",
  "RelatedPerson:Read",
  "RelatedPerson:Update",
  "ReportCriteria:Create",
  "ReportCriteria:Delete",
  "ReportCriteria:Read",
  "ReportCriteria:Update",
  "ReportDelivery:Create",
  "ReportDelivery:Delete",
  "ReportDelivery:Read",
  "ReportDelivery:Update",
  "Schedule:Create",
  "Schedule:Delete",
  "Schedule:Read",
  "Schedule:Update",
  "Scribble:Create",
  "Scribble:Delete",
  "Scribble:Read",
  "Scribble:Update",
  "Script:Create",
  "Script:Delete",
  "Script:Read",
  "Script:Update",
  "SecurityRole:Create",
  "SecurityRole:Delete",
  "SecurityRole:Read",
  "SecurityRole:Update",
  "SecurityToken:Create",
  "SecurityToken:Delete",
  "SecurityToken:Read",
  "SecurityToken:Update",
  "SharedScribble:Create",
  "SharedScribble:Delete",
  "SharedScribble:Read",
  "SharedScribble:Update",
  "SignatureConfiguration:Create",
  "SignatureConfiguration:Delete",
  "SignatureConfiguration:Read",
  "SignatureConfiguration:Update",
  "Snippet:Create",
  "Snippet:Delete",
  "Snippet:Read",
  "Snippet:Update",
  "SnippetTemplate:Create",
  "SnippetTemplate:Delete",
  "SnippetTemplate:Read",
  "SnippetTemplate:Update",
  "SpeechRule:Create",
  "SpeechRule:Delete",
  "SpeechRule:Read",
  "SpeechRule:Update",
  "SpeechRuleTemplate:Create",
  "SpeechRuleTemplate:Delete",
  "SpeechRuleTemplate:Read",
  "SpeechRuleTemplate:Update",
  "TextExpansion:Create",
  "TextExpansion:Delete",
  "TextExpansion:Read",
  "TextExpansion:Update",
  "TextExpansionTemplate:Create",
  "TextExpansionTemplate:Delete",
  "TextExpansionTemplate:Read",
  "TextExpansionTemplate:Update",
  "Transcription:Create",
  "Transcription:Delete",
  "Transcription:Read",
  "Transcription:Update"
];
const securityRoleIds = {
  "Patient": "01D6XWPP3BEWC008YYHVA19NCJ",
  "Practitioner": "01DG5TFX733HP7C5P8V88XHJRW",
  "Bells": "01EGTX131TJEQME0VRQRX0H1DJ",
  "Bells Unity": "01J0KVVY453EKTH3AQN1M4HKFX",
  "Supervisor": "01GQ06HMARA0J2PBW9FXT420RX",
  "Administrative": "01D6XVBQJKNJW0MQM1FJFY3DAD",
  "Quality Assurance": "01JRK66C591J5WSW790JZ9MXKN",
  "Case Management": "01DJTWP0KC578WW1J538SCH0BZ",
  "Non-Prescribing Clinician": "01D6XVBM5M2EB25AF2BCRZB9ND",
  "Prescribing Clinician": "01DJTWS1P6GAHHKENWAHMWEQVH",
  "Sandbox": "01E1CSGZJ9GRBECSS78AZ0DWC7",
  "Support": "01GTFCGQEF79XKXPX12Z1648NC",
  "AssociateRoleSysAdmin": "01D7ZR51KTRRQF79MWH85AK4KX",
  "AssociateRoleAdministrator": "01D7ZR57BZ3B0908D0AE3T49Z5",
  "AssociateRoleMarketing": "01DV6FMBJG56BB2GE5TVREKS68",
  "AssociateRoleDeveloper": "01D7ZR5BGWPR7BKYP41AJMSFJ4",
  "AssociateRoleImplementation": "01D7ZR5ZTZ0WV90NEX0E3322S7",
  "AssociateRoleProduct": "01E44MV2BPD47N6VN0RAXGCBFK",
  "AssociateRoleSupport": "01D7ZR6DW1TFJAKPQCPV9JY3G2",
  "AssociateRoleUser": "01D7ZR6S3TSX6GVYQ2STAKEMJ3",
  "Services": "01D8S75V6HCC9VJVBAZKW6845P",
  "Automation": "01EEBH7S1BWPTFZKGCFNF35WSE",
  "Scribe": "01HW84ZWXZ6JNT25WV6S7V18ZM"
};
const systems = {
  ...systems$1,
  ...ehrSystems
};
class AsyncQueue {
  constructor() {
    __publicField(this, "timers", /* @__PURE__ */ new Map());
  }
  /**
   * Cancels all queued items.  Can be used to cancel all async operations that were still pending.
   * May be useful for unit test scenarios.
   */
  cancelAll() {
    this.timers.forEach((node) => node.cancel());
  }
  cancel(key) {
    var _a;
    (_a = this.timers.get(key)) == null ? void 0 : _a.cancel();
  }
  /**
   * Forces all pending actions to start immediately and waits for completion.  The returned Promise
   * resolves immediately if there are no outstanding actions queued.  Will not wait for new actions
   * that were queued after the method is called.
   */
  async resolveAllQueued() {
    await Promise.all(Array.from(this.timers.values()).map((node) => {
      node.start(0);
      return node.promise;
    }));
  }
  /**
   * Waits until all currently pending actions have been completed.  The returned Promise
   * resolves immediately if there are no outstanding actions queued.  Will not wait for new actions
   * that were queued after the method is called.
   */
  async waitForAllQueued() {
    await Promise.all(Array.from(this.timers.values()).map((node) => node.promise));
  }
  /**
   * Forces any pending action to start immediately and waits for completion.  The returned Promise resolves
   * immediately if there is no outstanding action queued.  Will not wait for new actions
   * that are queued for this key after the method is called.
   */
  async resolveQueued(key) {
    const timer = this.timers.get(key);
    if (timer) {
      timer.start(0);
      await timer.promise;
    }
  }
  /**
   * Waits for currently pending action to be complete.  The returned Promise resolves
   * immediately if there is no outstanding action queued.  Will not wait for new actions
   * that are queued for this key after the method is called.
   */
  async waitForQueued(key) {
    var _a;
    await ((_a = this.timers.get(key)) == null ? void 0 : _a.promise);
  }
  queue(key, ...args) {
    let action;
    let queueDelay = 0;
    const firstArg = args.shift();
    if (typeof firstArg === "number") {
      queueDelay = firstArg;
      action = args.shift();
    } else {
      action = firstArg;
    }
    const existing = this.timers.get(key);
    if (existing && !existing.running) {
      existing.cancel();
    }
    let resolve = () => {
    };
    const node = {
      running: false,
      cancelled: false,
      start: () => {
      },
      cancel: () => {
      },
      promise: new Promise((r) => resolve = r)
    };
    const clearTimer = () => {
      if (node.timerId) {
        clearTimeout(node.timerId);
        node.timerId = void 0;
      }
    };
    const finalize = () => {
      node.running = false;
      clearTimer();
      if (node.next && !node.cancelled) {
        this.timers.set(key, node.next);
        node.next.start();
      } else {
        this.timers.delete(key);
      }
      resolve();
    };
    node.cancel = () => {
      node.cancelled = true;
      finalize();
    };
    const handler = async () => {
      try {
        if (!node.cancelled) {
          node.running = true;
          await action(...args);
        }
      } finally {
        finalize();
      }
    };
    node.start = (localDelayMs) => {
      if (node.running) {
        return;
      }
      clearTimer();
      const delay2 = localDelayMs ?? queueDelay;
      if (!delay2) {
        handler();
      } else {
        node.timerId = setTimeout(handler, delay2);
      }
    };
    if (existing && existing.running) {
      existing.next = node;
    } else {
      this.timers.set(key, node);
      node.start();
    }
  }
}
function cyrb53(str, seed = 0) {
  let h1 = 3735928559 ^ seed, h2 = 1103547991 ^ seed;
  for (let i = 0, ch; i < str.length; i++) {
    ch = str.charCodeAt(i);
    h1 = Math.imul(h1 ^ ch, 2654435761);
    h2 = Math.imul(h2 ^ ch, 1597334677);
  }
  h1 = Math.imul(h1 ^ h1 >>> 16, 2246822507) ^ Math.imul(h2 ^ h2 >>> 13, 3266489909);
  h2 = Math.imul(h2 ^ h2 >>> 16, 2246822507) ^ Math.imul(h1 ^ h1 >>> 13, 3266489909);
  return 4294967296 * (2097151 & h2) + (h1 >>> 0);
}
/*!
 * https://github.com/Starcounter-Jack/JSON-Patch
 * (c) 2017-2022 Joachim Wester
 * MIT licensed
 */
var __extends = /* @__PURE__ */ function() {
  var extendStatics = function(d, b) {
    extendStatics = Object.setPrototypeOf || { __proto__: [] } instanceof Array && function(d2, b2) {
      d2.__proto__ = b2;
    } || function(d2, b2) {
      for (var p in b2) if (b2.hasOwnProperty(p)) d2[p] = b2[p];
    };
    return extendStatics(d, b);
  };
  return function(d, b) {
    extendStatics(d, b);
    function __() {
      this.constructor = d;
    }
    d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
  };
}();
var _hasOwnProperty = Object.prototype.hasOwnProperty;
function hasOwnProperty(obj, key) {
  return _hasOwnProperty.call(obj, key);
}
function _objectKeys(obj) {
  if (Array.isArray(obj)) {
    var keys_1 = new Array(obj.length);
    for (var k = 0; k < keys_1.length; k++) {
      keys_1[k] = "" + k;
    }
    return keys_1;
  }
  if (Object.keys) {
    return Object.keys(obj);
  }
  var keys = [];
  for (var i in obj) {
    if (hasOwnProperty(obj, i)) {
      keys.push(i);
    }
  }
  return keys;
}
function _deepClone(obj) {
  switch (typeof obj) {
    case "object":
      return JSON.parse(JSON.stringify(obj));
    //Faster than ES5 clone - http://jsperf.com/deep-cloning-of-objects/5
    case "undefined":
      return null;
    //this is how JSON.stringify behaves for array items
    default:
      return obj;
  }
}
function isInteger(str) {
  var i = 0;
  var len = str.length;
  var charCode;
  while (i < len) {
    charCode = str.charCodeAt(i);
    if (charCode >= 48 && charCode <= 57) {
      i++;
      continue;
    }
    return false;
  }
  return true;
}
function escapePathComponent(path) {
  if (path.indexOf("/") === -1 && path.indexOf("~") === -1)
    return path;
  return path.replace(/~/g, "~0").replace(/\//g, "~1");
}
function unescapePathComponent(path) {
  return path.replace(/~1/g, "/").replace(/~0/g, "~");
}
function hasUndefined(obj) {
  if (obj === void 0) {
    return true;
  }
  if (obj) {
    if (Array.isArray(obj)) {
      for (var i_1 = 0, len = obj.length; i_1 < len; i_1++) {
        if (hasUndefined(obj[i_1])) {
          return true;
        }
      }
    } else if (typeof obj === "object") {
      var objKeys = _objectKeys(obj);
      var objKeysLength = objKeys.length;
      for (var i = 0; i < objKeysLength; i++) {
        if (hasUndefined(obj[objKeys[i]])) {
          return true;
        }
      }
    }
  }
  return false;
}
function patchErrorMessageFormatter(message, args) {
  var messageParts = [message];
  for (var key in args) {
    var value = typeof args[key] === "object" ? JSON.stringify(args[key], null, 2) : args[key];
    if (typeof value !== "undefined") {
      messageParts.push(key + ": " + value);
    }
  }
  return messageParts.join("\n");
}
var PatchError = (
  /** @class */
  function(_super) {
    __extends(PatchError2, _super);
    function PatchError2(message, name, index, operation, tree) {
      var _newTarget = this.constructor;
      var _this = _super.call(this, patchErrorMessageFormatter(message, { name, index, operation, tree })) || this;
      _this.name = name;
      _this.index = index;
      _this.operation = operation;
      _this.tree = tree;
      Object.setPrototypeOf(_this, _newTarget.prototype);
      _this.message = patchErrorMessageFormatter(message, { name, index, operation, tree });
      return _this;
    }
    return PatchError2;
  }(Error)
);
var JsonPatchError = PatchError;
var deepClone = _deepClone;
var objOps = {
  add: function(obj, key, document) {
    obj[key] = this.value;
    return { newDocument: document };
  },
  remove: function(obj, key, document) {
    var removed = obj[key];
    delete obj[key];
    return { newDocument: document, removed };
  },
  replace: function(obj, key, document) {
    var removed = obj[key];
    obj[key] = this.value;
    return { newDocument: document, removed };
  },
  move: function(obj, key, document) {
    var removed = getValueByPointer(document, this.path);
    if (removed) {
      removed = _deepClone(removed);
    }
    var originalValue = applyOperation(document, { op: "remove", path: this.from }).removed;
    applyOperation(document, { op: "add", path: this.path, value: originalValue });
    return { newDocument: document, removed };
  },
  copy: function(obj, key, document) {
    var valueToCopy = getValueByPointer(document, this.from);
    applyOperation(document, { op: "add", path: this.path, value: _deepClone(valueToCopy) });
    return { newDocument: document };
  },
  test: function(obj, key, document) {
    return { newDocument: document, test: _areEquals(obj[key], this.value) };
  },
  _get: function(obj, key, document) {
    this.value = obj[key];
    return { newDocument: document };
  }
};
var arrOps = {
  add: function(arr, i, document) {
    if (isInteger(i)) {
      arr.splice(i, 0, this.value);
    } else {
      arr[i] = this.value;
    }
    return { newDocument: document, index: i };
  },
  remove: function(arr, i, document) {
    var removedList = arr.splice(i, 1);
    return { newDocument: document, removed: removedList[0] };
  },
  replace: function(arr, i, document) {
    var removed = arr[i];
    arr[i] = this.value;
    return { newDocument: document, removed };
  },
  move: objOps.move,
  copy: objOps.copy,
  test: objOps.test,
  _get: objOps._get
};
function getValueByPointer(document, pointer) {
  if (pointer == "") {
    return document;
  }
  var getOriginalDestination = { op: "_get", path: pointer };
  applyOperation(document, getOriginalDestination);
  return getOriginalDestination.value;
}
function applyOperation(document, operation, validateOperation, mutateDocument, banPrototypeModifications, index) {
  if (validateOperation === void 0) {
    validateOperation = false;
  }
  if (mutateDocument === void 0) {
    mutateDocument = true;
  }
  if (banPrototypeModifications === void 0) {
    banPrototypeModifications = true;
  }
  if (index === void 0) {
    index = 0;
  }
  if (validateOperation) {
    if (typeof validateOperation == "function") {
      validateOperation(operation, 0, document, operation.path);
    } else {
      validator(operation, 0);
    }
  }
  if (operation.path === "") {
    var returnValue = { newDocument: document };
    if (operation.op === "add") {
      returnValue.newDocument = operation.value;
      return returnValue;
    } else if (operation.op === "replace") {
      returnValue.newDocument = operation.value;
      returnValue.removed = document;
      return returnValue;
    } else if (operation.op === "move" || operation.op === "copy") {
      returnValue.newDocument = getValueByPointer(document, operation.from);
      if (operation.op === "move") {
        returnValue.removed = document;
      }
      return returnValue;
    } else if (operation.op === "test") {
      returnValue.test = _areEquals(document, operation.value);
      if (returnValue.test === false) {
        throw new JsonPatchError("Test operation failed", "TEST_OPERATION_FAILED", index, operation, document);
      }
      returnValue.newDocument = document;
      return returnValue;
    } else if (operation.op === "remove") {
      returnValue.removed = document;
      returnValue.newDocument = null;
      return returnValue;
    } else if (operation.op === "_get") {
      operation.value = document;
      return returnValue;
    } else {
      if (validateOperation) {
        throw new JsonPatchError("Operation `op` property is not one of operations defined in RFC-6902", "OPERATION_OP_INVALID", index, operation, document);
      } else {
        return returnValue;
      }
    }
  } else {
    if (!mutateDocument) {
      document = _deepClone(document);
    }
    var path = operation.path || "";
    var keys = path.split("/");
    var obj = document;
    var t = 1;
    var len = keys.length;
    var existingPathFragment = void 0;
    var key = void 0;
    var validateFunction = void 0;
    if (typeof validateOperation == "function") {
      validateFunction = validateOperation;
    } else {
      validateFunction = validator;
    }
    while (true) {
      key = keys[t];
      if (key && key.indexOf("~") != -1) {
        key = unescapePathComponent(key);
      }
      if (banPrototypeModifications && (key == "__proto__" || key == "prototype" && t > 0 && keys[t - 1] == "constructor")) {
        throw new TypeError("JSON-Patch: modifying `__proto__` or `constructor/prototype` prop is banned for security reasons, if this was on purpose, please set `banPrototypeModifications` flag false and pass it to this function. More info in fast-json-patch README");
      }
      if (validateOperation) {
        if (existingPathFragment === void 0) {
          if (obj[key] === void 0) {
            existingPathFragment = keys.slice(0, t).join("/");
          } else if (t == len - 1) {
            existingPathFragment = operation.path;
          }
          if (existingPathFragment !== void 0) {
            validateFunction(operation, 0, document, existingPathFragment);
          }
        }
      }
      t++;
      if (Array.isArray(obj)) {
        if (key === "-") {
          key = obj.length;
        } else {
          if (validateOperation && !isInteger(key)) {
            throw new JsonPatchError("Expected an unsigned base-10 integer value, making the new referenced value the array element with the zero-based index", "OPERATION_PATH_ILLEGAL_ARRAY_INDEX", index, operation, document);
          } else if (isInteger(key)) {
            key = ~~key;
          }
        }
        if (t >= len) {
          if (validateOperation && operation.op === "add" && key > obj.length) {
            throw new JsonPatchError("The specified index MUST NOT be greater than the number of elements in the array", "OPERATION_VALUE_OUT_OF_BOUNDS", index, operation, document);
          }
          var returnValue = arrOps[operation.op].call(operation, obj, key, document);
          if (returnValue.test === false) {
            throw new JsonPatchError("Test operation failed", "TEST_OPERATION_FAILED", index, operation, document);
          }
          return returnValue;
        }
      } else {
        if (t >= len) {
          var returnValue = objOps[operation.op].call(operation, obj, key, document);
          if (returnValue.test === false) {
            throw new JsonPatchError("Test operation failed", "TEST_OPERATION_FAILED", index, operation, document);
          }
          return returnValue;
        }
      }
      obj = obj[key];
      if (validateOperation && t < len && (!obj || typeof obj !== "object")) {
        throw new JsonPatchError("Cannot perform operation at the desired path", "OPERATION_PATH_UNRESOLVABLE", index, operation, document);
      }
    }
  }
}
function applyPatch(document, patch, validateOperation, mutateDocument, banPrototypeModifications) {
  if (mutateDocument === void 0) {
    mutateDocument = true;
  }
  if (banPrototypeModifications === void 0) {
    banPrototypeModifications = true;
  }
  if (validateOperation) {
    if (!Array.isArray(patch)) {
      throw new JsonPatchError("Patch sequence must be an array", "SEQUENCE_NOT_AN_ARRAY");
    }
  }
  if (!mutateDocument) {
    document = _deepClone(document);
  }
  var results = new Array(patch.length);
  for (var i = 0, length_1 = patch.length; i < length_1; i++) {
    results[i] = applyOperation(document, patch[i], validateOperation, true, banPrototypeModifications, i);
    document = results[i].newDocument;
  }
  results.newDocument = document;
  return results;
}
function applyReducer(document, operation, index) {
  var operationResult = applyOperation(document, operation);
  if (operationResult.test === false) {
    throw new JsonPatchError("Test operation failed", "TEST_OPERATION_FAILED", index, operation, document);
  }
  return operationResult.newDocument;
}
function validator(operation, index, document, existingPathFragment) {
  if (typeof operation !== "object" || operation === null || Array.isArray(operation)) {
    throw new JsonPatchError("Operation is not an object", "OPERATION_NOT_AN_OBJECT", index, operation, document);
  } else if (!objOps[operation.op]) {
    throw new JsonPatchError("Operation `op` property is not one of operations defined in RFC-6902", "OPERATION_OP_INVALID", index, operation, document);
  } else if (typeof operation.path !== "string") {
    throw new JsonPatchError("Operation `path` property is not a string", "OPERATION_PATH_INVALID", index, operation, document);
  } else if (operation.path.indexOf("/") !== 0 && operation.path.length > 0) {
    throw new JsonPatchError('Operation `path` property must start with "/"', "OPERATION_PATH_INVALID", index, operation, document);
  } else if ((operation.op === "move" || operation.op === "copy") && typeof operation.from !== "string") {
    throw new JsonPatchError("Operation `from` property is not present (applicable in `move` and `copy` operations)", "OPERATION_FROM_REQUIRED", index, operation, document);
  } else if ((operation.op === "add" || operation.op === "replace" || operation.op === "test") && operation.value === void 0) {
    throw new JsonPatchError("Operation `value` property is not present (applicable in `add`, `replace` and `test` operations)", "OPERATION_VALUE_REQUIRED", index, operation, document);
  } else if ((operation.op === "add" || operation.op === "replace" || operation.op === "test") && hasUndefined(operation.value)) {
    throw new JsonPatchError("Operation `value` property is not present (applicable in `add`, `replace` and `test` operations)", "OPERATION_VALUE_CANNOT_CONTAIN_UNDEFINED", index, operation, document);
  } else if (document) {
    if (operation.op == "add") {
      var pathLen = operation.path.split("/").length;
      var existingPathLen = existingPathFragment.split("/").length;
      if (pathLen !== existingPathLen + 1 && pathLen !== existingPathLen) {
        throw new JsonPatchError("Cannot perform an `add` operation at the desired path", "OPERATION_PATH_CANNOT_ADD", index, operation, document);
      }
    } else if (operation.op === "replace" || operation.op === "remove" || operation.op === "_get") {
      if (operation.path !== existingPathFragment) {
        throw new JsonPatchError("Cannot perform the operation at a path that does not exist", "OPERATION_PATH_UNRESOLVABLE", index, operation, document);
      }
    } else if (operation.op === "move" || operation.op === "copy") {
      var existingValue = { op: "_get", path: operation.from, value: void 0 };
      var error = validate([existingValue], document);
      if (error && error.name === "OPERATION_PATH_UNRESOLVABLE") {
        throw new JsonPatchError("Cannot perform the operation from a path that does not exist", "OPERATION_FROM_UNRESOLVABLE", index, operation, document);
      }
    }
  }
}
function validate(sequence, document, externalValidator) {
  try {
    if (!Array.isArray(sequence)) {
      throw new JsonPatchError("Patch sequence must be an array", "SEQUENCE_NOT_AN_ARRAY");
    }
    if (document) {
      applyPatch(_deepClone(document), _deepClone(sequence), externalValidator || true);
    } else {
      externalValidator = externalValidator || validator;
      for (var i = 0; i < sequence.length; i++) {
        externalValidator(sequence[i], i, document, void 0);
      }
    }
  } catch (e) {
    if (e instanceof JsonPatchError) {
      return e;
    } else {
      throw e;
    }
  }
}
function _areEquals(a, b) {
  if (a === b)
    return true;
  if (a && b && typeof a == "object" && typeof b == "object") {
    var arrA = Array.isArray(a), arrB = Array.isArray(b), i, length, key;
    if (arrA && arrB) {
      length = a.length;
      if (length != b.length)
        return false;
      for (i = length; i-- !== 0; )
        if (!_areEquals(a[i], b[i]))
          return false;
      return true;
    }
    if (arrA != arrB)
      return false;
    var keys = Object.keys(a);
    length = keys.length;
    if (length !== Object.keys(b).length)
      return false;
    for (i = length; i-- !== 0; )
      if (!b.hasOwnProperty(keys[i]))
        return false;
    for (i = length; i-- !== 0; ) {
      key = keys[i];
      if (!_areEquals(a[key], b[key]))
        return false;
    }
    return true;
  }
  return a !== a && b !== b;
}
const core = /* @__PURE__ */ Object.freeze(/* @__PURE__ */ Object.defineProperty({
  __proto__: null,
  JsonPatchError,
  _areEquals,
  applyOperation,
  applyPatch,
  applyReducer,
  deepClone,
  getValueByPointer,
  validate,
  validator
}, Symbol.toStringTag, { value: "Module" }));
/*!
 * https://github.com/Starcounter-Jack/JSON-Patch
 * (c) 2017-2021 Joachim Wester
 * MIT license
 */
var beforeDict = /* @__PURE__ */ new WeakMap();
var Mirror = (
  /** @class */
  /* @__PURE__ */ function() {
    function Mirror2(obj) {
      this.observers = /* @__PURE__ */ new Map();
      this.obj = obj;
    }
    return Mirror2;
  }()
);
var ObserverInfo = (
  /** @class */
  /* @__PURE__ */ function() {
    function ObserverInfo2(callback, observer) {
      this.callback = callback;
      this.observer = observer;
    }
    return ObserverInfo2;
  }()
);
function getMirror(obj) {
  return beforeDict.get(obj);
}
function getObserverFromMirror(mirror, callback) {
  return mirror.observers.get(callback);
}
function removeObserverFromMirror(mirror, observer) {
  mirror.observers.delete(observer.callback);
}
function unobserve(root, observer) {
  observer.unobserve();
}
function observe(obj, callback) {
  var patches = [];
  var observer;
  var mirror = getMirror(obj);
  if (!mirror) {
    mirror = new Mirror(obj);
    beforeDict.set(obj, mirror);
  } else {
    var observerInfo = getObserverFromMirror(mirror, callback);
    observer = observerInfo && observerInfo.observer;
  }
  if (observer) {
    return observer;
  }
  observer = {};
  mirror.value = _deepClone(obj);
  if (callback) {
    observer.callback = callback;
    observer.next = null;
    var dirtyCheck = function() {
      generate(observer);
    };
    var fastCheck = function() {
      clearTimeout(observer.next);
      observer.next = setTimeout(dirtyCheck);
    };
    if (typeof window !== "undefined") {
      window.addEventListener("mouseup", fastCheck);
      window.addEventListener("keyup", fastCheck);
      window.addEventListener("mousedown", fastCheck);
      window.addEventListener("keydown", fastCheck);
      window.addEventListener("change", fastCheck);
    }
  }
  observer.patches = patches;
  observer.object = obj;
  observer.unobserve = function() {
    generate(observer);
    clearTimeout(observer.next);
    removeObserverFromMirror(mirror, observer);
    if (typeof window !== "undefined") {
      window.removeEventListener("mouseup", fastCheck);
      window.removeEventListener("keyup", fastCheck);
      window.removeEventListener("mousedown", fastCheck);
      window.removeEventListener("keydown", fastCheck);
      window.removeEventListener("change", fastCheck);
    }
  };
  mirror.observers.set(callback, new ObserverInfo(callback, observer));
  return observer;
}
function generate(observer, invertible) {
  if (invertible === void 0) {
    invertible = false;
  }
  var mirror = beforeDict.get(observer.object);
  _generate(mirror.value, observer.object, observer.patches, "", invertible);
  if (observer.patches.length) {
    applyPatch(mirror.value, observer.patches);
  }
  var temp = observer.patches;
  if (temp.length > 0) {
    observer.patches = [];
    if (observer.callback) {
      observer.callback(temp);
    }
  }
  return temp;
}
function _generate(mirror, obj, patches, path, invertible) {
  if (obj === mirror) {
    return;
  }
  if (typeof obj.toJSON === "function") {
    obj = obj.toJSON();
  }
  var newKeys = _objectKeys(obj);
  var oldKeys = _objectKeys(mirror);
  var deleted = false;
  for (var t = oldKeys.length - 1; t >= 0; t--) {
    var key = oldKeys[t];
    var oldVal = mirror[key];
    if (hasOwnProperty(obj, key) && !(obj[key] === void 0 && oldVal !== void 0 && Array.isArray(obj) === false)) {
      var newVal = obj[key];
      if (typeof oldVal == "object" && oldVal != null && typeof newVal == "object" && newVal != null && Array.isArray(oldVal) === Array.isArray(newVal)) {
        _generate(oldVal, newVal, patches, path + "/" + escapePathComponent(key), invertible);
      } else {
        if (oldVal !== newVal) {
          if (invertible) {
            patches.push({ op: "test", path: path + "/" + escapePathComponent(key), value: _deepClone(oldVal) });
          }
          patches.push({ op: "replace", path: path + "/" + escapePathComponent(key), value: _deepClone(newVal) });
        }
      }
    } else if (Array.isArray(mirror) === Array.isArray(obj)) {
      if (invertible) {
        patches.push({ op: "test", path: path + "/" + escapePathComponent(key), value: _deepClone(oldVal) });
      }
      patches.push({ op: "remove", path: path + "/" + escapePathComponent(key) });
      deleted = true;
    } else {
      if (invertible) {
        patches.push({ op: "test", path, value: mirror });
      }
      patches.push({ op: "replace", path, value: obj });
    }
  }
  if (!deleted && newKeys.length == oldKeys.length) {
    return;
  }
  for (var t = 0; t < newKeys.length; t++) {
    var key = newKeys[t];
    if (!hasOwnProperty(mirror, key) && obj[key] !== void 0) {
      patches.push({ op: "add", path: path + "/" + escapePathComponent(key), value: _deepClone(obj[key]) });
    }
  }
}
function compare(tree1, tree2, invertible) {
  if (invertible === void 0) {
    invertible = false;
  }
  var patches = [];
  _generate(tree1, tree2, patches, "", invertible);
  return patches;
}
const duplex = /* @__PURE__ */ Object.freeze(/* @__PURE__ */ Object.defineProperty({
  __proto__: null,
  compare,
  generate,
  observe,
  unobserve
}, Symbol.toStringTag, { value: "Module" }));
Object.assign({}, core, duplex, {
  JsonPatchError: PatchError,
  deepClone: _deepClone,
  escapePathComponent,
  unescapePathComponent
});
function retrocycle(obj) {
  var catalog = {};
  catalogObject(obj, catalog);
  resolveReferences(obj, catalog);
}
function catalogObject(obj, catalog) {
  if (obj && typeof obj === "object") {
    if (Array.isArray(obj)) {
      let i;
      for (i = 0; i < obj.length; i += 1) {
        catalogObject(obj[i], catalog);
      }
    } else {
      if ("$id" in obj) {
        const id = obj.$id;
        if (typeof id === "string") {
          catalog[id] = obj;
        }
        delete obj.$id;
      }
      let name;
      for (name in obj) {
        if (typeof obj[name] === "object") {
          catalogObject(obj[name], catalog);
        }
      }
    }
  }
}
function resolveReferences(obj, catalog) {
  let i, item, name, id;
  if (obj && typeof obj === "object") {
    if (Object.prototype.toString.apply(obj) === "[object Array]") {
      for (i = 0; i < obj.length; i += 1) {
        item = obj[i];
        if (item && typeof item === "object") {
          id = item.$ref;
          if (typeof id === "string") {
            obj[i] = catalog[id];
          } else {
            resolveReferences(item, catalog);
          }
        }
      }
    } else {
      for (name in obj) {
        if (typeof obj[name] === "object") {
          item = obj[name];
          if (item) {
            id = item.$ref;
            if (typeof id === "string") {
              obj[name] = catalog[id];
            } else {
              resolveReferences(item, catalog);
            }
          }
        }
      }
    }
  }
}
function prepForSave(resourceType, obj) {
  const clone2 = JSON.parse(safeStringify(obj));
  trimExpandedReferences$1(resourceType, clone2);
  trimNestedResources(clone2);
  return clone2;
}
var chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/";
var lookup = typeof Uint8Array === "undefined" ? [] : new Uint8Array(256);
for (var i = 0; i < chars.length; i++) {
  lookup[chars.charCodeAt(i)] = i;
}
var encode = function(arraybuffer) {
  var bytes = new Uint8Array(arraybuffer), i, len = bytes.length, base64 = "";
  for (i = 0; i < len; i += 3) {
    base64 += chars[bytes[i] >> 2];
    base64 += chars[(bytes[i] & 3) << 4 | bytes[i + 1] >> 4];
    base64 += chars[(bytes[i + 1] & 15) << 2 | bytes[i + 2] >> 6];
    base64 += chars[bytes[i + 2] & 63];
  }
  if (len % 3 === 2) {
    base64 = base64.substring(0, base64.length - 1) + "=";
  } else if (len % 3 === 1) {
    base64 = base64.substring(0, base64.length - 2) + "==";
  }
  return base64;
};
var decode = function(base64) {
  var bufferLength = base64.length * 0.75, len = base64.length, i, p = 0, encoded1, encoded2, encoded3, encoded4;
  if (base64[base64.length - 1] === "=") {
    bufferLength--;
    if (base64[base64.length - 2] === "=") {
      bufferLength--;
    }
  }
  var arraybuffer = new ArrayBuffer(bufferLength), bytes = new Uint8Array(arraybuffer);
  for (i = 0; i < len; i += 4) {
    encoded1 = lookup[base64.charCodeAt(i)];
    encoded2 = lookup[base64.charCodeAt(i + 1)];
    encoded3 = lookup[base64.charCodeAt(i + 2)];
    encoded4 = lookup[base64.charCodeAt(i + 3)];
    bytes[p++] = encoded1 << 2 | encoded2 >> 4;
    bytes[p++] = (encoded2 & 15) << 4 | encoded3 >> 2;
    bytes[p++] = (encoded3 & 3) << 6 | encoded4 & 63;
  }
  return arraybuffer;
};
function convertBase64ToBuffer(content) {
  if (content.includes(",")) {
    content = content.split(",").slice(1).join();
  }
  return decode(content);
}
function convertBufferToBase64(buffer, contentType) {
  return `data:${contentType};base64,${encode(buffer)}`;
}
function groupBy(items, key) {
  return items.reduce((map, item) => {
    const keyValue = key(item);
    const bucket = map.get(keyValue) ?? [];
    bucket.push(item);
    map.set(keyValue, bucket);
    return map;
  }, /* @__PURE__ */ new Map());
}
function paginate(items, pageSize) {
  return items.reduce((acc, val, i) => {
    const idx = Math.floor(i / pageSize);
    const page = acc[idx] || (acc[idx] = []);
    page.push(val);
    return acc;
  }, []);
}
function flatMap(items, map) {
  return items.reduce((acc, item) => acc.concat(map(item)), []);
}
function whereNotNull(items) {
  const results = [];
  items.forEach((item) => {
    if (item) {
      results.push(item);
    }
  });
  return results;
}
async function delay(ms) {
  return new Promise((resolve) => {
    setTimeout(() => resolve(), ms);
  });
}
function safeStringify(obj, replacer, indent = 0) {
  return JSON.stringify(obj, getCircularReferenceReplacer(replacer), indent);
}
function getCircularReferenceReplacer(replacer) {
  const stack = [];
  return function(key, value) {
    if (stack.length > 0) {
      const thisPos = stack.indexOf(this);
      if (~thisPos) {
        stack.splice(thisPos + 1);
      } else {
        stack.push(this);
      }
      if (~stack.indexOf(value)) {
        if (isReference(value)) {
          value = {
            ...value,
            resource: void 0
          };
        } else {
          value = void 0;
        }
      }
    } else {
      stack.push(value);
    }
    return replacer == null ? value : replacer.call(this, key, value);
  };
}
class Patch {
  static diff(previous, updated) {
    return compare(previous, updated);
  }
  static apply(document, operations) {
    applyPatch(document, operations);
  }
  static getValue(document, path) {
    return getValueByPointer(document, path);
  }
  static hasPath(document, path) {
    if (path === "" || path === "/") {
      return true;
    }
    let obj = document;
    let keys = path.split("/");
    for (let i = 1; i < keys.length; ++i) {
      if (typeof obj !== "object" || !obj) {
        return false;
      }
      const key = keys[i];
      if (!(key in obj)) {
        return false;
      }
      obj = obj[key];
    }
    return true;
  }
  static generatePatch(previous, updated) {
    var _a;
    let operations = Patch.diff(prepForSave(previous.resourceType, previous), prepForSave(updated.resourceType, updated));
    operations = removeReadOnlyPaths(updated.resourceType, operations);
    operations = removeRedundantOperations(previous, updated, operations);
    return {
      identity: {
        resourceId: updated.id,
        versionId: (_a = updated.meta) == null ? void 0 : _a.versionId,
        partition: updated.partition
      },
      operations
    };
  }
}
function getReadOnlyPaths(resourceType) {
  const paths = new Set(readOnlyPaths[resourceType]);
  paths.add("id");
  paths.add("partition");
  paths.add("display");
  paths.add("resourceType");
  paths.add("selfIdentity");
  paths.add("meta.hasTransformations");
  paths.add("meta.isDeleted");
  paths.add("meta.isHistorical");
  paths.add("meta.lastModified");
  paths.add("meta.lastModifiedBy");
  paths.add("meta.resourceSystem");
  paths.add("meta.versionId");
  return paths;
}
function patchPathToJsonPath(path) {
  path = path.replace(/\//g, ".");
  if (path.startsWith("."))
    path = path.slice(1);
  if (path.endsWith("."))
    path = path.slice(void 0, -1);
  return path;
}
function removeReadOnlyPaths(resourceType, operations) {
  const resourceReadOnlyPaths = getReadOnlyPaths(resourceType);
  return operations.filter((op) => {
    return !resourceReadOnlyPaths.has(patchPathToJsonPath(op.path));
  });
}
function removeRedundantOperations(previous, updated, operations) {
  return operations.filter((op) => {
    if (op.op === "remove") {
      const previousValue = getValueByPointer(previous, op.path);
      if (!!previousValue && Array.isArray(previousValue) && previousValue.length === 0) {
        return false;
      }
    }
    if (op.op === "add") {
      const updatedValue = getValueByPointer(updated, op.path);
      if (!!updatedValue && Array.isArray(updatedValue) && updatedValue.length === 0) {
        return false;
      }
    }
    return true;
  });
}
function resolveOperationConflicts(document, operations) {
  return operations.map((op) => {
    if (op.op === "add" && Patch.hasPath(document, op.path)) {
      return { ...op, op: "replace" };
    }
    return op;
  }).filter((op) => {
    if (op.op === "replace" && Patch.hasPath(document, op.path)) {
      const value = Patch.getValue(document, op.path);
      const encodedReplace = isObject(value) ? encodeObject(value) : value;
      const encodedValue = isObject(op.value) ? encodeObject(op.value) : op.value;
      return encodedReplace !== encodedValue;
    }
    return true;
  }).filter((op) => {
    if (op.op === "remove") {
      return Patch.hasPath(document, op.path);
    }
    return true;
  });
}
function isObject(value) {
  return typeof value === "object" && !!value;
}
function encodeObject(obj) {
  let cloned = clone(obj);
  cloned = removeExpandedReference(cloned);
  trimExpandedReferences(cloned);
  return safeStringify(cloned);
}
function clone(obj) {
  return JSON.parse(safeStringify(obj));
}
function removeExpandedReference(obj) {
  if (!isExpandedReference(obj)) {
    return obj;
  }
  return {
    id: obj.id,
    display: obj.display,
    partition: obj.partition,
    resourceType: obj.resourceType
  };
}
function trimExpandedReferences(obj) {
  for (const name in obj) {
    const value = obj[name];
    if (isObject(value)) {
      if (isExpandedReference(value)) {
        obj[name] = removeExpandedReference(value);
      } else {
        trimExpandedReferences(value);
      }
    }
  }
}
function isExpandedReference(value) {
  return value.id && !!value.resource && isObject(value.resource) && value.resource.id && (isObject(value.resource.meta) || !!value.resource.resourceType);
}
class Permissions {
  constructor(securityRoleIds2) {
    __publicField(this, "grantedPermissions", /* @__PURE__ */ new Set());
    __publicField(this, "hasPermission", (resourceType, ...permissions) => {
      return permissions.every((p) => this.grantedPermissions.has(resourceTypePermissionToIndex(resourceType, p)));
    });
    __publicField(this, "canRead", (resourceType) => this.hasPermission(resourceType, "Read"));
    __publicField(this, "canCreate", (resourceType) => this.hasPermission(resourceType, "Create"));
    __publicField(this, "canUpdate", (resourceType) => this.hasPermission(resourceType, "Update"));
    __publicField(this, "canDelete", (resourceType) => this.hasPermission(resourceType, "Delete"));
    __publicField(this, "hasFlag", (category, flag) => {
      const system = `apollo:contract-enum:${category.toLowerCase()}`;
      return this.grantedPermissions.has(createIndex(system, String(flag), "Allow"));
    });
    __publicField(this, "canGrantSet", (grantSetName) => {
      const grantSetIndex = grantSetNames.indexOf(grantSetName);
      const permissionIndexes = grantSetPermissions.get(grantSetIndex);
      if (!permissionIndexes)
        throw new Error(`GrantSet ${grantSetName} is not defined.`);
      return permissionIndexes.every((index) => {
        if (permissionIndex[index]) {
          return this.grantedPermissions.has(permissionIndex[index]);
        }
        return true;
      });
    });
    const roleIds = new Set(securityRoleIds2);
    roleIds.forEach((roleId) => {
      const grantSets = roleIdToGrantSets.get(roleId);
      if (grantSets) {
        grantSets.forEach((grantSetIndex) => {
          const permissionIndexes = grantSetPermissions.get(grantSetIndex);
          if (permissionIndexes) {
            permissionIndexes.forEach((index) => {
              if (permissionIndex[index]) {
                this.grantedPermissions.add(permissionIndex[index]);
              }
            });
          }
        });
      }
    });
  }
  can(permissionGrants) {
    const grants = Array.isArray(permissionGrants) ? permissionGrants : [permissionGrants];
    if (grants.length === 0) {
      return true;
    }
    return grants.every((neededGrant) => {
      return this.grantedPermissions.has(grantToIndex(neededGrant));
    });
  }
}
function createIndex(system, code, permission) {
  return `${system}:${code}:${permission}`;
}
function resourceTypePermissionToIndex(resourceType, permission) {
  return `${resourceType}:${permission}`;
}
function grantToIndex(grant) {
  if (grant.subject.system === systems.resourceTypes) {
    return `${grant.subject.code}:${grant.permission}`;
  }
  return createIndex(grant.subject.system, grant.subject.code, grant.permission);
}
function parseProfileClaim(claim) {
  const parts = claim.split("|");
  if (parts.length !== 3) {
    return null;
  }
  return {
    zone: parts[0],
    networkId: parts[1],
    profile: parts[2]
  };
}
function profileClaimAreEqual(left, right) {
  return left.zone === right.zone && left.networkId === right.networkId && left.profile === right.profile;
}
function appendUserProfileHeader(config) {
  if (config.practice.user) {
    return {
      ...config,
      headers: {
        ...config.headers,
        [headerNames.profile]: `${config.practice.user.resourceType}/${config.practice.user.resourceId}`
      }
    };
  }
  return config;
}
const allowedStatusCodes = {
  get: [200, 304],
  count: [204],
  upsert: [200, 201],
  upload: [200, 204],
  patch: [200],
  delete: [410, 204, 200],
  sync: [200]
};
function validateStatus(validStatuses) {
  return (status) => validStatuses.indexOf(status) != -1;
}
function readResponse(data) {
  retrocycle(data);
  return data;
}
function enhanceError(error) {
  error.isApolloError = true;
  return Promise.reject(error);
}
function removeFalsy(obj) {
  const newObj = {};
  if (obj) {
    for (const prop in obj) {
      const propertyValue = obj[prop];
      if (typeof propertyValue === "string") {
        newObj[prop] = propertyValue;
      }
    }
  }
  return newObj;
}
function appendHeaders(append, headers) {
  if (!headers) {
    return removeFalsy(append);
  }
  return {
    ...removeFalsy(headers),
    ...removeFalsy(append)
  };
}
function getSortQuery(orderBy) {
  if (!orderBy) {
    return {};
  }
  if (orderBy.direction === "Unspecified") {
    return {
      orderBy: orderBy.field
    };
  }
  return {
    orderBy: orderBy.field,
    order: orderBy.direction == "Descending" ? "desc" : "asc"
  };
}
function isApolloError(error) {
  return error && typeof error === "object" && "isApolloError" in error && error.isApolloError === true;
}
function isApolloRequestError(error) {
  return isApolloError(error) && "request" in error && typeof error.request === "object" && isApolloError(error);
}
function isApolloResponseError(error) {
  return isApolloRequestError(error) && "response" in error && typeof error.response === "object" && typeof error.response.status === "number";
}
class ApolloAxiosClient {
  constructor(config) {
    __publicField(this, "axios");
    this.axios = Axios.create(config);
    this.axios.defaults.transformResponse = [].concat(
      this.axios.defaults.transformResponse || [],
      readResponse
    );
    this.axios.defaults.paramsSerializer = {
      indexes: null
    };
    this.axios.defaults.headers.post["Content-Type"] = "application/json";
    this.axios.defaults.headers.put["Content-Type"] = "application/json";
    this.axios.defaults.headers.patch["Content-Type"] = "application/json-patch+json";
    this.axios.interceptors.request.use(void 0, enhanceError);
    this.axios.interceptors.response.use(void 0, enhanceError);
    if (config.requestHandler) {
      const requestHandlers = [].concat(config.requestHandler);
      requestHandlers.forEach((handler) => {
        this.axios.interceptors.request.use(handler.onBeforeRequest, handler.onRequestError);
        this.axios.interceptors.response.use(handler.onSuccessfulResponse, handler.onFailedResponse);
      });
    }
  }
  addRequestTransformer(transformer) {
    this.axios.defaults.transformRequest = [].concat(
      this.axios.defaults.transformRequest || [],
      transformer
    );
  }
  removeRequestTransformer(transformer) {
    if (this.axios.defaults.transformRequest) {
      if (this.axios.defaults.transformRequest === transformer) {
        this.axios.defaults.transformRequest = void 0;
      } else if (Array.isArray(this.axios.defaults.transformRequest)) {
        this.axios.defaults.transformRequest = this.axios.defaults.transformRequest.filter((r) => r !== transformer);
      }
    }
  }
  addResponseTransformer(transformer) {
    this.axios.defaults.transformResponse = [].concat(
      this.axios.defaults.transformResponse || [],
      transformer
    );
  }
  removeResponseTransformer(transformer) {
    if (this.axios.defaults.transformResponse) {
      if (this.axios.defaults.transformResponse === transformer) {
        this.axios.defaults.transformResponse = void 0;
      } else if (Array.isArray(this.axios.defaults.transformResponse)) {
        this.axios.defaults.transformResponse = this.axios.defaults.transformResponse.filter((r) => r !== transformer);
      }
    }
  }
  useRequestInterceptor(interceptor) {
    return this.axios.interceptors.request.use(interceptor);
  }
  ejectRequestInterceptor(id) {
    this.axios.interceptors.request.eject(id);
  }
  useResponseInterceptor(onFulfilled, onRejected) {
    return this.axios.interceptors.response.use(onFulfilled, onRejected);
  }
  ejectResponseInterceptor(id) {
    this.axios.interceptors.response.eject(id);
  }
  async get(request) {
    const { url, validStatuses, ...options } = request;
    return retryPolicy(() => {
      var _a;
      return this.axios.get(url, {
        validateStatus: validateStatus(validStatuses),
        ...options,
        signal: options.abort,
        headers: removeFalsy((_a = options.httpOptions) == null ? void 0 : _a.headers)
      });
    });
  }
  async delete(request) {
    const { url, validStatuses, ...options } = request;
    await retryPolicy(() => {
      var _a;
      return this.axios.delete(url, {
        validateStatus: validateStatus(validStatuses),
        ...options,
        signal: options.abort,
        headers: removeFalsy((_a = options.httpOptions) == null ? void 0 : _a.headers)
      });
    });
  }
  post(request) {
    const { url, validStatuses, data, ...options } = request;
    return retryPolicy(() => {
      var _a;
      return this.axios.post(url, data, {
        validateStatus: validateStatus(validStatuses),
        ...options,
        signal: options.abort,
        headers: removeFalsy((_a = options.httpOptions) == null ? void 0 : _a.headers)
      });
    });
  }
  async put(request) {
    const { url, validStatuses, data, ...options } = request;
    return retryPolicy(() => {
      var _a;
      return this.axios.put(url, data, {
        validateStatus: validateStatus(validStatuses),
        ...options,
        signal: options.abort,
        headers: removeFalsy((_a = options.httpOptions) == null ? void 0 : _a.headers)
      });
    });
  }
  async patch(request) {
    const { url, validStatuses, data, ...options } = request;
    return retryPolicy(() => {
      var _a;
      return this.axios.patch(url, data, {
        validateStatus: validateStatus(validStatuses),
        ...options,
        signal: options.abort,
        headers: removeFalsy((_a = options.httpOptions) == null ? void 0 : _a.headers)
      });
    });
  }
  async download(request, responseType) {
    var _a;
    const { url, validStatuses, ...options } = request;
    const axiosResponseType = responseType === "base64" ? "arraybuffer" : responseType;
    const response = await retryPolicy(() => {
      var _a2;
      return this.axios.request({
        url,
        method: "GET",
        adapter: axiosResponseType === "stream" ? "fetch" : void 0,
        responseType: axiosResponseType,
        validateStatus: validateStatus(validStatuses),
        ...options,
        signal: options.abort,
        headers: removeFalsy((_a2 = options.httpOptions) == null ? void 0 : _a2.headers)
      });
    });
    switch (responseType) {
      default:
        return response.data;
      case "base64":
        return convertBufferToBase64(response.data, ((_a = response.headers["content-type"]) == null ? void 0 : _a.toLowerCase()) ?? "application/unknown");
    }
  }
}
function storeOriginal(resources) {
  if (resources === void 0 || resources === null || typeof resources !== "object") {
    return resources;
  }
  if (Array.isArray(resources)) {
    resources.forEach(appendOriginal);
  } else {
    appendOriginal(resources);
  }
  return resources;
}
function appendOriginal(resource) {
  if (typeof resource !== "object" || !resource) {
    return;
  }
  resource._original = JSON.parse(safeStringify(resource));
}
function copyOriginal(src, dest) {
  dest._original = src._original;
}
function readOriginal(resource) {
  return resource._original;
}
function removeOriginal(resource) {
  const clone2 = {
    ...resource,
    _original: void 0
  };
  delete clone2._original;
  return clone2;
}
function makeNew(resource) {
  const copy = removeOriginal(resource);
  copy.id = void 0;
  copy.meta = void 0;
  copy.identifiers = void 0;
  return copy;
}
const defaultRetryDelay = 100;
const transientRetryDelay = 1e3 * 5;
const retryStatusCodes = /* @__PURE__ */ new Set([429, 449]);
async function retryPolicy(request) {
  do {
    try {
      return await request();
    } catch (error) {
      if (isApolloResponseError(error) && retryStatusCodes.has(error.response.status)) {
        await delay(getRetryAfter(error.response));
      }
      if (isTransientNetworkError(error)) {
        await delay(transientRetryDelay);
      } else {
        throw error;
      }
    }
  } while (true);
}
function isTransientNetworkError(error) {
  if (isAxiosError(error)) {
    return !error.response && (!error.code || error.code === "ECONNABORTED" || error.code === "ECONNREFUSED");
  }
  return false;
}
function getRetryAfter(response) {
  const retryAfterHeader = response.headers[headerNames.retryAfter];
  if (retryAfterHeader) {
    const retryAfter = Number.parseInt(retryAfterHeader);
    if (!Number.isNaN(retryAfter) && retryAfter > 0) {
      return retryAfter;
    }
  }
  return defaultRetryDelay;
}
class BaseResourceClient {
  constructor(resourceType, baseUrl, routeName, config) {
    __publicField(this, "resourceType");
    __publicField(this, "client");
    __publicField(this, "baseUrl");
    __publicField(this, "routeName");
    __publicField(this, "config");
    __publicField(this, "url");
    __publicField(this, "fetchedHandlers", /* @__PURE__ */ new Map());
    __publicField(this, "subscriptionIndex", 0);
    __publicField(this, "fireFetched", (items) => {
      this.fetchedHandlers.forEach((handler) => handler(items));
    });
    this.resourceType = resourceType;
    this.baseUrl = baseUrl;
    this.routeName = routeName;
    this.config = config;
    this.url = `${baseUrl}/${routeName}`;
    this.client = new ApolloAxiosClient(config);
  }
  onFetched(handler) {
    const index = this.subscriptionIndex++;
    this.fetchedHandlers.set(index, handler);
    return () => this.fetchedHandlers.delete(index);
  }
  async fetch(fetchRequest) {
    var _a;
    const {
      httpOptions = {},
      feedOptions = {},
      fetchOptions = {},
      abort,
      silent = false
    } = fetchRequest ?? {};
    const { headers, ...restHttpOptions } = httpOptions;
    const response = await this.client.get({
      url: this.url,
      validStatuses: allowedStatusCodes.get,
      abort,
      params: {
        pageSize: feedOptions.maxItemCount,
        ...getSortQuery(fetchRequest == null ? void 0 : fetchRequest.orderBy),
        ...fetchOptions
      },
      httpOptions: {
        headers: appendHeaders({
          [headerNames.continuationToken]: feedOptions.continuationToken,
          [headerNames.response]: (fetchRequest == null ? void 0 : fetchRequest.responseOptions) ? JSON.stringify(fetchRequest.responseOptions) : void 0,
          [headerNames.partitionKey]: (fetchRequest == null ? void 0 : fetchRequest.feedOptions) ? fetchRequest.feedOptions.partition : void 0,
          [headerNames.crossPartition]: ((_a = fetchRequest == null ? void 0 : fetchRequest.feedOptions) == null ? void 0 : _a.crossPartition) === true ? "true" : void 0
        }, headers),
        ...restHttpOptions
      }
    });
    const results = storeOriginal(response.data) || [];
    if (!silent) {
      this.fireFetched(results);
    }
    return {
      continuationToken: response.headers ? response.headers[headerNames.continuationToken] : void 0,
      results
    };
  }
  async query(queryRequest) {
    var _a;
    const {
      filters = [],
      feedOptions = {},
      httpOptions = {},
      abort,
      silent = false
    } = queryRequest;
    const { headers, ...restHttpOptions } = httpOptions;
    const response = await this.client.post({
      url: `${this.url}/queries`,
      validStatuses: allowedStatusCodes.get,
      data: filters,
      abort,
      params: {
        pageSize: feedOptions.maxItemCount,
        ...getSortQuery(queryRequest.orderBy)
      },
      httpOptions: {
        headers: appendHeaders({
          [headerNames.continuationToken]: feedOptions.continuationToken,
          [headerNames.response]: queryRequest.responseOptions ? JSON.stringify(queryRequest.responseOptions) : void 0,
          [headerNames.partitionKey]: queryRequest.feedOptions ? queryRequest.feedOptions.partition : void 0,
          [headerNames.crossPartition]: ((_a = queryRequest.feedOptions) == null ? void 0 : _a.crossPartition) === true ? "true" : void 0
        }, headers),
        ...restHttpOptions
      }
    });
    const results = storeOriginal(response.data) || [];
    if (!silent) {
      this.fireFetched(results);
    }
    return {
      continuationToken: response.headers ? response.headers[headerNames.continuationToken] : void 0,
      results
    };
  }
  async queryCount(countRequest) {
    const {
      filters = [],
      abort,
      httpOptions = {}
    } = countRequest ?? {};
    const { headers, ...restHttpOptions } = httpOptions;
    const response = await this.client.post({
      url: `${this.url}/queries`,
      validStatuses: allowedStatusCodes.count,
      data: filters,
      abort,
      httpOptions: {
        headers: appendHeaders({
          [headerNames.prefer]: "return=minimal",
          [headerNames.partitionKey]: countRequest == null ? void 0 : countRequest.partition,
          [headerNames.crossPartition]: (countRequest == null ? void 0 : countRequest.crossPartition) === true ? "true" : void 0
        }, headers),
        ...restHttpOptions
      }
    });
    if (!response.headers) {
      return 0;
    }
    const headerValue = response.headers[headerNames.totalCount];
    if (!headerValue) {
      return 0;
    }
    return parseInt(headerValue, 10);
  }
  /**
   * Saves a new instance of AuditEvent.
   * @param resource The AuditEvent to save.
   * @param request Additional options for this request.
   */
  async create(resource, request) {
    if (resource.id) {
      throw new Error("Resource requested for creation already has an id.");
    }
    const { httpOptions = {}, abort, silent = false } = request ?? {};
    const { headers, ...restHttpOptions } = httpOptions;
    const response = await this.client.post({
      url: this.url,
      validStatuses: allowedStatusCodes.upsert,
      data: prepForSave(this.resourceType, removeOriginal(resource)),
      abort,
      httpOptions: {
        headers: appendHeaders({
          [headerNames.ifNoneExists]: (request == null ? void 0 : request.conditions) ? JSON.stringify(request.conditions) : void 0,
          [headerNames.response]: (request == null ? void 0 : request.responseOptions) ? JSON.stringify(request.responseOptions) : void 0
        }, headers),
        ...restHttpOptions
      }
    });
    const result = storeOriginal(response.data);
    if (!silent) {
      this.fireFetched([result]);
    }
    return result;
  }
  /**
   * An upsert request that updates an existing instance of AuditEvent if
   * the provided ID is found in the database, or creates it new if not found.
   * @param resource The AuditEvent to save.
   * @param request Additional options for this request.
   */
  async update(resource, request) {
    var _a;
    if (!resource.id) {
      throw new Error("Cannot update a resource that has no id.");
    }
    resource = prepForSave(this.resourceType, resource);
    const original = readOriginal(resource);
    resource = removeOriginal(resource);
    if (original && isNotNew(resource)) {
      if (original.id === resource.id) {
        const patch = Patch.generatePatch(original, resource);
        if (patch.operations.length === 0) {
          return resource;
        }
        return await this.patch(patch, request);
      }
      if (original.meta && resource.meta && original.meta.versionId === resource.meta.versionId) {
        resource.meta.versionId = void 0;
        resource.identifiers = void 0;
      }
    }
    if ((_a = resource.meta) == null ? void 0 : _a.hasTransformations)
      throw new Error("Cannot perform update using a resource that had transformations (such as exclusions) performed on it.");
    const { httpOptions = {}, abort, silent = false } = request ?? {};
    const { headers, ...restHttpOptions } = httpOptions;
    const response = await this.client.put({
      url: `${this.url}/${resource.id}`,
      validStatuses: allowedStatusCodes.upsert,
      data: resource,
      abort,
      httpOptions: {
        headers: appendHeaders({
          [headerNames.ifNoneExists]: (request == null ? void 0 : request.conditions) ? JSON.stringify(request.conditions) : void 0,
          [headerNames.response]: (request == null ? void 0 : request.responseOptions) ? JSON.stringify(request.responseOptions) : void 0
        }, headers),
        ...restHttpOptions
      }
    });
    const result = storeOriginal(response.data);
    if (!silent) {
      this.fireFetched([result]);
    }
    return result;
  }
  async patch(patch, request = {}) {
    const { httpOptions = {}, abort, silent = false } = request;
    const { headers, ...restHttpOptions } = httpOptions;
    const response = await this.client.patch({
      url: `${this.url}/${patch.identity.resourceId}?versionId=${patch.identity.versionId || ""}`,
      validStatuses: allowedStatusCodes.patch,
      data: patch.operations,
      abort,
      httpOptions: {
        headers: appendHeaders({
          [headerNames.ifNoneExists]: request.conditions ? JSON.stringify(request.conditions) : void 0,
          [headerNames.response]: request.responseOptions ? JSON.stringify(request.responseOptions) : void 0,
          [headerNames.partitionKey]: patch.identity.partition
        }, headers),
        ...restHttpOptions
      }
    });
    const result = storeOriginal(response.data);
    if (!silent) {
      this.fireFetched([result]);
    }
    return result;
  }
  async downloadContentInternal(request) {
    const { resourceId, versionId, partition, thumbnail, format, abort, httpOptions = {} } = request;
    const { headers, ...restHttpOptions } = httpOptions;
    const query = thumbnail !== void 0 ? `?thumbnail=${thumbnail}` : "";
    const path = versionId ? `${resourceId}/_history/${versionId}/content` : `${resourceId}/content`;
    return await this.client.download({
      url: `${this.url}/${path}${query}`,
      validStatuses: allowedStatusCodes.get,
      abort,
      httpOptions: {
        headers: appendHeaders({
          [headerNames.partitionKey]: partition
        }, headers),
        ...restHttpOptions
      }
    }, format);
  }
  async uploadContentInternal(partition, resourceId, chunk, content, contentType, options) {
    const { httpOptions = {}, abort } = options ?? {};
    const { headers, ...restHttpOptions } = httpOptions;
    const data = typeof content === "string" ? convertBase64ToBuffer(content) : content;
    await this.client.post({
      url: `${this.url}/${resourceId}/content${chunk !== void 0 ? `/chunks/${chunk}` : ""}`,
      validStatuses: allowedStatusCodes.upload,
      data,
      abort,
      httpOptions: {
        headers: appendHeaders({
          "Content-Type": contentType,
          [headerNames.partitionKey]: partition
        }, headers),
        ...restHttpOptions
      }
    });
  }
  async commitChunksInternal(partition, resourceId, contentType, options) {
    const { httpOptions = {}, abort } = options ?? {};
    const { headers, ...restHttpOptions } = httpOptions;
    await this.client.post({
      url: `${this.url}/${resourceId}/content/chunks`,
      validStatuses: allowedStatusCodes.upload,
      data: {},
      abort,
      httpOptions: {
        headers: appendHeaders({
          "Content-Type": contentType,
          [headerNames.partitionKey]: partition
        }, headers),
        ...restHttpOptions
      }
    });
  }
  async deleteContentInternal(partition, resourceId, purge, options) {
    const { httpOptions, abort, ...restOptions } = options ?? {};
    const { headers, ...restHttpOptions } = httpOptions ?? {};
    await this.client.delete({
      url: `${this.url}/${resourceId}/content` + (purge ? "?purge=true" : ""),
      validStatuses: allowedStatusCodes.delete,
      abort,
      httpOptions: {
        headers: appendHeaders({
          [headerNames.partitionKey]: partition
        }, headers),
        ...restOptions,
        ...restHttpOptions
      }
    });
  }
  async ocrInternal(partition, resourceId, options) {
    const { httpOptions, abort, ...restOptions } = options ?? {};
    const { headers, ...restHttpOptions } = httpOptions ?? {};
    const response = await this.client.post({
      url: `${this.url}/${resourceId}/ocr`,
      data: {},
      validStatuses: allowedStatusCodes.upload,
      abort,
      httpOptions: {
        headers: appendHeaders({
          [headerNames.partitionKey]: partition
        }, headers),
        ...restOptions,
        ...restHttpOptions
      }
    });
    return response.data;
  }
  async expand(referencesParam, options) {
    if (Array.isArray(referencesParam)) {
      return this.expandMulti(referencesParam, options);
    }
    return this.expandSingle(referencesParam, options);
  }
  feed(options) {
    return new FeedIterator(this, options);
  }
  async fetchByIdInternal(partition, resourceId, versionIdOrRequestOptions, requestOptions) {
    const versionId = typeof versionIdOrRequestOptions === "string" ? versionIdOrRequestOptions : void 0;
    const requestOptions2 = typeof versionIdOrRequestOptions === "object" ? versionIdOrRequestOptions : requestOptions;
    const { httpOptions = {}, abort, silent = false } = requestOptions2 ?? {};
    const { headers, ...restHttpOptions } = httpOptions;
    const response = await this.client.get({
      url: versionId ? `${this.url}/${resourceId}/_history/${versionId}` : `${this.url}/${resourceId}`,
      validStatuses: allowedStatusCodes.get,
      abort,
      httpOptions: {
        headers: appendHeaders({
          [headerNames.response]: (requestOptions2 == null ? void 0 : requestOptions2.responseOptions) ? JSON.stringify(requestOptions2.responseOptions) : void 0,
          [headerNames.partitionKey]: partition
        }, headers),
        ...restHttpOptions
      }
    });
    const result = storeOriginal(response.data);
    if (!silent) {
      this.fireFetched([result]);
    }
    return result;
  }
  async deleteByIdInternal(partition, resourceId, versionIdOrRequest, request) {
    const requestOptions = typeof versionIdOrRequest === "string" ? request : void 0;
    const versionId = typeof versionIdOrRequest === "string" ? versionIdOrRequest : "";
    const { httpOptions = {}, abort } = requestOptions ?? {};
    const { headers, ...restHttpOptions } = httpOptions;
    await this.client.delete({
      url: `${this.url}/${resourceId}?versionId=${versionId}`,
      validStatuses: allowedStatusCodes.delete,
      abort,
      httpOptions: {
        headers: appendHeaders({
          [headerNames.ifNoneExists]: (requestOptions == null ? void 0 : requestOptions.conditions) ? JSON.stringify(requestOptions.conditions) : void 0,
          [headerNames.response]: (requestOptions == null ? void 0 : requestOptions.responseOptions) ? JSON.stringify(requestOptions.responseOptions) : void 0,
          [headerNames.partitionKey]: partition
        }, headers),
        ...restHttpOptions
      }
    });
  }
  async syncByIdInternal(partition, resourceId, requestOptions) {
    const { httpOptions = {}, abort, silent = false } = requestOptions ?? {};
    const { headers, ...restHttpOptions } = httpOptions;
    const response = await this.client.post({
      url: `${this.url}/${resourceId}/sync`,
      data: {},
      validStatuses: allowedStatusCodes.sync,
      abort,
      httpOptions: {
        headers: appendHeaders({
          [headerNames.response]: (requestOptions == null ? void 0 : requestOptions.responseOptions) ? JSON.stringify(requestOptions.responseOptions) : void 0,
          [headerNames.partitionKey]: partition
        }, headers),
        ...restHttpOptions
      }
    });
    const result = storeOriginal(response.data);
    if (!silent) {
      this.fireFetched([result]);
    }
    return result;
  }
  feedInternal(options) {
    return new FeedIterator(this, options);
  }
  async expandSingle(reference, options) {
    if (reference.resource) {
      return reference.resource;
    }
    if (!reference.id) {
      return null;
    }
    const expanded = await this.expandMulti([reference], options);
    if (expanded.length === 0) {
      throw getNotFoundError(reference);
    }
    return expanded[0];
    function getNotFoundError(expandable) {
      let display = expandable.id;
      if (expandable.resourceType) {
        display = `${expandable.resourceType} ${display}`;
      }
      if (expandable.versionId) {
        display += ` (version ${expandable.versionId})`;
      }
      return new Error(`Reference ${display} could not be expanded.`);
    }
  }
  async fetchExpand(ids, versionIds, options) {
    if (ids.length + versionIds.length > 100) {
      const resources = [];
      if (ids.length > 0) {
        for (const batch of paginate(ids, 100)) {
          resources.push(...await this.fetchExpand(batch, [], options));
        }
      }
      if (versionIds.length > 0) {
        for (const batch of paginate(versionIds, 100)) {
          resources.push(...await this.fetchExpand([], batch, options));
        }
      }
      return resources;
    }
    const filters = [];
    if (ids.length > 0) {
      filters.push({
        ids,
        includeDeleted: true
      });
    }
    if (versionIds.length > 0) {
      filters.push({
        versionId: { in: versionIds },
        includeDeleted: true
      });
    }
    const feed = this.feedInternal({ filters, crossPartition: true, responseOptions: options == null ? void 0 : options.responseOptions });
    return await feed.all(options);
  }
  async expandMulti(references, options) {
    const notExpandedYet = references.filter((r) => !r.resource);
    if (notExpandedYet.length === 0) {
      return distinctResources(references.map((r) => r.resource));
    }
    const newlyExpanded = /* @__PURE__ */ new Map();
    const ids = Array.from(new Set(notExpandedYet.filter((r) => r.id && !r.versionId).map((r) => r.id)));
    const versionIds = Array.from(new Set(notExpandedYet.filter((r) => r.id && r.versionId).map((r) => r.versionId)));
    if (ids.length > 0 || versionIds.length > 0) {
      const results = await this.fetchExpand(ids, versionIds, options);
      const resultsLookup = /* @__PURE__ */ new Map();
      results.forEach((resource) => {
        var _a, _b;
        if (!((_a = resource.meta) == null ? void 0 : _a.isHistorical) && !resultsLookup.has(resource.id)) {
          resultsLookup.set(resource.id, resource);
        }
        if (((_b = resource.meta) == null ? void 0 : _b.versionId) && !resultsLookup.has(resource.meta.versionId)) {
          resultsLookup.set(resource.meta.versionId, resource);
        }
      });
      notExpandedYet.forEach((reference) => {
        reference = fixNestedResource(reference);
        const referenceIdentity = reference.versionId ?? reference.id;
        if (referenceIdentity && resultsLookup.has(referenceIdentity)) {
          const resource = resultsLookup.get(referenceIdentity);
          if (!reference.resource) {
            reference.resource = resource;
          }
          newlyExpanded.set(referenceIdentity, resource);
        }
      });
    }
    const alreadyExpanded = references.filter((r) => r.resource);
    return distinctResources([...alreadyExpanded.map((r) => r.resource), ...Array.from(newlyExpanded.values())]);
  }
}
class PartitionedResourceClient extends BaseResourceClient {
  fetchById(partition, resourceId, versionIdOrRequestOptions, requestOptions) {
    return this.fetchByIdInternal(partition, resourceId, versionIdOrRequestOptions, requestOptions);
  }
  async deleteById(partition, resourceId, versionIdOrRequest, request) {
    await this.deleteByIdInternal(partition, resourceId, versionIdOrRequest, request);
  }
  syncById(partition, resourceId, requestOptions) {
    return this.syncByIdInternal(partition, resourceId, requestOptions);
  }
  async downloadContent(...args) {
    if (typeof args[0] === "string") {
      return await this.downloadContentById.apply(this, args);
    } else {
      return await this.downloadContentByResource.apply(this, args);
    }
  }
  async uploadContent(partition, resourceId, request) {
    await this.uploadContentInternal(partition, resourceId, void 0, request.content, request.contentType, request);
  }
  async upsertChunk(partition, resourceId, chunk, content, request) {
    await this.uploadContentInternal(partition, resourceId, chunk, content, void 0, request);
  }
  async commitChunks(partition, resourceId, contentType, request) {
    await this.commitChunksInternal(partition, resourceId, contentType, request);
  }
  async deleteContent(partition, resourceId, options = {}) {
    await this.deleteContentInternal(partition, resourceId, false, options);
  }
  async purgeContent(partition, resourceId, options = {}) {
    await this.deleteContentInternal(partition, resourceId, true, options);
  }
  async ocr(partition, resourceId, options = {}) {
    return await this.ocrInternal(partition, resourceId, options);
  }
  async downloadContentByResource(resource, format, thumbnailOrOptions = void 0, options = {}) {
    var _a;
    return await this.downloadContentInternal({
      resourceId: resource.id,
      versionId: isReference(resource) ? resource.versionId : (_a = resource.meta) == null ? void 0 : _a.versionId,
      format,
      thumbnail: typeof thumbnailOrOptions === "number" ? thumbnailOrOptions : void 0,
      ...typeof thumbnailOrOptions !== "number" ? thumbnailOrOptions : options,
      partition: resource.partition
    });
  }
  async downloadContentById(partition, resourceId, format, thumbnailOrOptions = void 0, options = {}) {
    return await this.downloadContentInternal({
      resourceId,
      format,
      thumbnail: typeof thumbnailOrOptions === "number" ? thumbnailOrOptions : void 0,
      ...typeof thumbnailOrOptions !== "number" ? thumbnailOrOptions : options,
      partition
    });
  }
}
class ResourceClient extends BaseResourceClient {
  fetchById(resourceId, versionIdOrRequestOptions, requestOptions) {
    return this.fetchByIdInternal(void 0, resourceId, versionIdOrRequestOptions, requestOptions);
  }
  async deleteById(resourceId, versionIdOrRequest, request) {
    await this.deleteByIdInternal(void 0, resourceId, versionIdOrRequest, request);
  }
  syncById(resourceId, requestOptions) {
    return this.syncByIdInternal(void 0, resourceId, requestOptions);
  }
  async downloadContent(...args) {
    if (typeof args[0] === "string") {
      return await this.downloadContentById.apply(this, args);
    } else {
      return await this.downloadContentByResource.apply(this, args);
    }
  }
  async uploadContent(resourceId, request) {
    await this.uploadContentInternal(void 0, resourceId, void 0, request.content, request.contentType, request);
  }
  async upsertChunk(resourceId, chunk, content, request) {
    await this.uploadContentInternal(void 0, resourceId, chunk, content, void 0, request);
  }
  async commitChunks(resourceId, contentType, request) {
    await this.commitChunksInternal(void 0, resourceId, contentType, request);
  }
  async deleteContent(resourceId, options = {}) {
    await this.deleteContentInternal(void 0, resourceId, false, options);
  }
  async purgeContent(resourceId, options = {}) {
    await this.deleteContentInternal(void 0, resourceId, true, options);
  }
  async ocr(resourceId, options = {}) {
    return await this.ocrInternal(void 0, resourceId, options);
  }
  async downloadContentByResource(resource, format, thumbnailOrOptions = void 0, options = {}) {
    var _a;
    return await this.downloadContentInternal({
      resourceId: resource.id,
      versionId: isReference(resource) ? resource.versionId : (_a = resource.meta) == null ? void 0 : _a.versionId,
      format,
      thumbnail: typeof thumbnailOrOptions === "number" ? thumbnailOrOptions : void 0,
      ...typeof thumbnailOrOptions !== "number" ? thumbnailOrOptions : options
    });
  }
  async downloadContentById(resourceId, format, thumbnailOrOptions = void 0, options = {}) {
    return await this.downloadContentInternal({
      resourceId,
      format,
      thumbnail: typeof thumbnailOrOptions === "number" ? thumbnailOrOptions : void 0,
      ...typeof thumbnailOrOptions !== "number" ? thumbnailOrOptions : options
    });
  }
}
class FeedIterator {
  constructor(client, options) {
    __publicField(this, "client");
    __publicField(this, "options");
    // Empty string means no more pages, undefined means no pages queried yet
    __publicField(this, "continuationToken");
    this.client = client;
    this.options = options;
  }
  get hasMoreResults() {
    return this.continuationToken !== "";
  }
  async totalCount(options) {
    return await this.client.queryCount({
      filters: this.options.filters,
      ...options
    });
  }
  async next(maxItemCount, options) {
    if (this.continuationToken === "") {
      return [];
    }
    const { crossPartition, partition, ...queryOptions } = this.options;
    const response = await this.client.query({
      ...queryOptions,
      feedOptions: {
        crossPartition,
        partition,
        maxItemCount,
        continuationToken: this.continuationToken
      },
      ...options
    });
    this.continuationToken = response.continuationToken ?? "";
    return response.results;
  }
  async all(options) {
    if (this.continuationToken === "") {
      return [];
    }
    let continuationToken = this.continuationToken;
    const items = [];
    this.continuationToken = "";
    const { crossPartition, partition, ...queryOptions } = this.options;
    do {
      const response = await this.client.query({
        ...queryOptions,
        feedOptions: {
          crossPartition,
          partition,
          maxItemCount: 100,
          continuationToken
        },
        ...options
      });
      items.push(...response.results);
      continuationToken = response.continuationToken ?? "";
    } while (continuationToken);
    return items;
  }
}
class DatabaseClient {
  constructor(config) {
    __publicField(this, "client");
    this.client = new ApolloAxiosClient(config);
  }
  async createDatabase(networkId, options) {
    await this.client.post({
      url: `api/v1/database/${networkId}`,
      validStatuses: [204],
      data: {},
      ...options
    });
  }
  async deleteDatabase(networkId, options) {
    await this.client.delete({
      url: `api/v1/database/${networkId}`,
      validStatuses: [204],
      ...options
    });
  }
}
function extendRegistryClients(clients) {
  return {
    ...clients
  };
}
function extendPracticeClients(clients) {
  return {
    ...clients,
    patients: new PatientsClient(clients.patients.resourceType, clients.patients.baseUrl, clients.patients.routeName, clients.patients.config),
    practitioners: new PractitionersClient(clients.practitioners.resourceType, clients.practitioners.baseUrl, clients.practitioners.routeName, clients.practitioners.config),
    relatedPeople: new RelatedPeopleClient(clients.relatedPeople.resourceType, clients.relatedPeople.baseUrl, clients.relatedPeople.routeName, clients.relatedPeople.config),
    reportDeliveries: new ReportDeliveriesClient(clients.reportDeliveries.resourceType, clients.reportDeliveries.baseUrl, clients.reportDeliveries.routeName, clients.reportDeliveries.config)
  };
}
class PatientsClient extends ResourceClient {
  async invite(resourceId, email, options) {
    await this.client.post({
      url: `${this.url}/${resourceId}/invite`,
      validStatuses: [200, 201, 204],
      data: {
        target: email
      },
      ...options
    });
  }
}
class PractitionersClient extends ResourceClient {
  async invite(resourceId, email, options) {
    await this.client.post({
      url: `${this.url}/${resourceId}/invite`,
      validStatuses: [200, 201, 204],
      data: {
        target: email
      },
      ...options
    });
  }
}
class RelatedPeopleClient extends PartitionedResourceClient {
  async invite(resourceId, email, options) {
    await this.client.post({
      url: `${this.url}/${resourceId}/invite`,
      validStatuses: [200, 201, 204],
      data: {
        target: email
      },
      ...options
    });
  }
}
class ReportDeliveriesClient extends ResourceClient {
  async decryptPassword(reportDeliveryId, options) {
    const response = await this.client.post({
      url: `${this.url}/${reportDeliveryId}/decrypt-password`,
      validStatuses: [200],
      data: {},
      ...options
    });
    return response.data.plaintext;
  }
}
class RegistryClients {
  constructor(config) {
    __publicField(this, "database");
    __publicField(this, "keyVaults");
    __publicField(this, "announcements");
    __publicField(this, "notations");
    __publicField(this, "snippetTemplates");
    __publicField(this, "speechRuleTemplates");
    __publicField(this, "textExpansionTemplates");
    const baseUrl = "/api/v1";
    const clients = extendRegistryClients({
      database: new DatabaseClient(config),
      keyVaults: new ResourceClient("KeyVault", baseUrl, "KeyVaults", config),
      announcements: new ResourceClient("Announcement", baseUrl, "Announcements", config),
      notations: new ResourceClient("Notation", baseUrl, "Notations", config),
      snippetTemplates: new ResourceClient("SnippetTemplate", baseUrl, "SnippetTemplates", config),
      speechRuleTemplates: new ResourceClient("SpeechRuleTemplate", baseUrl, "SpeechRuleTemplates", config),
      textExpansionTemplates: new ResourceClient("TextExpansionTemplate", baseUrl, "TextExpansionTemplates", config)
    });
    this.database = clients.database;
    this.keyVaults = clients.keyVaults;
    this.announcements = clients.announcements;
    this.notations = clients.notations;
    this.snippetTemplates = clients.snippetTemplates;
    this.speechRuleTemplates = clients.speechRuleTemplates;
    this.textExpansionTemplates = clients.textExpansionTemplates;
  }
}
class PracticeClients {
  constructor(config) {
    __publicField(this, "allergies");
    __publicField(this, "appointments");
    __publicField(this, "carePlans");
    __publicField(this, "carePlanOutcomes");
    __publicField(this, "carePlanSessions");
    __publicField(this, "cdaParsingTasks");
    __publicField(this, "channels");
    __publicField(this, "channelMemberships");
    __publicField(this, "chatMessages");
    __publicField(this, "clinicalDocuments");
    __publicField(this, "communication");
    __publicField(this, "complianceAudits");
    __publicField(this, "complianceQuestionnaires");
    __publicField(this, "conditions");
    __publicField(this, "consents");
    __publicField(this, "contracts");
    __publicField(this, "coverages");
    __publicField(this, "dialogues");
    __publicField(this, "districts");
    __publicField(this, "documentReferences");
    __publicField(this, "documentTransmissions");
    __publicField(this, "encounters");
    __publicField(this, "episodeOfCares");
    __publicField(this, "forms");
    __publicField(this, "grantSets");
    __publicField(this, "groups");
    __publicField(this, "groupNoteDefinitions");
    __publicField(this, "healthcareServices");
    __publicField(this, "itineraries");
    __publicField(this, "listings");
    __publicField(this, "locations");
    __publicField(this, "media");
    __publicField(this, "medications");
    __publicField(this, "notes");
    __publicField(this, "noteAudits");
    __publicField(this, "noteDefinitions");
    __publicField(this, "noteRules");
    __publicField(this, "noteSectionForms");
    __publicField(this, "noteTemplates");
    __publicField(this, "notifications");
    __publicField(this, "observableRoutines");
    __publicField(this, "observations");
    __publicField(this, "organizations");
    __publicField(this, "packets");
    __publicField(this, "packetResponses");
    __publicField(this, "patients");
    __publicField(this, "personalPreferences");
    __publicField(this, "personalSignatures");
    __publicField(this, "practicePreferences");
    __publicField(this, "practitioners");
    __publicField(this, "programs");
    __publicField(this, "questionnaires");
    __publicField(this, "questionnaireResponses");
    __publicField(this, "relatedPeople");
    __publicField(this, "reportCriteria");
    __publicField(this, "reportDeliveries");
    __publicField(this, "schedules");
    __publicField(this, "scribbles");
    __publicField(this, "scripts");
    __publicField(this, "securityRoles");
    __publicField(this, "securityTokens");
    __publicField(this, "sharedScribbles");
    __publicField(this, "signatureConfigurations");
    __publicField(this, "snippets");
    __publicField(this, "speechRules");
    __publicField(this, "textExpansions");
    __publicField(this, "transcriptions");
    config = appendUserProfileHeader(config);
    const baseUrl = `/api/v1/practices/${config.practice.networkId}`;
    const clients = extendPracticeClients({
      allergies: new PartitionedResourceClient("Allergy", baseUrl, "Allergies", config),
      appointments: new PartitionedResourceClient("Appointment", baseUrl, "Appointments", config),
      carePlans: new PartitionedResourceClient("CarePlan", baseUrl, "CarePlans", config),
      carePlanOutcomes: new PartitionedResourceClient("CarePlanOutcome", baseUrl, "CarePlanOutcomes", config),
      carePlanSessions: new PartitionedResourceClient("CarePlanSession", baseUrl, "CarePlanSessions", config),
      cdaParsingTasks: new ResourceClient("CdaParsingTask", baseUrl, "CdaParsingTasks", config),
      channels: new ResourceClient("Channel", baseUrl, "Channels", config),
      channelMemberships: new ResourceClient("ChannelMembership", baseUrl, "ChannelMemberships", config),
      chatMessages: new PartitionedResourceClient("ChatMessage", baseUrl, "ChatMessages", config),
      clinicalDocuments: new PartitionedResourceClient("ClinicalDocument", baseUrl, "ClinicalDocuments", config),
      communication: new ResourceClient("Communication", baseUrl, "Communication", config),
      complianceAudits: new PartitionedResourceClient("ComplianceAudit", baseUrl, "ComplianceAudits", config),
      complianceQuestionnaires: new ResourceClient("ComplianceQuestionnaire", baseUrl, "ComplianceQuestionnaires", config),
      conditions: new PartitionedResourceClient("Condition", baseUrl, "Conditions", config),
      consents: new PartitionedResourceClient("Consent", baseUrl, "Consents", config),
      contracts: new ResourceClient("Contract", baseUrl, "Contracts", config),
      coverages: new PartitionedResourceClient("Coverage", baseUrl, "Coverages", config),
      dialogues: new PartitionedResourceClient("Dialogue", baseUrl, "Dialogues", config),
      districts: new ResourceClient("District", baseUrl, "Districts", config),
      documentReferences: new PartitionedResourceClient("DocumentReference", baseUrl, "DocumentReferences", config),
      documentTransmissions: new ResourceClient("DocumentTransmission", baseUrl, "DocumentTransmissions", config),
      encounters: new PartitionedResourceClient("Encounter", baseUrl, "Encounters", config),
      episodeOfCares: new PartitionedResourceClient("EpisodeOfCare", baseUrl, "EpisodeOfCares", config),
      forms: new ResourceClient("Form", baseUrl, "Forms", config),
      grantSets: new ResourceClient("GrantSet", baseUrl, "GrantSets", config),
      groups: new ResourceClient("Group", baseUrl, "Groups", config),
      groupNoteDefinitions: new ResourceClient("GroupNoteDefinition", baseUrl, "GroupNoteDefinitions", config),
      healthcareServices: new ResourceClient("HealthcareService", baseUrl, "HealthcareServices", config),
      itineraries: new ResourceClient("Itinerary", baseUrl, "Itineraries", config),
      listings: new ResourceClient("Listing", baseUrl, "Listings", config),
      locations: new ResourceClient("Location", baseUrl, "Locations", config),
      media: new PartitionedResourceClient("Media", baseUrl, "Media", config),
      medications: new PartitionedResourceClient("Medication", baseUrl, "Medications", config),
      notes: new PartitionedResourceClient("Note", baseUrl, "Notes", config),
      noteAudits: new PartitionedResourceClient("NoteAudit", baseUrl, "NoteAudits", config),
      noteDefinitions: new ResourceClient("NoteDefinition", baseUrl, "NoteDefinitions", config),
      noteRules: new ResourceClient("NoteRule", baseUrl, "NoteRules", config),
      noteSectionForms: new ResourceClient("NoteSectionForm", baseUrl, "NoteSectionForms", config),
      noteTemplates: new ResourceClient("NoteTemplate", baseUrl, "NoteTemplates", config),
      notifications: new PartitionedResourceClient("Notification", baseUrl, "Notifications", config),
      observableRoutines: new PartitionedResourceClient("ObservableRoutines", baseUrl, "ObservableRoutines", config),
      observations: new PartitionedResourceClient("Observation", baseUrl, "Observations", config),
      organizations: new ResourceClient("Organization", baseUrl, "Organizations", config),
      packets: new ResourceClient("Packet", baseUrl, "Packets", config),
      packetResponses: new PartitionedResourceClient("PacketResponse", baseUrl, "PacketResponses", config),
      patients: new ResourceClient("Patient", baseUrl, "Patients", config),
      personalPreferences: new ResourceClient("PersonalPreferences", baseUrl, "PersonalPreferences", config),
      personalSignatures: new ResourceClient("PersonalSignature", baseUrl, "PersonalSignatures", config),
      practicePreferences: new ResourceClient("PracticePreferences", baseUrl, "PracticePreferences", config),
      practitioners: new ResourceClient("Practitioner", baseUrl, "Practitioners", config),
      programs: new ResourceClient("Program", baseUrl, "Programs", config),
      questionnaires: new ResourceClient("Questionnaire", baseUrl, "Questionnaires", config),
      questionnaireResponses: new PartitionedResourceClient("QuestionnaireResponse", baseUrl, "QuestionnaireResponses", config),
      relatedPeople: new PartitionedResourceClient("RelatedPerson", baseUrl, "RelatedPeople", config),
      reportCriteria: new ResourceClient("ReportCriteria", baseUrl, "ReportCriteria", config),
      reportDeliveries: new ResourceClient("ReportDelivery", baseUrl, "ReportDeliveries", config),
      schedules: new ResourceClient("Schedule", baseUrl, "Schedules", config),
      scribbles: new PartitionedResourceClient("Scribble", baseUrl, "Scribbles", config),
      scripts: new ResourceClient("Script", baseUrl, "Scripts", config),
      securityRoles: new ResourceClient("SecurityRole", baseUrl, "SecurityRoles", config),
      securityTokens: new ResourceClient("SecurityToken", baseUrl, "SecurityTokens", config),
      sharedScribbles: new ResourceClient("SharedScribble", baseUrl, "SharedScribbles", config),
      signatureConfigurations: new ResourceClient("SignatureConfiguration", baseUrl, "SignatureConfigurations", config),
      snippets: new ResourceClient("Snippet", baseUrl, "Snippets", config),
      speechRules: new ResourceClient("SpeechRule", baseUrl, "SpeechRules", config),
      textExpansions: new ResourceClient("TextExpansion", baseUrl, "TextExpansions", config),
      transcriptions: new PartitionedResourceClient("Transcription", baseUrl, "Transcriptions", config)
    });
    this.allergies = clients.allergies;
    this.appointments = clients.appointments;
    this.carePlans = clients.carePlans;
    this.carePlanOutcomes = clients.carePlanOutcomes;
    this.carePlanSessions = clients.carePlanSessions;
    this.cdaParsingTasks = clients.cdaParsingTasks;
    this.channels = clients.channels;
    this.channelMemberships = clients.channelMemberships;
    this.chatMessages = clients.chatMessages;
    this.clinicalDocuments = clients.clinicalDocuments;
    this.communication = clients.communication;
    this.complianceAudits = clients.complianceAudits;
    this.complianceQuestionnaires = clients.complianceQuestionnaires;
    this.conditions = clients.conditions;
    this.consents = clients.consents;
    this.contracts = clients.contracts;
    this.coverages = clients.coverages;
    this.dialogues = clients.dialogues;
    this.districts = clients.districts;
    this.documentReferences = clients.documentReferences;
    this.documentTransmissions = clients.documentTransmissions;
    this.encounters = clients.encounters;
    this.episodeOfCares = clients.episodeOfCares;
    this.forms = clients.forms;
    this.grantSets = clients.grantSets;
    this.groups = clients.groups;
    this.groupNoteDefinitions = clients.groupNoteDefinitions;
    this.healthcareServices = clients.healthcareServices;
    this.itineraries = clients.itineraries;
    this.listings = clients.listings;
    this.locations = clients.locations;
    this.media = clients.media;
    this.medications = clients.medications;
    this.notes = clients.notes;
    this.noteAudits = clients.noteAudits;
    this.noteDefinitions = clients.noteDefinitions;
    this.noteRules = clients.noteRules;
    this.noteSectionForms = clients.noteSectionForms;
    this.noteTemplates = clients.noteTemplates;
    this.notifications = clients.notifications;
    this.observableRoutines = clients.observableRoutines;
    this.observations = clients.observations;
    this.organizations = clients.organizations;
    this.packets = clients.packets;
    this.packetResponses = clients.packetResponses;
    this.patients = clients.patients;
    this.personalPreferences = clients.personalPreferences;
    this.personalSignatures = clients.personalSignatures;
    this.practicePreferences = clients.practicePreferences;
    this.practitioners = clients.practitioners;
    this.programs = clients.programs;
    this.questionnaires = clients.questionnaires;
    this.questionnaireResponses = clients.questionnaireResponses;
    this.relatedPeople = clients.relatedPeople;
    this.reportCriteria = clients.reportCriteria;
    this.reportDeliveries = clients.reportDeliveries;
    this.schedules = clients.schedules;
    this.scribbles = clients.scribbles;
    this.scripts = clients.scripts;
    this.securityRoles = clients.securityRoles;
    this.securityTokens = clients.securityTokens;
    this.sharedScribbles = clients.sharedScribbles;
    this.signatureConfigurations = clients.signatureConfigurations;
    this.snippets = clients.snippets;
    this.speechRules = clients.speechRules;
    this.textExpansions = clients.textExpansions;
    this.transcriptions = clients.transcriptions;
  }
}
class SecurityTokenHandlerFactory {
  static create(owner, clients, store, options) {
    return new SecurityTokenHandlerImpl(owner, store, clients, options ?? {});
  }
}
const clearTokenSubStatusCodes = [
  knownCodings.subStatusCodes.invalidSecurityToken,
  knownCodings.subStatusCodes.expiredSecurityToken
];
class SecurityTokenHandlerImpl {
  constructor(owner, storage, clients, options) {
    __publicField(this, "injectHeader", (data, headers) => {
      if (!this.storage.token) {
        return data;
      }
      if (headers) {
        headers[headerNames.securityToken] = this.storage.token.value;
      }
      return data;
    });
    __publicField(this, "clearExpiredToken", async (config) => {
      if (this.storage.token && Instant.toDate(this.storage.token.expiration) < /* @__PURE__ */ new Date()) {
        this.storage.clear();
      }
      return config;
    });
    __publicField(this, "detectErrorResponse", async (error) => {
      var _a;
      if (isApolloResponseError(error) && ((_a = error.response.data) == null ? void 0 : _a.subStatus) && clearTokenSubStatusCodes.includes(error.response.data.subStatus)) {
        await this.storage.clear();
      }
      throw error;
    });
    this.owner = owner;
    this.storage = storage;
    this.clients = clients;
    this.options = options;
    Object.values(clients).forEach((client) => {
      if (isBasicClient(client)) {
        client.client.addRequestTransformer(this.injectHeader);
        client.client.useRequestInterceptor(this.clearExpiredToken);
        client.client.useResponseInterceptor(void 0, this.detectErrorResponse);
      }
    });
  }
  get hasToken() {
    return !!this.storage.token;
  }
  get expiration() {
    var _a;
    return ((_a = this.storage.token) == null ? void 0 : _a.expiration) ?? null;
  }
  get patientsAllowed() {
    var _a;
    return ((_a = this.storage.token) == null ? void 0 : _a.patientsAllowed) ?? [];
  }
  async openPatient(patient, reason) {
    await this.openPatients([patient], reason);
  }
  async openPatients(patients, reason) {
    var _a, _b, _c, _d;
    if (this.storage.token) {
      const token = await this.fetch(this.storage.token.value);
      if (token) {
        const newPatients = patients.filter((p) => !token.patientAllowances.some((a) => a.id === p.id));
        token.patientAllowances = [...token.patientAllowances, ...newPatients];
        (_b = (_a = this.options).onOpen) == null ? void 0 : _b.call(_a, newPatients, reason);
        const updatedToken = await this.clients.securityTokens.update(token);
        await this.store(updatedToken);
        return;
      }
    }
    (_d = (_c = this.options).onOpen) == null ? void 0 : _d.call(_c, patients, reason);
    await this.store(await this.createNew(patients));
  }
  async closePatient(patient) {
    await this.closePatients([patient]);
  }
  async closePatients(patients) {
    var _a, _b;
    if (!this.storage.token) {
      return;
    }
    const token = await this.fetch(this.storage.token.value);
    if (token) {
      (_b = (_a = this.options).onClose) == null ? void 0 : _b.call(_a, patients);
      token.patientAllowances = token.patientAllowances.filter((a) => patients.some((p) => p.id === a.id));
      if (token.patientAllowances.length > 0) {
        const updatedToken = await this.clients.securityTokens.update(token);
        await this.store(updatedToken);
        return;
      }
      await this.clients.securityTokens.deleteById(token.id);
    }
    await this.storage.clear();
  }
  async createNew(patientAllowances) {
    return await this.clients.securityTokens.create({
      expiration: Instant.fromDateTime(DateTime.now().plus({ hours: 24 })),
      patientAllowances,
      owner: this.owner
    });
  }
  async fetch(value) {
    const parts = value.split(":");
    if (parts.length !== 2) {
      return void 0;
    }
    try {
      return await this.clients.securityTokens.fetchById(parts[0], parts[1]);
    } catch {
      return void 0;
    }
  }
  async store(securityToken) {
    var _a;
    const token = {
      value: `${securityToken.id}:${(_a = securityToken.meta) == null ? void 0 : _a.versionId}`,
      expiration: securityToken.expiration,
      patientsAllowed: securityToken.patientAllowances
    };
    await this.storage.store(token);
  }
}
function isBasicClient(item) {
  return item && "client" in item;
}
function defaultHash(item) {
  return cyrb53(safeStringify(item));
}
class MemoryStorage {
  constructor(buffer, hasher, synchronousEvents) {
    __publicField(this, "itemsChangeHandlers", /* @__PURE__ */ new Map());
    __publicField(this, "itemSetHandlers", /* @__PURE__ */ new Map());
    __publicField(this, "itemRemovedHandlers", /* @__PURE__ */ new Map());
    __publicField(this, "setHandlers", /* @__PURE__ */ new Map());
    __publicField(this, "removedHandlers", /* @__PURE__ */ new Map());
    __publicField(this, "clearHandlers", /* @__PURE__ */ new Map());
    __publicField(this, "hashes");
    __publicField(this, "snapshot", /* @__PURE__ */ new Map());
    __publicField(this, "queuedItemsChange", null);
    __publicField(this, "itemsChangeTimer", null);
    __publicField(this, "subscriptionIndex", 0);
    __publicField(this, "fireItemsChange", () => {
      const delta = this.queuedItemsChange;
      this.queuedItemsChange = null;
      const oldItems = this.snapshot;
      const items = this.buffer;
      this.snapshot = new Map(items);
      if (this.itemsChangeHandlers.size === 0 || delta === null) {
        return;
      }
      if (delta.added.size === 0 && delta.updated.size === 0 && delta.removed.size === 0) {
        return;
      }
      this.itemsChangeHandlers.forEach((handler) => handler(items, oldItems, delta));
    });
    this.buffer = buffer;
    this.hasher = hasher;
    this.synchronousEvents = synchronousEvents;
    this.hashes = hashEntries(buffer, hasher);
  }
  static create(options = {}) {
    return new MemoryStorage(
      options.buffer ? new Map(options.buffer) : /* @__PURE__ */ new Map(),
      options.hasher ?? defaultHash,
      options.synchronousEvents ?? false
    );
  }
  onItemsChange(handler) {
    const index = this.subscriptionIndex++;
    this.itemsChangeHandlers.set(String(index), handler);
    return () => this.itemsChangeHandlers.delete(String(index));
  }
  onSet(handler) {
    const index = this.subscriptionIndex++;
    this.setHandlers.set(index, handler);
    return () => this.setHandlers.delete(index);
  }
  onRemove(handler) {
    const index = this.subscriptionIndex++;
    this.removedHandlers.set(index, handler);
    return () => this.removedHandlers.delete(index);
  }
  onClear(handler) {
    const index = this.subscriptionIndex++;
    this.clearHandlers.set(index, handler);
    return () => this.clearHandlers.delete(index);
  }
  onItemSet(key, handler) {
    const index = this.subscriptionIndex++;
    let handlers = this.itemSetHandlers.get(key);
    if (!handlers) {
      handlers = /* @__PURE__ */ new Map();
      this.itemSetHandlers.set(key, handlers);
    }
    handlers.set(index, handler);
    return () => handlers == null ? void 0 : handlers.delete(index);
  }
  onItemRemove(key, handler) {
    const index = this.subscriptionIndex++;
    let handlers = this.itemRemovedHandlers.get(key);
    if (!handlers) {
      handlers = /* @__PURE__ */ new Map();
      this.itemRemovedHandlers.set(key, handlers);
    }
    handlers.set(index, handler);
    return () => handlers == null ? void 0 : handlers.delete(index);
  }
  size() {
    return this.buffer.size;
  }
  keys() {
    return Array.from(this.buffer.keys());
  }
  values() {
    return Array.from(this.buffer.values());
  }
  entries() {
    return this.buffer;
  }
  has(key) {
    return this.buffer.has(key);
  }
  get(key) {
    return this.buffer.get(key) ?? null;
  }
  hash(key) {
    return this.hashes.get(key) ?? null;
  }
  set(key, value) {
    const oldHash = this.hashes.get(key);
    const newHash = this.hasher(value);
    if (oldHash && oldHash === newHash) {
      return;
    }
    this.buffer.set(key, value);
    this.hashes.set(key, newHash);
    const delta = /* @__PURE__ */ new Map([[key, value]]);
    this.fireItemSet(key, value);
    this.queueItemsChange({
      added: oldHash ? void 0 : delta,
      updated: oldHash ? delta : void 0
    });
  }
  delete(key) {
    const item = this.get(key);
    if (!item) {
      return null;
    }
    this.buffer.delete(key);
    this.hashes.delete(key);
    this.fireItemRemoved(key, item);
    this.queueItemsChange({ removed: /* @__PURE__ */ new Map([[key, item]]) });
    return item;
  }
  clear() {
    if (this.buffer.size === 0) {
      return;
    }
    const removed = new Map(this.buffer);
    this.buffer.clear();
    this.hashes.clear();
    this.snapshot.clear();
    removed.forEach((item, key) => this.fireItemRemoved(key, item));
    this.queueItemsChange({ removed });
    this.fireClear();
  }
  fireSet(key) {
    const item = this.get(key);
    if (item) {
      this.fireItemSet(key, item);
    }
  }
  queueItemsChange(delta) {
    if (this.itemsChangeHandlers.size === 0) {
      return;
    }
    if (this.itemsChangeTimer) {
      clearTimeout(this.itemsChangeTimer);
    }
    const eventDelta = {
      added: delta.added ?? /* @__PURE__ */ new Map(),
      updated: delta.updated ?? /* @__PURE__ */ new Map(),
      removed: delta.removed ?? /* @__PURE__ */ new Map()
    };
    this.queuedItemsChange = this.queuedItemsChange ? merge(this.queuedItemsChange, eventDelta) : eventDelta;
    if (this.synchronousEvents) {
      this.fireItemsChange();
    } else {
      this.itemsChangeTimer = setTimeout(this.fireItemsChange, 0);
    }
  }
  fireItemSet(key, item) {
    var _a;
    (_a = this.itemSetHandlers.get(key)) == null ? void 0 : _a.forEach((handler) => handler(item));
    this.setHandlers.forEach((handler) => handler(key, item));
  }
  fireItemRemoved(key, item) {
    var _a;
    (_a = this.itemRemovedHandlers.get(key)) == null ? void 0 : _a.forEach((handler) => handler());
    this.removedHandlers.forEach((handler) => handler(key, item));
  }
  fireClear() {
    this.clearHandlers.forEach((handler) => handler());
  }
}
function merge(previous, delta) {
  const added = /* @__PURE__ */ new Map();
  const updated = /* @__PURE__ */ new Map();
  const removed = /* @__PURE__ */ new Map();
  previous.added.forEach((item, key) => {
    if (!delta.added.has(key) && !delta.removed.has(key)) {
      added.set(key, item);
    }
  });
  previous.updated.forEach((item, key) => {
    if (!delta.added.has(key) && !delta.updated.has(key) && !delta.removed.has(key)) {
      updated.set(key, item);
    }
  });
  previous.removed.forEach((item, key) => {
    if (!delta.added.has(key) && !delta.updated.has(key) && !delta.removed.has(key)) {
      removed.set(key, item);
    }
  });
  delta.added.forEach((item, key) => {
    added.set(key, item);
  });
  delta.updated.forEach((item, key) => {
    if (previous.added.has(key)) {
      added.set(key, item);
    } else {
      updated.set(key, item);
    }
  });
  delta.removed.forEach((item, key) => {
    removed.set(key, item);
  });
  return { added, updated, removed };
}
function hashEntries(source, hasher) {
  const hashes = /* @__PURE__ */ new Map();
  source.forEach((value, key) => {
    hashes.set(key, hasher(value));
  });
  return hashes;
}
class BufferStorage {
  constructor(store, storage, debounceMs) {
    __publicField(this, "collectionSymbol", Symbol());
    __publicField(this, "queue");
    __publicField(this, "debounceMs");
    this.store = store;
    this.storage = storage;
    this.queue = new AsyncQueue();
    this.debounceMs = debounceMs;
  }
  static async create(options) {
    const buffer = await options.storage.getAll();
    const store = MemoryStorage.create({
      buffer,
      ...options
    });
    return new BufferStorage(
      store,
      options.storage,
      options.debounceMs
    );
  }
  onItemsChange(handler) {
    return this.store.onItemsChange(handler);
  }
  onSet(handler) {
    return this.store.onSet(handler);
  }
  onRemove(handler) {
    return this.store.onRemove(handler);
  }
  onClear(handler) {
    return this.store.onClear(handler);
  }
  onItemSet(key, handler) {
    return this.store.onItemSet(key, handler);
  }
  onItemRemove(key, handler) {
    return this.store.onItemRemove(key, handler);
  }
  size() {
    return this.store.size();
  }
  keys() {
    return this.store.keys();
  }
  values() {
    return this.store.values();
  }
  entries() {
    return this.store.entries();
  }
  has(key) {
    return this.store.has(key);
  }
  get(key) {
    return this.store.get(key);
  }
  async getOriginal(key) {
    return await this.storage.getItem(key);
  }
  hash(key) {
    return this.store.hash(key);
  }
  set(key, value) {
    this.store.set(key, value);
    this.queue.queue(key, this.debounceMs, async (item) => {
      await this.queue.waitForQueued(this.collectionSymbol);
      await this.storage.setItem(key, item);
    }, value);
  }
  delete(key) {
    const item = this.store.delete(key);
    if (!item) {
      return null;
    }
    this.queue.queue(key, this.debounceMs, async () => {
      await this.queue.waitForQueued(this.collectionSymbol);
      await this.storage.removeItem(key);
    });
    return item;
  }
  clear() {
    if (this.size() === 0) {
      return;
    }
    this.store.clear();
    this.queue.cancelAll();
    this.queue.queue(this.collectionSymbol, this.debounceMs, () => this.storage.clear());
  }
  /**
   * Cancels all pending writes.  Can be used to cancel all async operations that were still pending.
   * May be useful for unit test scenarios.
   */
  cancelPendingWrites() {
    this.queue.cancelAll();
  }
  /**
   * Waits until all currently pending writes have been completed.  The returned Promise
   * resolves immediately if there are no outstanding writes.  Will not wait for writes
   * that were added after the method is called.
   */
  async waitForPendingWrites() {
    await this.queue.resolveAllQueued();
  }
  fireSet(key) {
    this.store.fireSet(key);
  }
}
class StoragePartition {
  constructor(storage, partition, chunkSize) {
    __publicField(this, "storage");
    __publicField(this, "chunksIndexKey");
    __publicField(this, "partitionKey");
    __publicField(this, "chunkSize");
    this.chunksIndexKey = `partition-chunks-${partition}`;
    this.partitionKey = `partition-${partition}`;
    this.storage = storage;
    this.chunkSize = chunkSize;
  }
  /**
   * Creates a storage partition.
   * @param storage The storage medium to store the data.
   * @param partition The value of the partition to store the data within.
   * @param chunkSize The maximum number of items to store in an individual chunk entry in the storage medium.  Defaults to 100.
   */
  static create(storage, partition, chunkSize = 100) {
    const store = new StoragePartition(storage, partition, chunkSize);
    return new Proxy(store, {
      get: (target, name) => {
        if (name in target) {
          return target[name];
        }
        return target.readAllChunks().get(String(name));
      }
    });
  }
  get length() {
    const chunks = this.getChunkIndexes();
    return chunks.reduce((sum, chunk) => sum + chunk.size, 0);
  }
  clear() {
    for (const chunkKey of this.getChunkKeys()) {
      this.storage.removeItem(this.getChunkEntryKey(chunkKey));
    }
    this.storage.removeItem(this.chunksIndexKey);
  }
  getAll() {
    return this.readAllChunks();
  }
  getItem(key) {
    for (const chunkKey of this.getChunkKeys()) {
      const chunkData = this.getChunk(chunkKey);
      const found = chunkData.get(key);
      if (found) {
        return found;
      }
    }
    return null;
  }
  key(index) {
    const chunks = this.getChunkIndexes();
    let chunkIndex = 0;
    while (index >= 0 && chunkIndex < chunks.length) {
      const chunk = chunks[chunkIndex];
      if (index < chunk.size) {
        const data = this.getChunk(chunk.key);
        return Array.from(data.values())[index] ?? null;
      }
      index -= chunk.size;
      chunkIndex++;
    }
    return null;
  }
  removeItem(key) {
    for (const chunkKey of this.getChunkKeys()) {
      const chunkData = this.getChunk(chunkKey);
      if (chunkData.delete(key)) {
        this.saveChunk(chunkKey, chunkData);
        return;
      }
    }
  }
  setItem(key, value) {
    const chunkIndexes = this.getChunkIndexes();
    for (const chunkIndex of chunkIndexes) {
      const chunkData = this.getChunk(chunkIndex.key);
      if (chunkData.has(key)) {
        chunkData.set(key, value);
        this.saveChunk(chunkIndex.key, chunkData);
        return;
      }
    }
    const lastChunkIndex = chunkIndexes.length > 0 ? chunkIndexes[chunkIndexes.length - 1] : null;
    if (lastChunkIndex && lastChunkIndex.size < this.chunkSize) {
      const lastChunk = this.getChunk(lastChunkIndex.key);
      lastChunk.set(key, value);
      this.saveChunk(lastChunkIndex.key, lastChunk);
    } else {
      const chunkData = /* @__PURE__ */ new Map();
      chunkData.set(key, value);
      this.saveChunk(chunkIndexes.length, chunkData);
    }
  }
  getChunk(chunkKey) {
    const json = this.storage.getItem(this.getChunkEntryKey(chunkKey));
    const data = json ? JSON.parse(json) : [];
    return new Map(data);
  }
  saveChunk(chunkKey, data) {
    const json = safeStringify(Array.from(data.entries()));
    this.storage.setItem(this.getChunkEntryKey(chunkKey), json);
    const chunkSize = Array.from(data.keys()).length;
    const index = this.getChunkIndexes();
    if (index[chunkKey]) {
      index[chunkKey].size = chunkSize;
    } else {
      index.push({ key: index.length, size: chunkSize });
    }
    this.saveChunkIndexes(index);
  }
  getChunkKeys() {
    const chunks = this.getChunkIndexes();
    return chunks.map((c) => c.key).sort();
  }
  getChunkIndexes() {
    const json = this.storage.getItem(this.chunksIndexKey);
    return json ? JSON.parse(json) : [];
  }
  saveChunkIndexes(index) {
    const json = safeStringify(index);
    this.storage.setItem(this.chunksIndexKey, json);
  }
  readAllChunks() {
    const data = /* @__PURE__ */ new Map();
    this.getChunkKeys().forEach((chunkKey) => {
      const chunkData = this.getChunk(chunkKey);
      chunkData.forEach((value, key) => {
        data.set(key, value);
      });
    });
    return data;
  }
  getChunkEntryKey(chunkKey) {
    return `${this.partitionKey}_${chunkKey}`;
  }
}
class StorageAsyncStorage {
  constructor(storage) {
    __publicField(this, "storage");
    this.storage = storage;
  }
  getAll() {
    const entries = this.storage.getAll();
    const items = /* @__PURE__ */ new Map();
    entries.forEach((value, key) => {
      items.set(key, JSON.parse(value));
    });
    return Promise.resolve(items);
  }
  getItem(key) {
    const json = this.storage.getItem(key);
    return Promise.resolve(json !== null ? JSON.parse(json) : null);
  }
  setItem(key, value) {
    return Promise.resolve(this.storage.setItem(key, safeStringify(value)));
  }
  removeItem(key) {
    return Promise.resolve(this.storage.removeItem(key));
  }
  clear() {
    return Promise.resolve(this.storage.clear());
  }
}
class StorageAsyncDatabase {
  /**
   * Constructs the storage.
   * @param storage The backing storage.
   * @param partitionKey The key that all data will be stored to within the `storage`.
   * @param chunkSize The maximum number of items to store in an individual chunk entry in the storage medium.  Defaults to 100.
   */
  constructor(storage, partitionKey = "apollo_offline", chunkSize) {
    __publicField(this, "storage");
    __publicField(this, "partitionKey");
    __publicField(this, "chunkSize");
    this.storage = storage;
    this.partitionKey = partitionKey;
    this.chunkSize = chunkSize;
  }
  create(partitions) {
    const result = {};
    partitions.forEach((partition) => {
      const storagePartition = StoragePartition.create(this.storage, `${this.partitionKey}_${partition}`, this.chunkSize);
      result[partition] = new StorageAsyncStorage(storagePartition);
    });
    return Promise.resolve(result);
  }
}
function createKey(partition, resourceId) {
  return `${partition ?? ""}:${resourceId}`;
}
function splitKey(key) {
  const [partition, resourceId] = key.split(":");
  return {
    partition: partition ? partition : void 0,
    resourceId
  };
}
function createHistoricalKey(partition, resourceId, versionId) {
  return `${partition ?? ""}:${resourceId}:${versionId}`;
}
function createReferenceKey(resourceType, partition, resourceId) {
  return `${resourceType}:${partition ?? ""}:${resourceId}`;
}
function splitReferenceKey(key) {
  const [resourceType, partition, resourceId] = key.split(":");
  return {
    resourceType,
    partition: partition ? partition : void 0,
    resourceId
  };
}
const retryDelayMs = 2e3;
class ClientWriteQueue {
  constructor(client, store, historical, upserts, deletes, uploads, failures, isOnline, expander, onError, failureEvictionTimeout) {
    __publicField(this, "writeQueue");
    __publicField(this, "expansionQueue");
    __publicField(this, "itemSavingHandlers", /* @__PURE__ */ new Map());
    __publicField(this, "itemQueuedHandlers", /* @__PURE__ */ new Map());
    __publicField(this, "itemUpsertedHandlers", /* @__PURE__ */ new Map());
    __publicField(this, "itemDeletedHandlers", /* @__PURE__ */ new Map());
    __publicField(this, "upsertedHandlers", /* @__PURE__ */ new Map());
    __publicField(this, "deletedHandlers", /* @__PURE__ */ new Map());
    __publicField(this, "itemFailedHandlers", /* @__PURE__ */ new Map());
    __publicField(this, "failedHandlers", /* @__PURE__ */ new Map());
    __publicField(this, "queueChangeHandlers", /* @__PURE__ */ new Map());
    __publicField(this, "queuedHandlers", /* @__PURE__ */ new Map());
    __publicField(this, "savingHandlers", /* @__PURE__ */ new Map());
    __publicField(this, "subscriptionIndex", 0);
    this.client = client;
    this.store = store;
    this.historical = historical;
    this.upserts = upserts;
    this.deletes = deletes;
    this.uploads = uploads;
    this.failures = failures;
    this.isOnline = isOnline;
    this.expander = expander;
    this.onError = onError;
    this.failureEvictionTimeout = failureEvictionTimeout;
    this.expansionQueue = new AsyncQueue();
    this.writeQueue = new AsyncQueue();
    upserts.onItemsChange(() => this.handleQueueChange());
    deletes.onItemsChange(() => this.handleQueueChange());
    failures.onItemsChange(() => this.handleQueueChange());
  }
  onItemSaving(key, handler) {
    const index = this.subscriptionIndex++;
    let handlers = this.itemSavingHandlers.get(key);
    if (!handlers) {
      handlers = /* @__PURE__ */ new Map();
      this.itemSavingHandlers.set(key, handlers);
    }
    handlers.set(index, handler);
    return () => handlers == null ? void 0 : handlers.delete(index);
  }
  onItemUpserted(key, handler) {
    const index = this.subscriptionIndex++;
    let handlers = this.itemUpsertedHandlers.get(key);
    if (!handlers) {
      handlers = /* @__PURE__ */ new Map();
      this.itemUpsertedHandlers.set(key, handlers);
    }
    handlers.set(index, handler);
    return () => handlers == null ? void 0 : handlers.delete(index);
  }
  onItemUpsertFailed(key, handler) {
    const index = this.subscriptionIndex++;
    let handlers = this.itemFailedHandlers.get(key);
    if (!handlers) {
      handlers = /* @__PURE__ */ new Map();
      this.itemFailedHandlers.set(key, handlers);
    }
    handlers.set(index, handler);
    return () => handlers == null ? void 0 : handlers.delete(index);
  }
  onItemDeleted(key, handler) {
    const index = this.subscriptionIndex++;
    let handlers = this.itemDeletedHandlers.get(key);
    if (!handlers) {
      handlers = /* @__PURE__ */ new Map();
      this.itemDeletedHandlers.set(key, handlers);
    }
    handlers.set(index, handler);
    return () => handlers == null ? void 0 : handlers.delete(index);
  }
  onItemQueued(key, handler) {
    const index = this.subscriptionIndex++;
    let handlers = this.itemQueuedHandlers.get(key);
    if (!handlers) {
      handlers = /* @__PURE__ */ new Map();
      this.itemQueuedHandlers.set(key, handlers);
    }
    handlers.set(index, handler);
    return () => handlers == null ? void 0 : handlers.delete(index);
  }
  onUpserted(handler) {
    const index = this.subscriptionIndex++;
    this.upsertedHandlers.set(index, handler);
    return () => this.upsertedHandlers.delete(index);
  }
  onUpsertFailed(handler) {
    const index = this.subscriptionIndex++;
    this.failedHandlers.set(index, handler);
    return () => this.failedHandlers.delete(index);
  }
  onDeleted(handler) {
    const index = this.subscriptionIndex++;
    this.deletedHandlers.set(index, handler);
    return () => this.deletedHandlers.delete(index);
  }
  onQueued(handler) {
    const index = this.subscriptionIndex++;
    this.queuedHandlers.set(index, handler);
    return () => this.queuedHandlers.delete(index);
  }
  onSaving(handler) {
    const index = this.subscriptionIndex++;
    this.savingHandlers.set(index, handler);
    return () => this.savingHandlers.delete(index);
  }
  onQueueChange(handler) {
    const index = this.subscriptionIndex++;
    this.queueChangeHandlers.set(index, handler);
    return () => this.queueChangeHandlers.delete(index);
  }
  queued() {
    return {
      upserts: Array.from(this.upserts.values()),
      deletes: Array.from(this.deletes.values()),
      failedUpserts: Array.from(this.failures.values())
    };
  }
  isQueued(key) {
    return this.upserts.has(key) || this.deletes.has(key);
  }
  /**
   * Update the store with a version of the resource that was fresh from an API call.
   */
  freshen(fresh) {
    var _a, _b, _c, _d;
    const key = createKey(fresh.partition ?? "", fresh.id);
    if ((_a = fresh.meta) == null ? void 0 : _a.versionId) {
      const historicalKey = createHistoricalKey(fresh.partition, fresh.id, fresh.meta.versionId);
      this.historical.set(historicalKey, fresh);
    }
    const queuedUpsert = this.upserts.get(key);
    if (queuedUpsert) {
      if (equalsQueued(fresh, queuedUpsert)) {
        this.upserts.delete(key);
        this.store.set(key, fresh);
      } else {
        this.upserts.set(key, { lastKnownSaved: fresh, hash: queuedUpsert.hash, upsert: queuedUpsert.upsert });
        this.store.set(key, queuedUpsert.upsert);
      }
    } else {
      const queuedDelete = this.deletes.get(key);
      if (queuedDelete) {
        this.deletes.set(key, { lastKnownSaved: fresh, resourceId: queuedDelete.resourceId, key: queuedDelete.key });
        this.store.delete(key);
      } else {
        const existingVersionId = (_c = (_b = this.store.get(key)) == null ? void 0 : _b.meta) == null ? void 0 : _c.versionId;
        const freshVersionId = (_d = fresh.meta) == null ? void 0 : _d.versionId;
        if (!existingVersionId || !freshVersionId || existingVersionId <= freshVersionId) {
          const queuedUpload = this.uploads.get(key);
          if (queuedUpload) {
            appendContent(fresh, queuedUpload);
          }
          this.store.set(key, fresh);
        }
      }
    }
  }
  /**
   * Queue an upsert (create or update).
   */
  upsert(resource, delayMs) {
    this.upsertAsync(resource, delayMs, false);
  }
  /**
   * Queue an upsert (create or update).
   */
  upsertAsync(resource, delayMs, awaiting = true) {
    return new Promise((resolve, reject) => {
      const key = createKey(resource.partition ?? "", resource.id);
      const wasQueued = this.isQueued(key);
      this.deletes.delete(key);
      const existingHash = this.store.hash(key);
      const newHash = hashResource(resource);
      if (existingHash && existingHash === newHash) {
        resolve();
        return;
      }
      const startUpsert = (upsert) => {
        var _a;
        this.forceFresh(upsert);
        const existingUpsert = this.upserts.get(key);
        let lastKnownSaved = ((_a = this.deletes.get(key)) == null ? void 0 : _a.lastKnownSaved) ?? (existingUpsert == null ? void 0 : existingUpsert.lastKnownSaved) ?? this.store.get(key);
        if (lastKnownSaved === upsert) {
          lastKnownSaved = null;
        }
        this.store.set(key, upsert);
        if (existingUpsert && equalsQueued(upsert, existingUpsert)) {
          resolve();
          return;
        }
        const queueable = {
          lastKnownSaved: lastKnownSaved && lastKnownSaved.meta ? lastKnownSaved : null,
          hash: newHash,
          upsert: cloneResource(upsert, true)
        };
        this.upserts.set(key, queueable);
        if (!wasQueued) {
          this.fireQueued(key, true);
        }
        this.writeQueue.queue(key, delayMs ?? 0, async () => {
          try {
            await this.performUpsert(key, awaiting);
            this.forceFresh(upsert);
            resolve();
          } catch (error) {
            reject(error);
          }
        });
      };
      if (!this.expander.applyExpansions(resource, true)) {
        this.expansionQueue.queue(key, async () => {
          startUpsert(await this.expander.applyExpansions(resource));
        });
      } else {
        startUpsert(resource);
      }
    });
  }
  uploadContent(key, request) {
    this.uploadContentAsync(key, request, false);
  }
  uploadContentAsync(key, request, awaiting = true) {
    return new Promise((resolve, reject) => {
      this.uploads.delete(key);
      const queueable = {
        content: typeof request.content === "string" ? request.content : convertBufferToBase64(request.content, request.contentType),
        contentType: request.contentType,
        key
      };
      this.uploads.set(key, queueable);
      const resource = this.store.get(key);
      if (resource) {
        appendContent(resource, queueable);
        this.store.set(key, resource);
      }
      this.writeQueue.queue(key, async () => {
        try {
          await this.performUpload(key, awaiting);
          resolve();
        } catch (error) {
          reject(error);
        }
      });
    });
  }
  delete(key) {
    this.deleteAsync(key, false);
  }
  deleteAsync(key, awaiting = true) {
    if (!this.client.deleteById) {
      throw new Error(`Client ${this.client.resourceType} does not support delete.`);
    }
    return new Promise((resolve, reject) => {
      var _a, _b, _c;
      const wasQueued = this.isQueued(key);
      this.upserts.delete(key);
      this.expansionQueue.cancel(key);
      const lastKnownSaved = ((_a = this.deletes.get(key)) == null ? void 0 : _a.lastKnownSaved) ?? ((_b = this.upserts.get(key)) == null ? void 0 : _b.lastKnownSaved) ?? this.store.get(key);
      const storeItem = this.store.get(key);
      this.store.delete(key);
      if (lastKnownSaved && (!lastKnownSaved.meta || lastKnownSaved.meta.isDeleted)) {
        return;
      }
      if (storeItem && ((_c = storeItem.meta) == null ? void 0 : _c.isDeleted) !== true) {
        storeItem.meta = {
          ...storeItem.meta,
          isDeleted: true,
          lastModified: Instant.now()
        };
      }
      const { resourceId } = splitKey(key);
      const queueable = {
        lastKnownSaved,
        resourceId,
        key
      };
      this.deletes.set(key, queueable);
      if (!wasQueued) {
        this.fireQueued(key, true);
      }
      this.writeQueue.queue(key, async () => {
        try {
          await this.performDelete(key, awaiting);
          resolve();
        } catch (error) {
          reject(error);
        }
      });
    });
  }
  /**
   * Queues up all changes made while offline.  If `isOnline` does not report true
   * at the time of calling the method, changes will remain queued.
   */
  queueOfflineChanges() {
    this.upserts.keys().forEach((key) => {
      this.writeQueue.queue(key, () => this.performUpsert(key, false));
    });
    this.deletes.keys().forEach((key) => {
      this.writeQueue.queue(key, () => this.performDelete(key, false));
    });
    this.uploads.keys().forEach((key) => {
      this.writeQueue.queue(key, () => this.performUpload(key, false));
    });
  }
  /**
   * Deletes a specific failure if it exists.
   */
  clearFailure(key) {
    this.failures.delete(key);
  }
  /**
   * Purges all failures based on the `failureEvictionTimeout` setting.
   */
  evictStaleFailures() {
    const now = DateTime.now();
    const staleKeys = [];
    this.failures.entries().forEach((failure, key) => {
      const age = now.diff(DateTime.fromJSDate(failure.lastAttempted)).as("minutes");
      if (age >= this.failureEvictionTimeout) {
        staleKeys.push(key);
      }
    });
    staleKeys.forEach((key) => {
      this.failures.delete(key);
    });
  }
  /**
   * Purges all changes queued while offline.
   */
  purgeOfflineChanges() {
    const keys = /* @__PURE__ */ new Set([
      ...this.upserts.keys(),
      ...this.deletes.keys()
    ]);
    this.upserts.clear();
    this.deletes.clear();
    this.uploads.clear();
    this.failures.clear();
    keys.forEach((key) => this.fireQueued(key, false));
  }
  /**
   * Cancels all pending writes.  Can be used to cancel all async operations that were still pending.
   * May be useful for unit test scenarios.
   */
  cancelPendingWrites() {
    this.writeQueue.cancelAll();
    this.expansionQueue.cancelAll();
    this.upserts.cancelPendingWrites();
    this.deletes.cancelPendingWrites();
    this.uploads.cancelPendingWrites();
    this.failures.cancelPendingWrites();
  }
  /**
   * Waits until all currently pending writes have been completed.  The returned Promise
   * resolves immediately if there are no outstanding writes.  Will not wait for writes
   * that were added after the method is called.
   */
  async waitForPendingWrites() {
    await Promise.all([
      this.writeQueue.resolveAllQueued(),
      this.upserts.waitForPendingWrites(),
      this.deletes.waitForPendingWrites(),
      this.uploads.waitForPendingWrites(),
      this.failures.waitForPendingWrites()
    ]);
  }
  async performUpsert(key, awaiting) {
    const queuedUpsert = this.upserts.get(key);
    if (!queuedUpsert) {
      return;
    }
    if (!await this.isOnline()) {
      return;
    }
    let upsert = queuedUpsert.upsert;
    this.fireSaving(key, true);
    this.forceFresh(upsert);
    try {
      const upserted = await this.client.update(upsert);
      const upsertedKey = createKey(upserted.partition, upserted.id);
      if (key !== upsertedKey) {
        this.fireSaving(key, false);
        this.store.delete(key);
        upsert.partition = upserted.partition;
        upsert.id = upserted.id;
        key = upsertedKey;
      }
      this.fireSaving(key, false);
      await this.handleUpsertComplete(key, upserted, upsert);
    } catch (error) {
      this.fireSaving(key, false);
      if (isTransientError(error)) {
        if (await this.isOnline()) {
          if (awaiting) {
            await this.performUpsert(key, true);
          } else {
            this.writeQueue.queue(key, retryDelayMs, () => this.performUpsert(key, false));
          }
        } else {
          this.writeQueue.cancelAll();
        }
      } else {
        if (isUnauthorizedError(error)) {
          this.writeQueue.cancelAll();
        } else {
          await this.handleSaveError(error, key, "upsert", upsert, queuedUpsert.lastKnownSaved);
        }
        if (awaiting) {
          throw error;
        }
        if (this.onError) {
          this.onError(error, "upsert", upsert);
        } else {
          console.log(error);
        }
      }
    }
  }
  async performDelete(key, awaiting) {
    var _a, _b;
    const queuedDelete = this.deletes.get(key);
    if (!queuedDelete || !this.client.deleteById) {
      return;
    }
    if (!await this.isOnline()) {
      return;
    }
    this.fireSaving(key, true);
    const { partition, resourceId } = splitKey(key);
    try {
      if (queuedDelete.lastKnownSaved) {
        if (partition) {
          await this.client.deleteById(partition, resourceId, (_a = queuedDelete.lastKnownSaved.meta) == null ? void 0 : _a.versionId);
        } else {
          await this.client.deleteById(resourceId, (_b = queuedDelete.lastKnownSaved.meta) == null ? void 0 : _b.versionId);
        }
      } else {
        if (partition) {
          await this.client.deleteById(partition, resourceId);
        } else {
          await this.client.deleteById(resourceId);
        }
      }
      this.fireSaving(key, false);
      this.handleDeleteComplete(key);
    } catch (error) {
      this.fireSaving(key, false);
      if (isNotFoundError(error)) {
        this.handleDeleteComplete(key);
        return;
      }
      if (isTransientError(error)) {
        if (await this.isOnline()) {
          if (awaiting) {
            this.performDelete(key, true);
          } else {
            this.writeQueue.queue(key, retryDelayMs, () => this.performDelete(key, false));
          }
        } else {
          this.writeQueue.cancelAll();
        }
      } else {
        if (isUnauthorizedError(error)) {
          this.writeQueue.cancelAll();
        } else {
          await this.handleSaveError(error, key, "delete", void 0, queuedDelete.lastKnownSaved);
        }
        if (awaiting) {
          throw error;
        }
        if (this.onError) {
          this.onError(error, "delete", void 0);
        } else {
          console.log(error);
        }
      }
    }
  }
  async performUpload(key, awaiting) {
    const queuedUpload = this.uploads.get(key);
    if (!queuedUpload) {
      return;
    }
    if (!await this.isOnline()) {
      return;
    }
    try {
      const { partition, resourceId } = splitKey(key);
      if (partition) {
        await this.client.uploadContent(partition, resourceId, queuedUpload);
      } else {
        await this.client.uploadContent(resourceId, queuedUpload);
      }
      this.uploads.delete(key);
      const saved = partition ? await this.client.fetchById(partition, resourceId) : await this.client.fetchById(resourceId);
      const fresh = await this.expander.applyExpansions(saved, []);
      this.freshen(fresh);
    } catch (error) {
      if (isTransientError(error)) {
        if (await this.isOnline()) {
          if (awaiting) {
            await this.performUpload(key, true);
          } else {
            this.writeQueue.queue(key, retryDelayMs, () => this.performUpload(key, false));
          }
        } else {
          this.writeQueue.cancelAll();
        }
      } else {
        if (isUnauthorizedError(error)) {
          this.writeQueue.cancelAll();
        }
        if (awaiting) {
          throw error;
        }
        if (this.onError) {
          this.onError(error, "upload", void 0);
        } else {
          console.log(error);
        }
      }
    }
  }
  handleDeleteComplete(key) {
    this.failures.delete(key);
    const queuedUpsert = this.upserts.get(key);
    if (queuedUpsert) {
      this.upserts.set(key, { lastKnownSaved: null, hash: queuedUpsert.hash, upsert: queuedUpsert.upsert });
    } else {
      this.store.delete(key);
      this.deletes.delete(key);
      this.fireQueued(key, false);
    }
    this.fireDeleted(key);
  }
  // We may have set a new update while this upsert was occurring,
  // so to avoid losing those changes, we only care to retain the new meta data
  async handleUpsertComplete(key, saved, previous) {
    var _a;
    const fresh = await this.expander.applyExpansions(saved, []);
    this.failures.delete(key);
    if ((_a = saved.meta) == null ? void 0 : _a.versionId) {
      const historicalKey = createHistoricalKey(saved.partition, saved.id, saved.meta.versionId);
      this.historical.set(historicalKey, fresh);
    }
    const queuedUpsert = this.upserts.get(key);
    if (queuedUpsert) {
      if (!equalsQueued(previous, queuedUpsert)) {
        const merged = this.mergeFresh(fresh, previous, queuedUpsert.upsert);
        this.store.set(key, merged);
        this.upserts.set(key, { lastKnownSaved: fresh, hash: hashResource(merged), upsert: merged });
      } else {
        this.store.set(key, fresh);
        this.upserts.delete(key);
        this.fireQueued(key, false);
      }
    } else {
      if (this.deletes.has(key)) {
        this.deletes.set(key, { lastKnownSaved: fresh, resourceId: saved.id, key: createKey(saved.partition ?? "", saved.id) });
      } else {
        this.store.set(key, fresh);
      }
    }
    this.fireUpserted(key, fresh);
  }
  mergeFresh(fresh, previous, current) {
    previous = removeOriginal(cloneResource(previous, false));
    current = removeOriginal(cloneResource(current, false));
    const patching = { ...fresh };
    delete previous.meta;
    delete current.meta;
    const operations = resolveOperationConflicts(patching, Patch.diff(previous, current));
    Patch.apply(patching, operations);
    return patching;
  }
  async handleSaveError(error, key, action, resource, lastKnownSaved) {
    this.upserts.delete(key);
    this.deletes.delete(key);
    this.fireQueued(key, false);
    await this.restoreFresh(key, lastKnownSaved);
    if (action === "upsert" && resource) {
      this.failures.set(key, { hash: hashResource(resource), upsert: resource, lastAttempted: /* @__PURE__ */ new Date() });
      this.fireUpsertFailed(key, resource, error);
    }
  }
  async restoreFresh(key, lastKnownSaved) {
    const { partition, resourceId } = splitKey(key);
    if (await this.isOnline()) {
      try {
        const latest = partition ? await this.client.fetchById(partition, resourceId) : await this.client.fetchById(resourceId);
        const expandedLatest = await this.expander.applyExpansions(latest);
        this.restore(key, expandedLatest);
        return;
      } catch (error) {
        if (isNotFoundError(error)) {
          this.store.delete(key);
          return;
        }
      }
    }
    if (lastKnownSaved) {
      this.restore(key, lastKnownSaved);
    }
  }
  restore(key, resource) {
    this.store.set(key, resource);
    this.upserts.set(key, { lastKnownSaved: resource, hash: hashResource(resource), upsert: resource });
    this.upserts.delete(key);
  }
  // Force freshness on upsert in case their copy is behind latest
  forceFresh(resource) {
    var _a, _b, _c, _d, _e;
    const key = createKey(resource.partition ?? "", resource.id);
    const existing = this.store.get(key);
    if (existing) {
      resource.meta = {
        ...existing.meta,
        // Allow local client to force a newer lastModified
        lastModified: ((_a = resource.meta) == null ? void 0 : _a.lastModified) && ((_b = existing.meta) == null ? void 0 : _b.lastModified) && ((_c = resource.meta) == null ? void 0 : _c.lastModified) > ((_d = existing.meta) == null ? void 0 : _d.lastModified) ? resource.meta.lastModified : (_e = existing.meta) == null ? void 0 : _e.lastModified
      };
      copyOriginal(existing, resource);
    }
  }
  handleQueueChange() {
    if (this.queueChangeHandlers.size > 0) {
      const queued = this.queued();
      this.queueChangeHandlers.forEach((handler) => handler(queued));
    }
  }
  fireSaving(key, saving) {
    var _a;
    const { resourceId } = splitKey(key);
    (_a = this.itemSavingHandlers.get(key)) == null ? void 0 : _a.forEach((handler) => handler(saving));
    this.savingHandlers.forEach((handler) => handler(resourceId, saving));
  }
  fireQueued(key, queued) {
    var _a;
    const { resourceId } = splitKey(key);
    (_a = this.itemQueuedHandlers.get(key)) == null ? void 0 : _a.forEach((handler) => handler(queued));
    this.queuedHandlers.forEach((handler) => handler(resourceId, queued));
  }
  fireUpserted(key, item) {
    var _a;
    (_a = this.itemUpsertedHandlers.get(key)) == null ? void 0 : _a.forEach((handler) => handler(item));
    this.upsertedHandlers.forEach((handler) => handler(item));
  }
  fireUpsertFailed(key, item, error) {
    var _a;
    (_a = this.itemFailedHandlers.get(key)) == null ? void 0 : _a.forEach((handler) => handler(item, error));
    this.failedHandlers.forEach((handler) => handler(item, error));
  }
  fireDeleted(key) {
    var _a;
    const { resourceId } = splitKey(key);
    (_a = this.itemDeletedHandlers.get(key)) == null ? void 0 : _a.forEach((handler) => handler());
    this.deletedHandlers.forEach((handler) => handler(resourceId));
  }
}
function isUnauthorizedError(error) {
  return isApolloResponseError(error) && error.response.status === 401;
}
function isNotFoundError(error) {
  return isApolloResponseError(error) && (error.response.status === 404 || error.response.status === 410);
}
function isTransientError(error) {
  return isApolloResponseError(error) ? isTransientResponse(error.response.status) : isApolloRequestError(error);
}
function isTransientResponse(statusCode) {
  switch (statusCode) {
    case 408:
    // Timeout
    case 429:
    // Too Many Requests
    case 449:
      return true;
    default:
      return false;
  }
}
function equalsQueued(resource, queued) {
  const cloned = removeExpansions(resource);
  const clonedHash = defaultHash(cloned);
  if (clonedHash === queued.hash) {
    return true;
  }
  delete cloned.meta;
  const clonedNoMetaHash = defaultHash(cloned);
  if (clonedNoMetaHash === queued.hash) {
    return true;
  }
  const queuedCloned = removeExpansions(queued.upsert);
  delete queuedCloned.meta;
  const queuedNoMetaHash = defaultHash(queuedCloned);
  if (queuedNoMetaHash === clonedNoMetaHash) {
    return true;
  }
  return false;
}
function hashResource(resource) {
  return defaultHash(removeExpansions(resource));
}
function removeExpansions(resource) {
  return cloneResource(resource, false);
}
function cloneResource(value, retainExpandedReferences) {
  if (retainExpandedReferences) {
    return JSON.parse(safeStringify(value));
  }
  const restoreExpansions = trimExpandedReferences$1(value.resourceType, value);
  const cloned = JSON.parse(safeStringify(value));
  restoreExpansions();
  return cloned;
}
function appendContent(resource, content) {
  resource.content = {
    contentType: content.contentType,
    url: content.content,
    hash: cyrb53(content.content).toString(),
    size: content.content.length
  };
}
class ItemView {
  constructor(key, query, queryCount, networkConsumer, store, views, options) {
    __publicField(this, "viewChangeHandlers", /* @__PURE__ */ new Map());
    __publicField(this, "isOnline");
    __publicField(this, "subscriptionIndex", 0);
    __publicField(this, "mergeQueuedChanges", (items, changes) => {
      if (!hasChanges(changes)) {
        return items;
      }
      return this.mergeChanges(items, changes.upserts.map((u) => u.upsert), changes.deletes.map((d) => d.resourceId));
    });
    __publicField(this, "mergeChanges", (items, upserted, removedIds) => {
      var _a, _b, _c, _d;
      if (upserted.length === 0 && removedIds.length === 0) {
        return items;
      }
      const oldView = this.views.get(this.key);
      const offlineFilter = (_b = (_a = this.options) == null ? void 0 : _a.filters) == null ? void 0 : _b.offline;
      const upsertedIds = new Set(upserted.map((d) => d.id));
      const upserts = offlineFilter ? upserted.filter((u) => offlineFilter(u)) : upserted;
      const newView = items.filter((r) => !upsertedIds.has(r.id) && !removedIds.includes(r.id)).concat(upserts);
      if (upsertedIds.size > 0) {
        const offlineSort = (_d = (_c = this.options) == null ? void 0 : _c.orderBy) == null ? void 0 : _d.offline;
        newView.sort(
          offlineSort ? sortWithFallback(offlineSort, oldView ? sortByFixedIds(oldView.ids) : sortById) : oldView ? sortWithFallback(sortByFixedIds(oldView.ids), sortById) : sortById
        );
      } else if (oldView) {
        newView.sort(sortByFixedIds(oldView.ids));
      }
      return newView;
    });
    __publicField(this, "handleQueueChanged", (changes) => {
      if (!hasChanges(changes)) {
        return;
      }
      const oldView = this.buildItems(this.store.values(), false);
      const newView = this.mergeQueuedChanges(oldView, changes);
      const view = this.views.get(this.key);
      const newIds = newView.map((i) => i.id);
      this.views.set(this.key, { ids: newIds, created: (view == null ? void 0 : view.created) ?? /* @__PURE__ */ new Date(), continuationToken: view == null ? void 0 : view.continuationToken });
      if (this.viewChangeHandlers.size > 0) {
        const oldIdSet = new Set((view == null ? void 0 : view.ids) ?? []);
        const newIdSet = new Set(newIds);
        const added = changes.upserts.filter((u) => !oldIdSet.has(u.upsert.id) && newIdSet.has(u.upsert.id)).map((i) => i.upsert);
        const updated = changes.upserts.filter((u) => oldIdSet.has(u.upsert.id) && newIdSet.has(u.upsert.id)).map((i) => i.upsert);
        const removed = changes.upserts.filter((i) => oldIdSet.has(i.upsert.id) && !newIdSet.has(i.upsert.id)).map((i) => i.upsert).concat(changes.deletes.filter((i) => oldIdSet.has(i.resourceId) && i.lastKnownSaved).map((i) => i.lastKnownSaved));
        const viewAffected = added.length > 0 || updated.length > 0 || removed.length > 0;
        if (viewAffected) {
          const viewDelta = { added, updated, removed };
          this.viewChangeHandlers.forEach((handler) => handler(newView, oldView, viewDelta));
        }
      }
    });
    __publicField(this, "handleItemsChanged", (newItems, oldItems, delta) => {
      var _a, _b;
      const offlineFilter = (_b = (_a = this.options) == null ? void 0 : _a.filters) == null ? void 0 : _b.offline;
      const view = this.views.get(this.key);
      const oldView = this.buildView(oldItems);
      const newItemsIds = new Set(newItems.map((i) => i.id));
      const allItems = [...newItems, ...oldItems.filter((i) => !newItemsIds.has(i.id))];
      let newView = this.buildView(allItems);
      if (offlineFilter && view) {
        let currentIds = view.ids;
        const currentIdSet = new Set(currentIds);
        const removedIds = new Set(delta.updated.filter((u) => currentIdSet.has(u.id) && !offlineFilter(u)).map((u) => u.id));
        newView = newView.filter((i) => !removedIds.has(i.id));
        const newIds = currentIds.filter((id) => !removedIds.has(id));
        this.views.set(this.key, { ids: newIds, created: (view == null ? void 0 : view.created) ?? /* @__PURE__ */ new Date(), continuationToken: view == null ? void 0 : view.continuationToken });
      }
      if (this.viewChangeHandlers.size > 0) {
        const added = this.buildItems(delta.added);
        const updated = this.buildItems(delta.updated);
        const removed = this.buildItems(delta.removed);
        const viewAffected = added.length > 0 || delta.updated.length > 0 || removed.length > 0;
        if (viewAffected) {
          const viewDelta = { added, updated, removed };
          this.viewChangeHandlers.forEach((handler) => handler(newView, oldView, viewDelta));
        }
      }
    });
    __publicField(this, "handleOnlineChanged", (isOnline) => {
      this.isOnline = isOnline;
    });
    this.key = key;
    this.query = query;
    this.queryCount = queryCount;
    this.networkConsumer = networkConsumer;
    this.store = store;
    this.views = views;
    this.options = options;
    this.networkConsumer.onOnlineChange(this.handleOnlineChanged);
    this.store.onItemsChange(this.handleItemsChanged);
    this.store.onQueueChange(this.handleQueueChanged);
  }
  get canLoadMore() {
    if (this.isOnline === false) {
      return false;
    }
    const view = this.views.get(this.key);
    if (view === null) {
      return true;
    }
    if (!!view.continuationToken || view.continuationToken === void 0) {
      return true;
    }
    return false;
  }
  get created() {
    const view = this.views.get(this.key);
    return (view == null ? void 0 : view.created) ?? null;
  }
  items() {
    return this.buildView(this.store.values());
  }
  reset() {
    this.isOnline = void 0;
    this.views.delete(this.key);
  }
  async loadTotalCount(abort) {
    var _a, _b;
    return await this.queryCount({
      filters: (_b = (_a = this.options) == null ? void 0 : _a.filters) == null ? void 0 : _b.online,
      abort
    });
  }
  async loadMore(options, abort) {
    var _a, _b, _c, _d, _e, _f, _g, _h;
    const maxItemCount = typeof options === "number" ? options : options == null ? void 0 : options.maxItemCount;
    if (!await this.networkConsumer.isOnline()) {
      this.isOnline = false;
      return;
    }
    this.isOnline = true;
    const view = this.views.get(this.key);
    if (view && view.continuationToken === null) {
      return;
    }
    let ids = (view == null ? void 0 : view.ids) ?? [];
    const created = (view == null ? void 0 : view.created) ?? /* @__PURE__ */ new Date();
    try {
      this.views.set(this.key, { ids, created, continuationToken: null });
      const results = await this.query({
        filters: (_b = (_a = this.options) == null ? void 0 : _a.filters) == null ? void 0 : _b.online,
        orderBy: (_d = (_c = this.options) == null ? void 0 : _c.orderBy) == null ? void 0 : _d.online,
        feedOptions: {
          ...(_e = this.options) == null ? void 0 : _e.feedOptions,
          continuationToken: (view == null ? void 0 : view.continuationToken) ?? void 0,
          maxItemCount
        },
        responseOptions: ((_f = this.options) == null ? void 0 : _f.responseOptions) ? {
          ...this.options.responseOptions
        } : void 0,
        abort,
        ...typeof options === "number" ? {} : options
      });
      const updatedIds = new Set(ids);
      for (const resource of results.results) {
        updatedIds.add(resource.id);
      }
      ids = Array.from(updatedIds);
      const queuedChanges = this.store.queued();
      if (hasChanges(queuedChanges)) {
        const items = [...this.store.values().filter((r) => updatedIds.has(r.id)), ...results.results];
        ids = this.mergeQueuedChanges(items, queuedChanges).map((i) => i.id);
      }
      this.views.set(this.key, { ids, created, continuationToken: results.continuationToken ?? null });
      await this.store.freshen(results.results, ((_h = (_g = this.options) == null ? void 0 : _g.responseOptions) == null ? void 0 : _h.expansions) ?? []);
    } catch (error) {
      if (view) {
        this.views.set(this.key, view);
      } else {
        this.views.delete(this.key);
      }
      throw error;
    }
  }
  onViewChange(handler) {
    const index = this.subscriptionIndex++;
    this.viewChangeHandlers.set(index, handler);
    return () => this.viewChangeHandlers.delete(index);
  }
  buildItems(items, withQueuedChanges = true) {
    var _a, _b, _c;
    const ids = new Set(((_a = this.views.get(this.key)) == null ? void 0 : _a.ids) ?? []);
    const offlineFilter = (_c = (_b = this.options) == null ? void 0 : _b.filters) == null ? void 0 : _c.offline;
    const viewItems = this.isOnline !== false ? ids.size !== 0 ? items.filter((r) => ids.has(r.id)) : [] : offlineFilter ? items.filter(offlineFilter) : items;
    return withQueuedChanges ? this.mergeQueuedChanges(viewItems, this.store.queued()) : viewItems;
  }
  buildView(items) {
    var _a, _b, _c, _d, _e;
    const view = this.buildItems(items);
    const ids = (_a = this.views.get(this.key)) == null ? void 0 : _a.ids;
    if (ids) {
      view.sort(sortByFixedIds(ids));
    } else if ((_c = (_b = this.options) == null ? void 0 : _b.orderBy) == null ? void 0 : _c.offline) {
      view.sort((_e = (_d = this.options) == null ? void 0 : _d.orderBy) == null ? void 0 : _e.offline);
    }
    return view;
  }
}
function hasChanges(changes) {
  return changes.upserts.length > 0 || changes.deletes.length > 0;
}
function sortByFixedIds(ids) {
  return (a, b) => {
    return ids.indexOf(a.id) - ids.indexOf(b.id);
  };
}
function sortById(a, b) {
  return a.id.localeCompare(b.id);
}
function sortWithFallback(sort, fallback) {
  return (a, b) => {
    const comparison = sort(a, b);
    return comparison === 0 ? fallback(a, b) : comparison;
  };
}
const partitionedStoreMethods = [
  "get",
  "fetch",
  "fireSet",
  "onItemSet",
  "onItemRemove",
  "onItemSaving",
  "onItemQueued",
  "onItemUpserted",
  "onItemUpsertFailed",
  "onItemDeleted",
  "isQueued",
  "uploadContent",
  "uploadContentAsync",
  "deleteById",
  "deleteByIdAsync",
  "clearFailure"
];
class Store {
  constructor(client, buffer, historical, views, expander, writeQueue, networkConsumer) {
    __publicField(this, "resourceType");
    __publicField(this, "activeViews", /* @__PURE__ */ new Map());
    __publicField(this, "expansionsQueue", new AsyncQueue());
    __publicField(this, "allowFreshening", true);
    __publicField(this, "freshen", (items) => {
      if (this.allowFreshening) {
        const [expanded, needsExpanding] = this.expander.applyExpansions(items, true);
        if (expanded.length > 0) {
          expanded.forEach(this.handleFetchComplete);
        }
        if (needsExpanding.length > 0) {
          this.expansionsQueue.queue("freshen", async () => {
            await this.expandAndFreshen(needsExpanding, []);
          });
        }
      }
    });
    __publicField(this, "fireSet", (key) => {
      this.buffer.fireSet(key);
    });
    __publicField(this, "expandAndFreshen", async (items, additionalExpansions) => {
      const [expanded, needsExpanding] = this.expander.applyExpansions(items, additionalExpansions, true);
      if (expanded.length > 0) {
        expanded.forEach(this.handleFetchComplete);
      }
      if (needsExpanding.length > 0) {
        const expanded2 = await this.expander.applyExpansions(needsExpanding, additionalExpansions);
        expanded2.forEach(this.handleFetchComplete);
      }
    });
    __publicField(this, "handleFetchComplete", (item) => {
      var _a, _b, _c;
      if ((_a = item.meta) == null ? void 0 : _a.versionId) {
        const historicalKey = createHistoricalKey(item.partition ?? "", item.id, item.meta.versionId);
        this.historical.set(historicalKey, item);
      }
      if (!((_b = item.meta) == null ? void 0 : _b.isHistorical)) {
        (_c = this.writeQueue) == null ? void 0 : _c.freshen(item);
      }
    });
    __publicField(this, "handleOnlineChange", (isOnline) => {
      var _a;
      if (isOnline) {
        (_a = this.writeQueue) == null ? void 0 : _a.queueOfflineChanges();
      }
    });
    this.client = client;
    this.buffer = buffer;
    this.historical = historical;
    this.views = views;
    this.expander = expander;
    this.writeQueue = writeQueue;
    this.networkConsumer = networkConsumer;
    this.resourceType = client.resourceType;
    this.networkConsumer.onOnlineChange(this.handleOnlineChange);
  }
  onItemsChange(handler) {
    return this.buffer.onItemsChange((items, prevItems, delta) => {
      handler(values(items), values(prevItems), {
        added: values(delta.added),
        updated: values(delta.updated),
        removed: values(delta.removed)
      });
    });
  }
  onSet(handler) {
    return this.buffer.onSet((_key, item) => handler(item));
  }
  onRemove(handler) {
    return this.buffer.onRemove((_key, item) => handler(item));
  }
  onQueueChange(handler) {
    var _a;
    return ((_a = this.writeQueue) == null ? void 0 : _a.onQueueChange(handler)) ?? (() => {
    });
  }
  onUpserted(handler) {
    var _a;
    return ((_a = this.writeQueue) == null ? void 0 : _a.onUpserted(handler)) ?? (() => {
    });
  }
  onUpsertFailed(handler) {
    var _a;
    return ((_a = this.writeQueue) == null ? void 0 : _a.onUpsertFailed(handler)) ?? (() => {
    });
  }
  onDeleted(handler) {
    var _a;
    return ((_a = this.writeQueue) == null ? void 0 : _a.onDeleted(handler)) ?? (() => {
    });
  }
  onSaving(handler) {
    var _a;
    return ((_a = this.writeQueue) == null ? void 0 : _a.onSaving(handler)) ?? (() => {
    });
  }
  onQueued(handler) {
    var _a;
    return ((_a = this.writeQueue) == null ? void 0 : _a.onQueued(handler)) ?? (() => {
    });
  }
  onItemSet(key, handler) {
    return this.buffer.onItemSet(key, handler);
  }
  onItemRemove(key, handler) {
    return this.buffer.onItemRemove(key, handler);
  }
  onItemSaving(key, handler) {
    var _a;
    return ((_a = this.writeQueue) == null ? void 0 : _a.onItemSaving(key, handler)) ?? (() => {
    });
  }
  onItemQueued(key, handler) {
    var _a;
    return ((_a = this.writeQueue) == null ? void 0 : _a.onItemQueued(key, handler)) ?? (() => {
    });
  }
  onItemUpserted(key, handler) {
    var _a;
    return ((_a = this.writeQueue) == null ? void 0 : _a.onItemUpserted(key, handler)) ?? (() => {
    });
  }
  onItemUpsertFailed(key, handler) {
    var _a;
    return ((_a = this.writeQueue) == null ? void 0 : _a.onItemUpsertFailed(key, handler)) ?? (() => {
    });
  }
  onItemDeleted(key, handler) {
    var _a;
    return ((_a = this.writeQueue) == null ? void 0 : _a.onItemDeleted(key, handler)) ?? (() => {
    });
  }
  view(options) {
    var _a, _b;
    const viewOptions = {
      ...options
    };
    const onlineFilter = (_a = viewOptions.filters) == null ? void 0 : _a.online;
    const sortFilter = (_b = viewOptions.orderBy) == null ? void 0 : _b.online;
    const expansions = viewOptions.responseOptions && "expansions" in viewOptions.responseOptions ? Array.from(viewOptions.responseOptions.expansions ?? []) : [];
    const viewKey = onlineFilter || sortFilter || expansions.length > 0 ? defaultHash({ filters: onlineFilter, orderBy: sortFilter, expansions }).toString() : "_empty";
    const activeView = this.activeViews.get(viewKey);
    if (activeView) {
      return activeView;
    }
    const store = {
      values: () => this.buffer.values(),
      freshen: this.expandAndFreshen,
      queued: () => this.queued(),
      onClear: (...args) => this.buffer.onClear(...args),
      onItemsChange: (...args) => this.onItemsChange(...args),
      onQueueChange: (...args) => this.onQueueChange(...args)
    };
    const query = async (request) => {
      try {
        this.allowFreshening = false;
        return await this.client.query(request);
      } finally {
        this.allowFreshening = true;
      }
    };
    const queryCount = async (request) => await this.client.queryCount(request);
    const view = new ItemView(viewKey, query, queryCount, this.networkConsumer, store, this.views, viewOptions);
    this.activeViews.set(viewKey, view);
    return view;
  }
  items() {
    return this.buffer.values();
  }
  queued() {
    return this.writeQueue ? this.writeQueue.queued() : { upserts: [], deletes: [], failedUpserts: [] };
  }
  get(key, versionId) {
    if (versionId) {
      const { partition, resourceId } = splitKey(key);
      return this.historical.get(createHistoricalKey(partition, resourceId, versionId));
    } else {
      return this.buffer.get(key);
    }
  }
  async fetch(key, versionIdOrRequestOptions, maybeRequestOptions) {
    const versionId = typeof versionIdOrRequestOptions === "string" ? versionIdOrRequestOptions : void 0;
    const requestOptions = maybeRequestOptions ?? (typeof versionIdOrRequestOptions !== "string" ? versionIdOrRequestOptions : void 0);
    if (versionId) {
      const { partition: partition2, resourceId: resourceId2 } = splitKey(key);
      const cached = this.historical.get(createHistoricalKey(partition2, resourceId2, versionId));
      if (cached) {
        return cached;
      }
    } else {
      const cached = this.buffer.get(key);
      if (cached) {
        return cached;
      }
    }
    const { partition, resourceId } = splitKey(key);
    const item = partition ? versionId ? await this.client.fetchById(partition, resourceId, versionId, requestOptions) : await this.client.fetchById(partition, resourceId, requestOptions) : versionId ? await this.client.fetchById(resourceId, versionId, requestOptions) : await this.client.fetchById(resourceId, requestOptions);
    await this.expandAndFreshen([item], []);
    return item;
  }
  uploadContent(key, request) {
    var _a;
    (_a = this.writeQueue) == null ? void 0 : _a.uploadContent(key, request);
  }
  async uploadContentAsync(key, request) {
    var _a;
    await ((_a = this.writeQueue) == null ? void 0 : _a.uploadContentAsync(key, request));
  }
  expand(referencesParam, options) {
    if (options === true) {
      if (Array.isArray(referencesParam)) {
        return this.expandMultiStore(Array.isArray(referencesParam) ? referencesParam : [referencesParam]);
      }
      return this.expandSingleStore(referencesParam);
    }
    if (Array.isArray(referencesParam)) {
      return this.expandMulti(referencesParam, options);
    }
    return this.expandSingle(referencesParam, options);
  }
  isQueued(key) {
    return this.writeQueue ? this.writeQueue.isQueued(key) : false;
  }
  upsert(resource, delayMs) {
    var _a;
    (_a = this.writeQueue) == null ? void 0 : _a.upsert(resource, delayMs);
  }
  async upsertAsync(resource, delayMs) {
    if (this.writeQueue) {
      await this.writeQueue.upsertAsync(resource, delayMs);
    }
  }
  deleteById(key) {
    var _a;
    (_a = this.writeQueue) == null ? void 0 : _a.delete(key);
  }
  async deleteByIdAsync(key) {
    var _a;
    await ((_a = this.writeQueue) == null ? void 0 : _a.deleteAsync(key));
  }
  clearFailure(key) {
    var _a;
    (_a = this.writeQueue) == null ? void 0 : _a.clearFailure(key);
  }
  purgeCache() {
    var _a;
    this.views.clear();
    this.buffer.clear();
    this.historical.clear();
    (_a = this.writeQueue) == null ? void 0 : _a.evictStaleFailures();
  }
  purgeQueuedChanges() {
    var _a;
    (_a = this.writeQueue) == null ? void 0 : _a.purgeOfflineChanges();
  }
  cancelPendingWrites() {
    var _a;
    if (this.views instanceof BufferStorage) {
      this.views.cancelPendingWrites();
    }
    if (this.buffer instanceof BufferStorage) {
      this.buffer.cancelPendingWrites();
    }
    if (this.historical instanceof BufferStorage) {
      this.historical.cancelPendingWrites();
    }
    (_a = this.writeQueue) == null ? void 0 : _a.cancelPendingWrites();
    this.expansionsQueue.cancelAll();
  }
  async waitForPendingWrites() {
    var _a;
    await Promise.all([
      this.views instanceof BufferStorage ? this.views.waitForPendingWrites() : Promise.resolve(),
      this.buffer instanceof BufferStorage ? this.buffer.waitForPendingWrites() : Promise.resolve(),
      this.historical instanceof BufferStorage ? this.historical.waitForPendingWrites() : Promise.resolve(),
      (_a = this.writeQueue) == null ? void 0 : _a.waitForPendingWrites(),
      this.expansionsQueue.waitForAllQueued()
    ]);
  }
  queueOfflineChanges() {
    var _a;
    (_a = this.writeQueue) == null ? void 0 : _a.queueOfflineChanges();
  }
  async expandSingle(reference, options) {
    if (reference.resource) {
      return reference.resource;
    }
    if (!reference.id) {
      return null;
    }
    const expanded = await this.expandMulti([reference], options);
    if (expanded.length === 0) {
      throw getNotFoundError(reference);
    }
    return expanded[0];
    function getNotFoundError(expandable) {
      let display = expandable.id;
      if (expandable.resourceType) {
        display = `${expandable.resourceType} ${display}`;
      }
      if (expandable.versionId) {
        display += ` (version ${expandable.versionId})`;
      }
      return new Error(`Reference ${display} could not be expanded.`);
    }
  }
  expandSingleStore(reference) {
    if (reference.resource) {
      return reference.resource;
    }
    const expanded = this.expandMultiStore([reference]);
    if (expanded.length === 0) {
      return null;
    }
    return expanded[0];
  }
  async expandMulti(references, options) {
    const storeExpanded = this.expandMultiStore(references);
    const needExpanding = references.filter((r) => !r.resource);
    this.allowFreshening = false;
    try {
      const expanded = needExpanding.length > 0 ? await this.client.expand(needExpanding, options) : [];
      await this.expandAndFreshen(expanded, []);
      return distinctResources([...Array.from(storeExpanded.values()), ...expanded]);
    } finally {
      this.allowFreshening = true;
    }
  }
  expandMultiStore(references) {
    const notExpandedYet = references.filter((r) => !r.resource);
    if (notExpandedYet.length === 0) {
      return distinctResources(references.map((r) => r.resource));
    }
    const newlyExpanded = /* @__PURE__ */ new Map();
    notExpandedYet.forEach((reference) => {
      reference = fixNestedResource(reference);
      if (!reference.id) {
        return;
      }
      const key = createKey(reference.partition, reference.id);
      const resource = this.get(key, reference.versionId);
      const referenceIdentity = reference.versionId ?? reference.id;
      if (resource) {
        if (!reference.resource) {
          reference.resource = resource;
        }
        newlyExpanded.set(referenceIdentity, resource);
      }
    });
    const alreadyExpanded = references.filter((r) => r.resource);
    return distinctResources([...alreadyExpanded.map((r) => r.resource), ...Array.from(newlyExpanded.values())]);
  }
}
function values(map) {
  return Array.from(map.values());
}
const partitionedMethods = partitionedStoreMethods;
const partitionedResourceTypeNames = partitionedResourceTypes;
function wrapStore(store) {
  const wrapped = {
    ...store
  };
  getAllMethods(store).forEach((name) => {
    const prop = store[name];
    if (typeof prop === "function") {
      const originalMethod = prop;
      if (partitionedMethods.includes(name)) {
        if (partitionedResourceTypeNames.includes(store.resourceType)) {
          wrapped[name] = createPartitionMethod(originalMethod.bind(store));
        } else {
          wrapped[name] = createNonPartitionMethod(originalMethod.bind(store));
        }
      } else {
        wrapped[name] = originalMethod.bind(store);
      }
    }
  });
  return wrapped;
}
function getAllMethods(toCheck) {
  var props = [];
  var obj = toCheck;
  do {
    props = props.concat(Object.getOwnPropertyNames(obj));
  } while (obj = Object.getPrototypeOf(obj));
  return props.sort().filter((e, i, arr) => {
    return e != arr[i + 1] && typeof toCheck[e] == "function";
  });
}
function createNonPartitionMethod(handler) {
  return function(resourceId, ...args) {
    return handler.apply(this, [createKey(void 0, resourceId), ...args]);
  };
}
function createPartitionMethod(handler) {
  return function(partition, resourceId, ...args) {
    return handler.apply(this, [createKey(partition, resourceId), ...args]);
  };
}
const activeExpansions = /* @__PURE__ */ new Map();
class Expander {
  constructor(stores, expansionOptions) {
    __publicField(this, "stores");
    __publicField(this, "expansionOptions");
    this.stores = stores;
    this.expansionOptions = expansionOptions;
  }
  applyExpansions(input, additionalExpansionsOrStoreOnly, storeOnly) {
    var _a;
    const resources = Array.isArray(input) ? input : [input];
    if (additionalExpansionsOrStoreOnly === true) {
      storeOnly = true;
    }
    const additionalExpansions = additionalExpansionsOrStoreOnly === true ? void 0 : additionalExpansionsOrStoreOnly;
    const expansionNames = Array.from(/* @__PURE__ */ new Set([...additionalExpansions ?? [], ...((_a = this.expansionOptions) == null ? void 0 : _a.expansions) ?? []]));
    const expandables = flatMap(resources, (resource) => getUnexpanded(resource, expansionNames));
    if (storeOnly) {
      this.expandStoreOnly(expandables);
      if (Array.isArray(input)) {
        const expanded = resources.filter(isExpanded);
        const needsExpanding = resources.filter((r) => !isExpanded(r));
        return [expanded, needsExpanding];
      }
      return isExpanded(resources[0]);
    }
    return this.expand(expandables).then(() => {
      return Array.isArray(input) ? resources : resources[0];
    });
    function isExpanded(resource) {
      return getUnexpanded(resource, expansionNames).length === 0;
    }
    function getUnexpanded(resource, expansionNames2) {
      if (expansionNames2.length === 0) {
        return [];
      }
      return getUnexpandedExpandablesByNames(resource.resourceType, resource, expansionNames2);
    }
  }
  async expand(expandables) {
    const expandableGroups = groupBy(expandables, (r) => r.resourceType);
    const expansionPromises = [];
    for (const [resourceType, references] of expandableGroups) {
      if (!resourceType) {
        continue;
      }
      const groupStore = this.stores[resourceType];
      if (!groupStore) {
        continue;
      }
      let activeExpansion = activeExpansions.get(resourceType);
      if (activeExpansion) {
        await activeExpansion;
      }
      activeExpansion = (async () => {
        try {
          await groupStore.expand(references);
        } finally {
          activeExpansions.delete(resourceType);
        }
      })();
      activeExpansions.set(resourceType, activeExpansion);
      expansionPromises.push(activeExpansion);
    }
    await Promise.all(expansionPromises);
  }
  expandStoreOnly(expandables) {
    const expandableGroups = groupBy(expandables, (r) => r.resourceType);
    expandableGroups.forEach((references, resourceType) => {
      if (!resourceType) {
        return;
      }
      const groupStore = this.stores[resourceType];
      if (groupStore) {
        groupStore.expand(references, true);
      }
    });
  }
}
function mapReferenceEvents(stores) {
  const lookup2 = new ReverseLookup();
  const lock = { locked: false };
  Object.values(stores).forEach((store) => {
    store == null ? void 0 : store.items().forEach((item) => {
      const key = createReferenceKey(item.resourceType, item.partition, item.id);
      updateMappings(key, item);
    });
    store == null ? void 0 : store.onSet(handleStoreSet);
  });
  function handleStoreSet(item) {
    const key = createReferenceKey(item.resourceType, item.partition, item.id);
    if (lock.locked) {
      return;
    }
    lock.locked = true;
    try {
      updateMappings(key, item);
      const changed = /* @__PURE__ */ new Set();
      appendChangedKeys(changed, key);
      changed.forEach((key2) => {
        var _a, _b;
        const { resourceType, partition, resourceId } = splitReferenceKey(key2);
        if (partition) {
          (_a = stores[resourceType]) == null ? void 0 : _a.fireSet(partition, resourceId);
        } else {
          (_b = stores[resourceType]) == null ? void 0 : _b.fireSet(resourceId);
        }
      });
    } finally {
      lock.locked = false;
    }
  }
  function updateMappings(key, item) {
    const expandables = getExpandables(item);
    const childrenKeys = whereNotNull(expandables.map((expandable) => {
      if (expandable.resourceType && expandable.id) {
        return createReferenceKey(expandable.resourceType, expandable.partition, expandable.id);
      }
      return null;
    }));
    lookup2.set(key, childrenKeys);
    const parents = getParents(key);
    parents.forEach((parent) => {
      const expandables2 = getExpandables(parent);
      expandables2.forEach((expandable) => {
        var _a;
        if (expandable.resourceType === item.resourceType && expandable.partition === item.partition && expandable.id === item.id && (!expandable.versionId || expandable.versionId === ((_a = item.meta) == null ? void 0 : _a.versionId))) {
          expandable.resource = item;
        }
      });
    });
  }
  function appendChangedKeys(set, child) {
    const parentKeys = lookup2.getParents(child);
    parentKeys.forEach((parentKey) => {
      if (!set.has(parentKey)) {
        set.add(parentKey);
        appendChangedKeys(set, parentKey);
      }
    });
  }
  function getParents(key) {
    const parentKeys = lookup2.getParents(key);
    return whereNotNull(parentKeys.map((parentKey) => {
      const { resourceType, partition, resourceId } = splitReferenceKey(parentKey);
      const store = stores[resourceType];
      return store ? partition ? store.get(partition, resourceId) : store.get(resourceId) : null;
    }));
  }
}
class ReverseLookup {
  constructor() {
    __publicField(this, "childToParent", /* @__PURE__ */ new Map());
    __publicField(this, "parentToChild", /* @__PURE__ */ new Map());
  }
  set(parentKey, childrenKeys) {
    const oldChildrenKeys = this.parentToChild.get(parentKey) ?? [];
    this.parentToChild.set(parentKey, childrenKeys);
    const removedChildrenKeys = oldChildrenKeys.filter((key) => !childrenKeys.includes(key));
    removedChildrenKeys.forEach((childKey) => {
      const parents = (this.childToParent.get(childKey) ?? []).filter((key) => key !== parentKey);
      if (parents.length > 0) {
        this.childToParent.set(childKey, parents);
      } else {
        this.childToParent.delete(childKey);
      }
    });
    childrenKeys.forEach((childKey) => {
      const parents = this.childToParent.get(childKey) ?? [];
      if (!parents.includes(parentKey)) {
        parents.push(parentKey);
        this.childToParent.set(childKey, parents);
      }
    });
  }
  getParents(childKey) {
    return this.childToParent.get(childKey) ?? [];
  }
  getChildren(parentKey) {
    return this.parentToChild.get(parentKey) ?? [];
  }
}
const databaseVersion = 2;
const defaultBufferDebounceMs = 200;
const defaultFailureEvictionTimeout = 1440;
class StoreFactory {
  constructor(database, networkConsumer) {
    __publicField(this, "database");
    __publicField(this, "networkConsumer");
    this.database = database;
    this.networkConsumer = networkConsumer;
  }
  async create(clients, options) {
    const clientNames = Object.keys(clients);
    const stores = {};
    const resourceStores = {};
    const storageNames = clientNames.reduce((storageNames2, clientName) => {
      const client = clients[clientName];
      if (isStoreClient(client)) {
        if (options == null ? void 0 : options.offlineStorage) {
          storageNames2.push(
            `${client.resourceType}_items`,
            `${client.resourceType}_historical`,
            `${client.resourceType}_views`
          );
        }
        storageNames2.push(
          `${client.resourceType}_pending_upserts`,
          `${client.resourceType}_pending_deletes`,
          `${client.resourceType}_failed_upserts`
        );
      }
      return storageNames2;
    }, []);
    const storage = await this.database.create(storageNames, databaseVersion);
    await Promise.all(clientNames.map(async (clientName) => {
      const client = clients[clientName];
      if (isStoreClient(client)) {
        const storeOptions = {
          offlineStorage: options == null ? void 0 : options.offlineStorage,
          failureEvictionTimeout: options == null ? void 0 : options.failureEvictionTimeout,
          ...options == null ? void 0 : options[clientName],
          bufferDebounceMs: options == null ? void 0 : options.bufferDebounceMs,
          onSaveError: options == null ? void 0 : options.onSaveError
        };
        const resourceStorage = {
          items: storage[`${client.resourceType}_items`],
          historical: storage[`${client.resourceType}_historical`],
          views: storage[`${client.resourceType}_views`],
          pendingUpserts: storage[`${client.resourceType}_pending_upserts`],
          pendingDeletes: storage[`${client.resourceType}_pending_deletes`],
          failedUpserts: storage[`${client.resourceType}_failed_upserts`]
        };
        const store = wrapStore(await createStore(resourceStores, client, resourceStorage, this.networkConsumer, storeOptions));
        stores[clientName] = store;
        resourceStores[client.resourceType] = store;
        client.onFetched(store.freshen);
      }
    }));
    mapReferenceEvents(resourceStores);
    return { ...stores, purgeCache };
    function purgeCache() {
      Object.values(stores).forEach((store) => store.purgeCache());
    }
  }
}
async function createStore(stores, client, storage, networkConsumer, options) {
  const bufferDebounceMs = (options == null ? void 0 : options.bufferDebounceMs) ?? defaultBufferDebounceMs;
  const failureEvictionTimeout = (options == null ? void 0 : options.failureEvictionTimeout) ?? defaultFailureEvictionTimeout;
  const buffer = storage.items ? await BufferStorage.create({ storage: storage.items, debounceMs: bufferDebounceMs, hasher: hashResource }) : MemoryStorage.create({ hasher: hashResource });
  const historical = storage.historical ? await BufferStorage.create({ storage: storage.historical, debounceMs: bufferDebounceMs, hasher: hashResource }) : MemoryStorage.create({ hasher: hashResource });
  const views = storage.views ? await BufferStorage.create({ storage: storage.views, debounceMs: bufferDebounceMs }) : MemoryStorage.create();
  const upserts = await BufferStorage.create({ storage: storage.pendingUpserts, debounceMs: bufferDebounceMs, synchronousEvents: true });
  const deletes = await BufferStorage.create({ storage: storage.pendingDeletes, debounceMs: bufferDebounceMs, synchronousEvents: true });
  const uploads = await BufferStorage.create({ storage: storage.pendingDeletes, debounceMs: bufferDebounceMs, synchronousEvents: true });
  const failures = await BufferStorage.create({ storage: storage.failedUpserts, debounceMs: bufferDebounceMs, synchronousEvents: false });
  upserts.entries().forEach((queuedUpsert, key) => {
    buffer.set(key, queuedUpsert.upsert);
  });
  deletes.entries().forEach((queuedDelete) => {
    buffer.delete(queuedDelete.key);
  });
  uploads.entries().forEach((queuedDelete) => {
    buffer.delete(queuedDelete.key);
  });
  if (buffer instanceof BufferStorage) {
    await buffer.waitForPendingWrites();
  }
  const expander = new Expander(stores, options);
  const writeQueue = isWriteableClient(client) ? new ClientWriteQueue(
    client,
    buffer,
    historical,
    upserts,
    deletes,
    uploads,
    failures,
    () => networkConsumer.isOnline(),
    expander,
    options == null ? void 0 : options.onSaveError,
    failureEvictionTimeout
  ) : void 0;
  const store = new Store(client, buffer, historical, views, expander, writeQueue, networkConsumer);
  if (await networkConsumer.isOnline()) {
    store.queueOfflineChanges();
  }
  return store;
}
function isStoreClient(item) {
  return item && "fetchById" in item;
}
function isWriteableClient(client) {
  return client && "update" in client;
}
export {
  AddressType,
  AddressUse,
  AdministrativeFlags,
  AdministrativeGender,
  AgendaFidelity,
  AllergyClinicalStatus,
  AllergySeverity,
  AllergySortField,
  AllergyType,
  AllergyVerificationStatus,
  AnnouncementSortField,
  ApolloAxiosClient,
  AppointmentExpandables,
  AppointmentSortField,
  Approximate,
  AsyncQueue,
  AuditVerdict,
  BaseResourceClient,
  BellsWebFontAppearance,
  BellsWebTheme,
  BirthSex,
  BufferStorage,
  CarePlanOutcomeExpandables,
  CarePlanOutcomeSortField,
  CarePlanSessionSortField,
  CdaParsingTaskExpandables,
  ChannelMembershipExpandables,
  ChannelParticipation,
  ChannelStatus,
  ChannelType,
  ChatAvailableStatus,
  ChatContentType,
  ChatMessageExpandables,
  ChatMessageSortField,
  ClinicalDocumentExpandables,
  ClinicalDocumentSortField,
  ClinicalFlags,
  CommunicationSortField,
  ComplianceAuditExpandables,
  ComplianceAuditSortField,
  ComplianceQuestionAnswerOption,
  ComplianceQuestionnaireExpandables,
  ComplianceQuestionnaireSortField,
  CompositionStatus,
  ConditionCategory,
  ConditionExpandables,
  ConditionSortField,
  ConditionStatus,
  ConsentSortField,
  ContactMethod,
  ContactPointUse,
  ContentDistribution,
  CoverageSortField,
  DatabaseClient,
  DateRangeBoundary,
  DateTimePrecision,
  DayOfWeek,
  DiagnosisLimitType,
  DiagnosisRole,
  DialogueExpandables,
  DialogueSortField,
  Distribution,
  DocumentTransmissionExpandables,
  DocumentationStatus,
  Duration,
  EncounterDocumentationType,
  EncounterExpandables,
  EncounterInteraction,
  EncounterSortField,
  EncounterStatus,
  EncryptionKeyPurpose,
  EpisodeOfCareExpandables,
  EpisodeOfCareSortField,
  EpisodeOfCareStatus,
  EventStatus,
  FormExpandables,
  FormSortField,
  FormType,
  GoalAchievementStatus,
  GoalElementType,
  GroupAttendanceStatus,
  GroupExpandables,
  GroupNoteDefinitionExpandables,
  GroupNoteDefinitionSortField,
  GroupSortField,
  GroupType,
  HealthcareServiceExpandables,
  HealthcareServiceSortField,
  Instant,
  InternalFlags,
  KeyVaultProvider,
  ListPurpose,
  LocalDate,
  LocalTime,
  LocationMode,
  LocationRole,
  LocationSortField,
  MediaType,
  MedicationStatus,
  MessagingFlags,
  NameUse,
  NoteAuditExpandables,
  NoteAuditSortField,
  NoteComposition,
  NoteDefinitionExpandables,
  NoteDefinitionSortField,
  NoteDefinitionType,
  NoteExcludables,
  NoteExpandables,
  NoteRuleCriteriaType,
  NoteRuleOutcomeType,
  NoteSectionFormExpandables,
  NoteSectionFormMode,
  NoteSectionFormSortField,
  NoteSectionFormUsage,
  NoteSectionFormat,
  NoteSortField,
  NoteSyncIssue,
  NoteTemplateExpandables,
  NoteTemplateSortField,
  NotificationImportance,
  NotificationSortField,
  NotificationTopic,
  ObservationInterpretation,
  ObservationStatus,
  OffsetDateTime,
  OffsetTime,
  OrganizationSortField,
  OrganizationType,
  PacketExpandables,
  PacketPurpose,
  PacketResponseExpandables,
  PacketResponseSortField,
  PacketResponseStatus,
  PacketSortField,
  ParameterPresence,
  ParticipantRole,
  ParticipationStatus,
  PartitionedResourceClient,
  Patch,
  PatientExpandables,
  PatientPopulationLevel,
  PatientSortField,
  Permission,
  PermissionFlags,
  Permissions,
  PersonalPreferencesExpandables,
  PersonalSignatureExpandables,
  PracticeClients,
  PractitionerExcludables,
  PractitionerExpandables,
  PractitionerSortField,
  ProgramSortField,
  PronounClass,
  PublicationStatus,
  QuestionnaireCalculation,
  QuestionnaireElementOrientation,
  QuestionnaireElementPresentation,
  QuestionnaireElementRole,
  QuestionnaireElementType,
  QuestionnaireResponseStatus,
  QuestionnaireSortField,
  QuestionnaireValueToken,
  RecurrenceType,
  RegistryClients,
  RelatedPeopleExpandables,
  RelatedPersonSortField,
  RelativeOccassion,
  ReportCriteriaSortField,
  ReportDeliveryExpandables,
  ReportDeliverySortField,
  RequestStatus,
  ResourceClient,
  ResourcePullOutcome,
  ResourceSyncStatus,
  RolePersona,
  SchedulerFlags,
  ScribbleExpandables,
  ScribbleSortField,
  ScriptSortField,
  SecurityRoleExpandables,
  SecurityRoleSortField,
  SecurityTokenHandlerFactory,
  SerialConjunction,
  ServiceCharacteristics,
  SessionTimeFormat,
  SharedScribbleExpandables,
  SharedScribbleSortField,
  SnippetExpandables,
  SnippetSortField,
  SnippetTemplateSortField,
  SortDirection,
  SpeechRuleCategory,
  SpeechRuleTemplateSortField,
  SpeechRuleType,
  StorageAsyncDatabase,
  StoragePartition,
  StoreFactory,
  SupervisoryRole,
  SurveyFlags,
  Tense,
  TextExpansionSortField,
  TextExpansionTemplateSortField,
  Tzdb,
  UserLicense,
  VisitStatus,
  WeekView,
  ZonedDateTime,
  activeZones,
  allowedStatusCodes,
  appendHeaders,
  appendUserProfileHeader,
  associateRoleAccessPolicies,
  associateRoles,
  attachedContentResourceTypes,
  bellsScheme,
  carefabricScheme,
  claims,
  convertBase64ToBuffer,
  convertBufferToBase64,
  copyOriginal,
  ctoneScheme,
  cyrb53,
  defaults,
  delay,
  deploymentZones,
  distinctResources,
  ehrSystems,
  exclusionPaths,
  expansionPaths,
  extendPracticeClients,
  extendRegistryClients,
  externalZones,
  fixNestedResource,
  flatMap,
  getExpandables,
  getExpandedExpandablesByNames,
  getExternalIdentifier,
  getReferences,
  getReferencesAtPath,
  getSortQuery,
  getUnexpandedExpandablesByNames,
  groupBy,
  headerNames,
  humanizeDuration,
  internalZones,
  isApolloError,
  isApolloRequestError,
  isApolloResponseError,
  isBells,
  isCareFabric,
  isCtOne,
  isEhrResourceIdentifier,
  isEhrResourceSystem,
  isExternalIdentifier,
  isGroupNote,
  isMultiNote,
  isMultiServiceNote,
  isNotNew,
  isParentEncounter,
  isPatientNote,
  isReference,
  knownCodings,
  makeNew,
  paginate,
  parseBells,
  parseCareFabric,
  parseCtOne,
  parseProfileClaim,
  parseResourceSystem,
  partitionedResourceTypes,
  plusWeekdays,
  principals,
  profileClaimAreEqual,
  readOnlyPaths,
  readOriginal,
  referencePaths,
  removeFalsy,
  removeOriginal,
  resolveOperationConflicts,
  resourceTypes,
  safeStringify,
  securityRoleIds,
  selfPartitionedResourceTypes,
  systems,
  trimExpandedReferences$1 as trimExpandedReferences,
  trimNestedResources,
  weekdayDuration,
  whereNotNull,
  zone
};
